// CTH Video (videos.coachtonyhockey.com). The player itself is
// player/player.js, written against the small UI kit in web/obsidian-shim.js;
// this file is the app around it: the vault, the workspace, the file tree and
// library, and the HOST hooks the player calls. The vault is the library on
// the app's own server (web/cloud.js: /api/fs/*, kept in step with Tony's Mac
// folder ~/CTH/videos by the bridge), mapped to the vault path "videos/", so
// clip notes, resume positions and exports use the same paths in every browser
// and match ~/CTH/videos. Sign-in is the password card in front of the site.
//
// Layout: the file tree (folders and videos) on the left; the library (a
// folder as video cards) or the player on the right. The URL hash is the place:
//   #/            the videos root
//   #/f/<folder>  a folder, relative to the root
//   #/v/<file>    a video, relative to the root
import './host-setup.js';
import { Events, Notice, Modal, Menu, TFile, TFolder, Setting, setIcon, isModalOpen, copyText } from './obsidian-shim.js';
import CthVideoPlugin from '../player/player.js';
import YAML from 'yaml';
import * as cloud from './cloud.js';
import { shared, onShared, loadShared, setShared, scratchApi, claudeApi, inAdmin, keyMatches, comboOf, keyLabel, SHARED_DEFAULTS } from './shared.js';
import { version } from '../package.json';

const HOST = globalThis.__CVS_HOST__;
const manifest = { id: 'cth-video', name: 'CTH Video', version };
// The Mac app is called CTH Videos (Tony, 2026-10-02): window title and tree header.
const APP_TITLE = cloud.local ? 'CTH Videos' : 'CTH Video';
// Inside the CTH Hub: hand the window title (the open video's name) to the hub.
if (cloud.inHub) {
  const tell = () => { try { window.parent.postMessage({ cthHub: 'title', title: document.title }, location.ancestorOrigins?.[0] || '*'); } catch (_) { /* fine */ } };
  new MutationObserver(tell).observe(document.querySelector('title') || document.head, { childList: true, subtree: true, characterData: true });
  addEventListener('load', tell);
}
const VP = 'videos';          // the vault folder the library root stands for
const ROOT_NAME = 'Videos';   // what the tree and breadcrumbs call the library root
const BASE = '/lib';          // the fake vault's absolute base path
const DATA_KEY = 'cthvw.data.v1';
const WEB_KEY = 'cthvw.web.v1';
const SHARED_VP = 'videos/.cth-video.json'; // settings and resume positions shared by every browser and embed
const VIDEO_RE = /\.(mp4|m4v|mov|webm)$/i;
const $ = (tag, o, ...kids) => { const e = createEl(tag, o); for (const k of kids) if (k) e.append(k); return e; };
const icon = (name, cls = '') => { const s = createSpan({ cls: `cvsw-ic ${cls}`.trim() }); setIcon(s, name); return s; };

// ---- web-only settings (the player's own settings are the plugin's) --------

let firstRun = true;
// fade: the short cross-fade between the table and the player; quickLook: a thumbnail click opens the small popup player
const WEB_DEFAULTS = { treeOpen: true, expanded: [], sort: 'modified', treeW: 272, treeGuides: true, fade: true, quickLook: true, quickLookPlay: true, setSec: 'general', statusDots: true, statusPos: 'before', statusInherit: 'nearest', statusProgress: 'off', confirmations: 'quiet' };
const web = (() => { try { const raw = localStorage.getItem(WEB_KEY); firstRun = !raw; return { ...WEB_DEFAULTS, ...JSON.parse(raw || '{}') }; } catch (_) { return { ...WEB_DEFAULTS }; } })();
Notice.mode = web.confirmations === 'all' ? 'all' : 'quiet';
const saveWeb = () => { try { localStorage.setItem(WEB_KEY, JSON.stringify(web)); } catch (_) { /* private window */ } };

// ---- paths -------------------------------------------------------------------
// The server's paths are relative to the library root ('/Drills/x.mp4', and ''
// for the root itself); the vault calls the root "videos" ('videos/Drills/x.mp4').

const normVault = (p) => String(p || '').replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\/|\/$/g, '');
const inVideos = (vp) => vp === VP || vp.startsWith(`${VP}/`);
/** The server path of a vault (or absolute) path: '' for the root, null outside videos/. */
const toCloud = (p) => {
  let vp = String(p || '');
  if (vp.startsWith(`${BASE}/`)) vp = vp.slice(BASE.length + 1);
  vp = normVault(vp);
  if (!inVideos(vp)) return null;
  return cloud.normPath(vp.slice(VP.length + 1));
};
const vaultOf = (cloudPath) => { const p = cloud.normPath(cloudPath); return p ? `${VP}${p}` : VP; };
const absOf = (vp) => `${BASE}/${normVault(vp)}`;
const parentOf = (p) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '');
const baseName = (p) => p.split('/').pop();
const relOf = (vp) => (vp === VP ? '' : vp.slice(VP.length + 1));
const encodeRel = (rel) => rel.split('/').map(encodeURIComponent).join('/');
const decodeRel = (s) => s.split('/').map((x) => { try { return decodeURIComponent(x); } catch (_) { return x; } }).join('/');

// ---- changes waiting to save --------------------------------------------------------
// A clip note (or the shared settings file) that could not reach the server
// (offline, wifi dropped at the rink, signed out) is kept here, in this
// browser, and saved by itself when the server is reachable again. Reading that
// note returns the kept text, so a reload or a crash never loses a clip.
const PENDING_KEY = 'cthvw.pending.v1';
function pendingAll() { try { return JSON.parse(localStorage.getItem(PENDING_KEY) || '{}') || {}; } catch (_) { return {}; } }
function pendingSet(vp, text) {
  const all = pendingAll();
  if (text == null) delete all[vp]; else all[vp] = { text, at: Date.now() };
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(all)); } catch (_) { /* private window: memory only */ }
  updateBanner();
}
let netOk = true;        // false after a request failed to reach the server, until one succeeds
let signedOut = false;   // the server said the session ended (the page reloads onto the password card)

// ---- the vault (the library on the server) ------------------------------------------------

class VFile extends TFile {
  constructor(path, e) { super(); this.path = path; this.name = baseName(path); const i = this.name.lastIndexOf('.'); this.basename = i > 0 ? this.name.slice(0, i) : this.name; this.extension = i > 0 ? this.name.slice(i + 1) : ''; this.parent = null; this.setMeta(e); }
  setMeta(e) { this.rev = e?.rev || ''; this.id = e?.id || this.id || ''; this.stat = { size: e?.size || 0, mtime: e?.modified || Date.now(), ctime: e?.created || e?.modified || Date.now() }; }
}
class VFolder extends TFolder {
  constructor(path) { super(); this.path = path; this.name = baseName(path) || ''; this.children = []; this.parent = null; }
  isRoot() { return this.path === '/'; }
}

class CloudVault extends Events {
  constructor() {
    super();
    this.configDir = '.obsidian';
    this.index = new Map();         // vault path -> VFile | VFolder
    this.rootFolder = new VFolder('/');
    this.texts = new Map();         // vault path -> { rev, text }
    this.ownRevs = new Set();       // revisions this page wrote (so the long-poll does not "reload" our own saves)
    this.pending = 0;               // writes in flight (the page warns before closing)
    this.loaded = false;
    const vault = this;
    this.adapter = {
      getBasePath: () => BASE,
      exists: async (p) => { const vp = normVault(p); if (vault.index.has(vp)) return true; const cp = toCloud(vp); if (!cp) return cp === ''; try { await cloud.meta(cp); return true; } catch (_) { return false; } },
      read: async (p) => vault.#readText(normVault(p)),
      write: async (p, data) => { const vp = normVault(p); if (!toCloud(vp)) return; await vault.#writeText(vp, String(data)); },
      stat: async (p) => { const f = vault.index.get(normVault(p)); return f ? { type: f instanceof VFolder ? 'folder' : 'file', ...(f.stat || {}) } : null; },
    };
  }

  async refresh() {
    const list = await cloud.listAll();
    const next = new Map();
    const top = new VFolder(VP);
    next.set(VP, top);
    for (const e of list) {
      const vp = vaultOf(e.path);
      if (vp === VP) continue;
      const old = this.index.get(vp);
      if (e.dir) next.set(vp, Object.assign(old instanceof VFolder ? old : new VFolder(vp), { children: [], id: e.id || '' }));
      else { const f = old instanceof VFile ? old : new VFile(vp, e); const changed = old instanceof VFile && old.rev && e.rev && old.rev !== e.rev; f.setMeta(e); next.set(vp, f); if (changed && !this.ownRevs.has(e.rev)) this.changed.push(f); }
    }
    for (const [p, f] of next) { if (p === VP) continue; const par = next.get(parentOf(p)); if (par) { f.parent = par; par.children.push(f); } }
    top.parent = this.rootFolder; this.rootFolder.children = [top];
    this.index = next;
    this.loaded = true;
  }
  changed = [];

  getAbstractFileByPath(p) { const vp = normVault(p); return vp === '' || vp === '/' ? this.rootFolder : this.index.get(vp) || null; }
  getFileByPath(p) { const f = this.getAbstractFileByPath(p); return f instanceof VFile ? f : null; }
  getFolderByPath(p) { const f = this.getAbstractFileByPath(p); return f instanceof VFolder ? f : null; }
  getRoot() { return this.rootFolder; }
  getFiles() { return [...this.index.values()].filter((f) => f instanceof VFile); }
  getMarkdownFiles() { return this.getFiles().filter((f) => f.extension === 'md'); }
  getAllLoadedFiles() { return [...this.index.values()]; }
  getName() { return ROOT_NAME; }

  async #readText(vp) {
    const kept = pendingAll()[vp];
    if (kept) return kept.text; // not on the server yet: this browser holds the newest text
    const f = this.index.get(vp);
    const hit = this.texts.get(vp);
    if (hit && f && hit.rev && hit.rev === f.rev) return hit.text;
    const { text, meta } = await cloud.readText(toCloud(vp));
    if (meta) { this.texts.set(vp, { rev: meta.rev, text }); if (f instanceof VFile) f.setMeta(meta); }
    return text;
  }
  async #writeText(vp, text) { return this.#write(vp, new TextEncoder().encode(text), text); }
  async #write(vp, bytes, text) {
    const dp = toCloud(vp);
    if (!dp) throw new Error(`${vp} is outside the videos folder`);
    this.pending++;
    try {
      let meta;
      try { meta = await cloud.uploadAny(dp, bytes, { overwrite: true }); }
      catch (e) {
        // Clips notes wait on this device; the shared settings file does not (a
        // late blind write could undo another device's settings; it merges and
        // shares again on the next change or focus).
        if (text != null && vp !== SHARED_VP && (cloud.isNetworkError(e) || e.message === 'NOT_CONNECTED')) pendingSet(vp, text);
        noteTrouble(e);
        throw e;
      }
      noteOk();
      if (text != null && pendingAll()[vp]) pendingSet(vp, null);
      this.ownRevs.add(meta.rev);
      let f = this.index.get(vp);
      const created = !f;
      if (!f) { f = new VFile(vp, meta); this.#attach(f); } else f.setMeta(meta);
      if (text != null) this.texts.set(vp, { rev: meta.rev, text });
      this.trigger(created ? 'create' : 'modify', f);
      return f;
    } finally { this.pending--; }
  }
  #attach(f) {
    this.index.set(f.path, f);
    let par = this.index.get(parentOf(f.path));
    if (!par) { const p = parentOf(f.path); par = new VFolder(p); this.#attach(par); }
    f.parent = par; if (!par.children.includes(f)) par.children.push(f);
  }
  #detach(f) {
    for (const p of [...this.index.keys()]) if (p === f.path || p.startsWith(`${f.path}/`)) this.index.delete(p);
    if (f.parent) f.parent.children = f.parent.children.filter((c) => c !== f);
  }

  async read(f) { return this.#readText(f.path); }
  async cachedRead(f) { return this.#readText(f.path); }
  async modify(f, text) { await this.#writeText(f.path, text); }
  async process(f, fn) { const t = await this.read(f); const n = fn(t); await this.modify(f, n); return n; }
  async create(p, text) { const vp = normVault(p); if (this.index.has(vp)) throw new Error('File already exists.'); return this.#writeText(vp, text); }
  async createBinary(p, bytes) { return this.#write(normVault(p), new Uint8Array(bytes)); }
  async writeBinary(vp, bytes) { return this.#write(normVault(vp), bytes); }
  async writeText(vp, text) { return this.#writeText(normVault(vp), text); }
  async createFolder(p) {
    const vp = normVault(p); const cp = toCloud(vp);
    if (cp == null) throw new Error(`${vp} is outside the videos folder`);
    if (cp) await cloud.ensureFolder(cp); // '' is the library root: always there
    if (!this.index.has(vp)) this.#attach(new VFolder(vp));
  }
  async trash(f) { await cloud.remove(toCloud(f.path)); this.#detach(f); this.trigger('delete', f); }
  async delete(f) { return this.trash(f); }
  async rename(f, newPath) {
    const vp = normVault(newPath); const old = f.path;
    const meta = await cloud.move(toCloud(old), toCloud(vp));
    this.#detach(f);
    f.path = vp; f.name = baseName(vp); const i = f.name.lastIndexOf('.'); if (f instanceof VFile) { f.basename = i > 0 ? f.name.slice(0, i) : f.name; f.extension = i > 0 ? f.name.slice(i + 1) : ''; f.setMeta(meta); }
    this.#attach(f);
    this.trigger('rename', f, old);
  }
}

// ---- the workspace (one pane: the open video) ----------------------------------

class VideoView {
  constructor(leaf, containerEl, contentEl, file) { this.leaf = leaf; this.containerEl = containerEl; this.contentEl = contentEl; this.file = file; this.app = app; }
  getViewType() { return 'video'; }
  getDisplayText() { return this.file ? this.file.basename : 'Video'; }
  getIcon() { return 'video'; }
}
class Leaf {
  constructor(view) { this.view = view; }
  updateHeader() {}
  getViewState() { return { type: 'video', state: { file: this.view.file?.path } }; }
  async setViewState() {}
  async loadIfDeferred() {}
  detach() { closeVideo(); }
  async openFile(f) { openVaultFile(f); }
}
class Workspace extends Events {
  constructor() { super(); this.leaf = null; this.layoutReady = false; }
  get activeLeaf() { return this.leaf; }
  getLeavesOfType(t) { return t === 'video' && this.leaf ? [this.leaf] : []; }
  getLeaf() { return { openFile: async (f) => openVaultFile(f) }; }
  getActiveViewOfType() { return null; }
  iterateAllLeaves(cb) { if (this.leaf) cb(this.leaf); }
  iterateRootLeaves(cb) { if (this.leaf) cb(this.leaf); }
  onLayoutReady(cb) { if (this.layoutReady) cb(); else this.on('__ready', cb); }
  setActiveLeaf() {}
  revealLeaf() {}
  requestSaveLayout() {}
}

const vault = new CloudVault();
const workspace = new Workspace();
const app = {
  vault,
  workspace,
  metadataCache: new Events(),
  fileManager: {
    renameFile: (f, p) => vault.rename(f, p),
    processFrontMatter: async (f, fn) => {
      const text = await vault.read(f);
      const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(text);
      const data = m ? (YAML.parse(m[1]) || {}) : {};
      fn(data);
      const fm = `---\n${YAML.stringify(data).trimEnd()}\n---\n`;
      await vault.modify(f, m ? fm + text.slice(m[0].length) : fm + text);
    },
  },
  setting: { open: () => openSettings('player'), openTabById: () => {}, close: () => {} },
  plugins: { plugins: {} },
  __loadData: async () => { try { return JSON.parse(localStorage.getItem(DATA_KEY) || 'null'); } catch (_) { return null; } },
  __saveData: async (id, d) => { try { localStorage.setItem(DATA_KEY, JSON.stringify(d)); } catch (_) { /* private window */ } },
};
app.metadataCache.fileToLinktext = (f) => f.basename;
app.metadataCache.getFileCache = () => null;

// ---- the HOST hooks player/player.js calls ----------------------------------------

Object.assign(HOST, {
  // Bytes for the scrub decoder: Range requests on the file's signed link.
  provider(absPath) {
    const dp = toCloud(absPath);
    const f = vault.getFileByPath(absPath.slice(BASE.length + 1));
    if (!dp || !f) return null;
    return { local: false, size: f.stat.size, read: (offset, length) => { const end = Math.min(f.stat.size, offset + length); return offset >= end ? Promise.resolve(null) : cloud.readRange(dp, offset, end - offset); } };
  },
  pathFor: (video) => video.dataset.cvsPath || null,
  // the clip log opened or closed (S, the top bar button, a narrow pane): the toggle shows it
  onSideChange: (p, shown) => { const b = bar.querySelector('.cvsw-side-toggle'); if (b) b.setAttribute('aria-pressed', String(!!shown)); },
  // "Show in folder" after an export and "Open the export folder": that folder in the library.
  reveal(abs) { const vp = normVault(String(abs || '').slice(BASE.length + 1)); if (toCloud(vp) != null) go('f', vault.getFolderByPath(parentOf(vp)) ? parentOf(vp) : VP); },
  revealFolder(vp) {
    const v = normVault(vp);
    if (toCloud(v) == null) new Notice(`The export folder "${vp}" is outside the videos folder.`);
    else if (!vault.getFolderByPath(v)) new Notice(`The export folder "${v}" is not there yet. It is made by the first export.`);
    else go('f', v);
  },
  // fullscreen needs the page around an embed to allow it
  noFullscreen: () => { if (cloud.embedded) blockedInEmbed('Fullscreen'); },
  // inside a Notion (or any other) page
  embedded: cloud.embedded,
  // a link's start (&t= seconds, &clip= key) for the video about to open, taken once
  takeLink(vp) { const l = pendingLink; if (!l || l.vp !== normVault(vp)) return null; pendingLink = null; return l; },
  // "Copy link at this time" / "Copy link to this clip"
  copyLinkAt: cloud.local ? null : (vp, at) => copyLink(normVault(vp), vault.getFileByPath(normVault(vp))?.id, at), // a link to a moment or clip on the website (the Notion embed buttons were retired 2026-10-02)
  // exporter helpers
  cloud, toCloud, absOf, normVault, basename: baseName,
  vaultHas: (vp) => vault.index.has(normVault(vp)),
  vaultWritable: (vp) => toCloud(vp) != null, // '' (the videos folder itself) is writable
  writeBinary: (vp, bytes) => vault.writeBinary(vp, bytes),
  // Settings > Export > Export to: pick a folder inside the library ('videos' is the top level)
  pickFolder: (currentVp, onPick) => folderPicker('Export to', currentVp, onPick),
});
// The exporter (Mediabunny, most of the bundle) loads on the first export.
let exporterClass = null;
HOST.ClipExporter = class LazyClipExporter {
  constructor(player) { this.player = player; this.folderVault = normVault(player.s.exportFolder || 'videos'); }
  async #real() { if (!exporterClass) exporterClass = (await import('./exporter.js')).makeExporter(HOST); return (this.real ||= new exporterClass(this.player)); }
  async exportClip(tag, progress) { return (await this.#real()).exportClip(tag, progress); }
  async probe() { return (await this.#real()).probe(); }
};

// ---- the page shell ---------------------------------------------------------------

// The Mac app layout (2026-10-02, Tony; the standard for every CTH Admin app): one top bar
// across the whole width (the app's icon and name, the left panel toggle, breadcrumbs, then
// the right panel toggle at the far right), a collapsible left panel (the file tree) and,
// in the player, a collapsible right panel (the clip log). Both panels share one look:
// --sidebar background, 44px header and footer rows, the same resize grip and slide.
const shell = $('div', { cls: 'cvsw-app' });
const bar = $('header', { cls: 'cvsw-appbar' });
const tree = $('aside', { cls: 'cvsw-tree', attr: { 'aria-label': 'Videos folder' } });
const main = $('main', { cls: 'cvsw-main' });
const scrim = $('div', { cls: 'cvsw-scrim' });
scrim.onclick = () => setTree(false);
shell.append(bar, tree, scrim, main);

const isNarrow = () => matchMedia('(max-width: 800px)').matches;
function setTree(open, fromHub = false) {
  if (isNarrow()) shell.toggleClass('is-drawer-open', open);
  else {
    web.treeOpen = open; saveWeb();
    if (!fromHub) panelToHub();
    // slide it (web.css .is-tree-sliding), like shadcn's sidebar
    clearTimeout(setTree.t); shell.addClass('is-tree-sliding');
    shell.toggleClass('is-tree-closed', !open);
    setTree.t = setTimeout(() => shell.removeClass('is-tree-sliding'), 240);
  }
  syncBarToggles();
}
/** Reload the whole app (works inside a Notion embed too); clips save first. */
function reloadBtn() {
  return btn('Reload the app', 'rotate-cw', async (e) => {
    e.currentTarget.disabled = true;
    const p = curPlayer();
    try { p?.commitEditIfAny?.(); if (p?.dirty) await p.save(); } catch (_) { /* kept on this device */ }
    location.reload();
  }, { variant: 'ghost', iconOnly: true });
}
function treeToggle() {
  const b = $('button', { cls: 'cvsw-btn is-ghost is-icon cvsw-tree-toggle', attr: { type: 'button', 'aria-label': 'Show or hide the file tree', title: 'Show or hide the file tree' } }, icon('panel-left'));
  b.onclick = () => setTree(isNarrow() ? !shell.hasClass('is-drawer-open') : shell.hasClass('is-tree-closed'));
  return b;
}
// the clip log (the player's right panel): S in the player, or this button
function sideToggle() {
  const b = $('button', { cls: 'cvsw-btn is-ghost is-icon cvsw-side-toggle', attr: { type: 'button', 'aria-label': 'Show or hide the clip log (S)', title: 'Show or hide the clip log (S)' } }, icon('panel-right'));
  b.onclick = () => curPlayer()?.toggleSidebar();
  return b;
}
function syncBarToggles() {
  const t = bar.querySelector('.cvsw-tree-toggle');
  if (t) t.setAttribute('aria-pressed', String(isNarrow() ? shell.hasClass('is-drawer-open') : !shell.hasClass('is-tree-closed')));
  const p = curPlayer();
  const sb = bar.querySelector('.cvsw-side-toggle');
  if (sb && p) sb.setAttribute('aria-pressed', String(p.sideShown ? p.sideShown() : !!p.s.sidebarOpen));
}

/** The open video's player (the one in the page now; a player fading out does not count). */
function curPlayer() { for (const p of plugin?.players.values() || []) if (leafEl && leafEl.contains(p.video)) return p; return null; }

// ---- quick panels: the Scratchpad and the CLAUDE.md editor (Tony, 2026-10-02; the same in CTH Docs) ----
// Two Button 31s at the top right, each opening a dropdown panel under it (420 x 520, resized from its
// bottom-left corner, size remembered): a header, one plain text area, a small footer. The Scratchpad's
// text is shared across CTH Admin's apps (web/shared.js; outside CTH Admin it lives in this browser);
// CLAUDE.md is Tony's global instruction file, reached through the hub only, so its button shows only
// inside CTH Admin. Their keys are shared settings (scratchKey, claudeKey).
let keyRecording = false; // while a new shortcut is being pressed, no key does anything else
class QuickPanel {
  constructor(cfg) {
    this.cfg = cfg; this.text = ''; this.updated = 0; this.loaded = false; this.dirty = false; this.busy = false; this.el = null; this.caret = null; this.timer = 0;
    this.btn = $('button', { cls: `cvsw-barbtn cvsw-qp-btn-${cfg.id}`, attr: { type: 'button', 'aria-haspopup': 'dialog', 'aria-expanded': 'false' } }, icon(cfg.icon));
    this.btn.onclick = () => this.toggle();
    this.btn.oncontextmenu = (e) => { e.preventDefault(); this.keyMenu(e); };
    this.label();
  }
  get key() { return shared[this.cfg.keyName]; }
  label() { this.btn.setAttribute('aria-label', `${this.cfg.title} (${keyLabel(this.key)})`); }
  get open() { return !!this.el; }
  toggle() { if (this.el) this.close(); else void this.show(); }
  /** Right-click the bar button: change or reset its shortcut. */
  keyMenu(e) {
    const m = new Menu();
    m.addItem((i) => i.setTitle('Change shortcut').setIcon('keyboard').setShortcut(keyLabel(this.key)).onClick(() => recordKeyAt(this.btn, this.cfg.keyName, this.cfg.title)));
    m.addItem((i) => i.setTitle('Reset shortcut').setIcon('rotate-ccw').setDisabled(this.key === SHARED_DEFAULTS[this.cfg.keyName]).onClick(() => void setKey(this.cfg.keyName, SHARED_DEFAULTS[this.cfg.keyName], this.cfg.title)));
    m.showAtMouseEvent(e);
  }
  size() {
    const w = Math.max(320, Math.min(Number(web[`${this.cfg.id}W`]) || 420, innerWidth - 16));
    const h = Math.max(240, Math.min(Number(web[`${this.cfg.id}H`]) || 520, innerHeight - 64));
    return { w, h };
  }
  place() {
    if (!this.el) return;
    const r = this.btn.getBoundingClientRect(); const { w, h } = this.size();
    const right = Math.max(8, Math.min(innerWidth - r.right, innerWidth - w - 8));
    Object.assign(this.el.style, { top: `${Math.round(r.bottom + 6)}px`, right: `${Math.round(right)}px`, width: `${w}px`, height: `${Math.min(h, innerHeight - r.bottom - 14)}px` });
  }
  state(text, bad = false) { if (!this.stateEl) return; this.stateEl.setText(text); this.stateEl.toggleClass('is-bad', bad); }
  count() {
    if (!this.countEl) return;
    const t = this.ta.value;
    if (this.cfg.counter === 'lines') { const n = t ? t.split('\n').length : 0; this.countEl.setText(plural(n, 'line')); }
    else { const n = (t.trim().match(/\S+/g) || []).length; this.countEl.setText(plural(n, 'word')); }
  }
  async show() {
    if (this.el) { this.ta.focus(); return; }
    const c = this.cfg;
    for (const other of quickPanels) if (other !== this) other.close();
    const ib = (label, ic, fn) => { const b = $('button', { cls: 'cvsw-qp-ib', attr: { type: 'button', 'aria-label': label } }, icon(ic)); b.onclick = fn; return b; };
    this.ta = $('textarea', { cls: `cvsw-qp-text${c.mono ? ' is-mono' : ''}`, attr: { spellcheck: c.mono ? 'false' : 'true', placeholder: c.placeholder || '', 'aria-label': c.title, autocomplete: 'off', autocapitalize: c.mono ? 'off' : null } });
    this.ta.value = this.text; this.ta.readOnly = !this.loaded; // until the first read comes back
    this.countEl = $('span', { cls: 'cvsw-qp-count' }); this.stateEl = $('span', { cls: 'cvsw-qp-state', attr: { role: 'status' } });
    this.warn = $('div', { cls: 'cvsw-qp-warn', attr: { role: 'alert' } });
    const grip = $('span', { cls: 'cvsw-qp-grip', attr: { 'aria-hidden': 'true', title: 'Drag to resize' } });
    this.el = $('div', { cls: `cvsw-qp cvsw-qp-${c.id}`, attr: { role: 'dialog', 'aria-label': c.title } },
      $('div', { cls: 'cvsw-qp-head' }, $('span', { cls: 'cvsw-qp-title', text: c.title }), $('span', { cls: 'cvsw-grow' }),
        c.newNote ? ib(`New note (${keyLabel(this.key)})`, 'separator-horizontal', () => void this.newNote()) : null,
        ib('Copy all', 'copy', () => void copyText(this.ta.value, { title: c.title, done: 'Copied.' })),
        ib('Close (esc)', 'x', () => this.close())),
      this.warn, this.ta, $('div', { cls: 'cvsw-qp-foot' }, this.countEl, this.stateEl), grip);
    document.body.append(this.el);
    this.btn.setAttribute('aria-expanded', 'true'); this.btn.addClass('is-open');
    this.place(); this.count(); this.conflictBar();
    this.ta.addEventListener('input', () => { this.text = this.ta.value; this.dirty = true; this.count(); this.state(''); clearTimeout(this.timer); this.timer = setTimeout(() => void this.save(), c.delay); });
    this.ta.addEventListener('keydown', (e) => e.stopPropagation()); // typing never reaches the player's keys (the panels' own keys are caught earlier, on the window)
    // resize from the bottom-left corner: left makes it wider (its right edge stays under the button), down taller
    grip.addEventListener('pointerdown', (e) => {
      e.preventDefault(); try { grip.setPointerCapture(e.pointerId); } catch (_) { /* a made-up pointer */ }
      const x0 = e.clientX; const y0 = e.clientY; const { w: w0, h: h0 } = this.size();
      const move = (ev) => { web[`${c.id}W`] = Math.round(Math.max(320, w0 - (ev.clientX - x0))); web[`${c.id}H`] = Math.round(Math.max(240, h0 + ev.clientY - y0)); this.place(); };
      const up = () => { grip.removeEventListener('pointermove', move); saveWeb(); };
      grip.addEventListener('pointermove', move); grip.addEventListener('pointerup', up, { once: true }); grip.addEventListener('pointercancel', up, { once: true });
    });
    this.away = (e) => { if (!this.el || this.el.contains(e.target) || this.btn.contains(e.target) || e.target.closest?.('.menu, .modal-container, .cvsw-keyrec')) return; this.close(); };
    this.esc = (e) => { if (e.key !== 'Escape' || keyRecording || document.querySelector('.cvsw-fallback-menu, .modal-container')) return; e.preventDefault(); e.stopPropagation(); this.close(); };
    this.fit = () => this.place();
    setTimeout(() => { if (this.el) { addEventListener('pointerdown', this.away, true); addEventListener('keydown', this.esc, true); addEventListener('resize', this.fit); } }, 0);
    // the cursor goes back where it was
    const cr = this.caret; this.ta.focus({ preventScroll: true });
    if (cr) { try { this.ta.setSelectionRange(Math.min(cr.a, this.ta.value.length), Math.min(cr.b, this.ta.value.length)); } catch (_) { /* fine */ } this.ta.scrollTop = cr.top; }
    else this.ta.setSelectionRange(this.ta.value.length, this.ta.value.length);
    await this.refresh();
  }
  close() {
    if (!this.el) return;
    this.caret = { a: this.ta.selectionStart, b: this.ta.selectionEnd, top: this.ta.scrollTop };
    removeEventListener('pointerdown', this.away, true); removeEventListener('keydown', this.esc, true); removeEventListener('resize', this.fit);
    this.el.remove(); this.el = null; this.stateEl = null; this.countEl = null;
    this.btn.setAttribute('aria-expanded', 'false'); this.btn.removeClass('is-open');
    void this.save(); // typing is never lost on close
  }
  /** Read the text again (the panel opened, or another app changed it). Unsaved typing is kept. */
  async refresh() {
    let r;
    try { r = await this.cfg.api.read(); } catch (e) { if (!this.loaded) this.state(`Could not load: ${friendlyErr(e)}`, true); if (this.ta && !this.cfg.base) this.ta.readOnly = false; return; }
    if (this.ta) this.ta.readOnly = false;
    if (this.dirty) { // keep the typing, add nothing: the next save wins (CLAUDE.md asks instead, on save)
      if (!this.cfg.base) {
        // typed before the first read came back: the text that was there stays, the typing goes under it
        if (!this.loaded && r.text && r.text !== this.text) { this.text = `${String(r.text).replace(/\s+$/, '')}\n${this.text}`; if (this.el) { this.ta.value = this.text; this.ta.setSelectionRange(this.text.length, this.text.length); this.count(); } }
        this.updated = Number(r.updated) || 0; this.loaded = true;
      }
      return;
    }
    const text = String(r.text ?? '');
    this.updated = Number(r.updated) || 0; this.loaded = true;
    if (text !== this.text) {
      this.text = text;
      if (this.el) { const a = this.ta.selectionStart; const b = this.ta.selectionEnd; const top = this.ta.scrollTop; this.ta.value = text; try { this.ta.setSelectionRange(Math.min(a, text.length), Math.min(b, text.length)); } catch (_) { /* fine */ } this.ta.scrollTop = top; this.count(); }
    }
    if (this.conflict) { this.conflict = null; this.conflictBar(); }
    if (this.stateEl && !this.stateEl.textContent) this.state('Saved');
  }
  async save() {
    clearTimeout(this.timer);
    if (!this.dirty || this.busy || this.conflict) return;
    const c = this.cfg; const text = this.text;
    if (c.base && !text.trim()) { this.state('CLAUDE.md cannot be empty', true); return; } // never send it empty
    if (!text && !this.loaded) return; // never an empty save over text that was not read yet
    this.busy = true; this.state('Saving...');
    try {
      const r = await c.api.write(text, this.updated);
      this.updated = Number(r.updated) || this.updated; this.loaded = true;
      if (this.text === text) { this.dirty = false; this.state('Saved'); }
    } catch (e) {
      if (e.status === 409 && e.body) { this.conflict = e.body; this.conflictBar(); this.state(''); }
      else if (e.status === 400) this.state(c.base ? 'CLAUDE.md cannot be empty' : 'Not saved', true);
      else this.state(`Not saved: ${friendlyErr(e)}`, true);
    } finally { this.busy = false; }
    if (this.dirty && !this.conflict && this.text !== text) this.timer = setTimeout(() => void this.save(), c.delay); // typed on while it saved
  }
  /** CLAUDE.md changed somewhere else since it was loaded (an AI may have edited it): ask. */
  conflictBar() {
    if (!this.warn) return;
    this.warn.empty(); this.warn.toggleClass('is-on', !!this.conflict);
    if (!this.conflict) return;
    const load = $('button', { cls: 'cvsw-qp-warn-btn', text: 'Load the new version', attr: { type: 'button' } });
    const keep = $('button', { cls: 'cvsw-qp-warn-btn', text: 'Keep mine', attr: { type: 'button' } });
    load.onclick = () => { const b = this.conflict; this.conflict = null; this.text = String(b.text ?? ''); this.updated = Number(b.updated) || 0; this.dirty = false; if (this.el) { this.ta.value = this.text; this.count(); } this.conflictBar(); this.state('Saved'); };
    keep.onclick = () => { const b = this.conflict; this.conflict = null; this.updated = Number(b.updated) || 0; this.conflictBar(); void this.save(); };
    this.warn.append($('span', { text: `${this.cfg.title} changed somewhere else` }), $('div', { cls: 'cvsw-qp-warn-btns' }, load, keep));
  }
  /** The Scratchpad's New note: a "---" line under what is there, then a fresh line with the cursor on it. */
  async newNote() {
    if (!this.el) await this.show();
    if (!this.el) return;
    const ta = this.ta; const body = ta.value.replace(/\s+$/, '');
    if (body) {
      const last = body.split('\n').pop().trim();
      const next = `${body}${last === '---' ? '' : '\n---'}\n`;
      if (next !== ta.value) { ta.value = next; ta.dispatchEvent(new Event('input', { bubbles: true })); }
    }
    ta.focus({ preventScroll: true }); ta.setSelectionRange(ta.value.length, ta.value.length); ta.scrollTop = ta.scrollHeight;
  }
}
const friendlyErr = (e) => (cloud.isNetworkError?.(e) || /Failed to fetch|Load failed|NetworkError/.test(String(e?.message)) ? 'CTH Admin did not answer' : String(e?.message || e));
// Notion embeds get neither button (Tony); CLAUDE.md only inside CTH Admin
const scratchPanel = cloud.embedded ? null : new QuickPanel({ id: 'scratch', title: 'Scratchpad', icon: 'notebook-pen', keyName: 'scratchKey', api: scratchApi, placeholder: 'Quick notes, thoughts, drafts...', delay: 400, newNote: true, counter: 'words' });
const claudePanel = !cloud.embedded && inAdmin && claudeApi ? new QuickPanel({ id: 'claude', title: 'CLAUDE.md', icon: 'file-cog', keyName: 'claudeKey', api: claudeApi, base: true, mono: true, delay: 800, counter: 'lines' }) : null;
const quickPanels = [claudePanel, scratchPanel].filter(Boolean);
scratchApi.watch(() => { if (scratchPanel && (scratchPanel.loaded || scratchPanel.open)) void scratchPanel.refresh(); });
// their keys: the Scratchpad's makes a new note (opening the panel), CLAUDE.md's opens and closes it
window.addEventListener('keydown', (e) => {
  if (keyRecording || e.isComposing || e.repeat) return;
  const hit = (panel) => panel && keyMatches(e, panel.key);
  if (hit(scratchPanel)) { e.preventDefault(); e.stopImmediatePropagation(); void scratchPanel.newNote(); }
  else if (hit(claudePanel)) { e.preventDefault(); e.stopImmediatePropagation(); claudePanel.toggle(); }
}, true);
// from the hub: the key pressed on its rail, or another app changed the text or a shared setting
addEventListener('message', (e) => {
  if (e.source !== window.parent || !inAdmin) return;
  const what = e.data && e.data.cthHub;
  if (what === 'scratch-new') void scratchPanel?.newNote();
  else if (what === 'claude-open') claudePanel?.toggle();
  else if (what === 'scratch') { if (scratchPanel && (scratchPanel.loaded || scratchPanel.open)) void scratchPanel.refresh(); }
  else if (what === 'claude') { if (claudePanel?.open && !claudePanel.dirty) void claudePanel.refresh(); }
  else if (what === 'shared') void loadShared();
});
/** Save a quick panel's key; refuses one the other panel or the app already uses. */
async function setKey(keyName, combo, title) {
  const other = keyName === 'scratchKey' ? 'claudeKey' : 'scratchKey';
  if (combo === shared[other]) { new Notice(`${keyLabel(combo)} is already the key for ${other === 'claudeKey' ? 'CLAUDE.md' : 'the Scratchpad'}.`); return false; }
  if (combo === 'mod+[' || combo === 'mod+]') { new Notice(`${keyLabel(combo)} is Back and Forward.`); return false; }
  await setShared({ [keyName]: combo });
  new Notice(`${title}: ${keyLabel(combo)}`, undefined, { routine: true });
  return true;
}
/** Press the new keys (Escape cancels). onKey(combo) gets the keys; returns the function that stops it. */
function recordKey(onKey, onEnd) {
  keyRecording = true; document.documentElement.classList.add('cvsw-key-recording');
  const stop = () => { if (!keyRecording) return; keyRecording = false; document.documentElement.classList.remove('cvsw-key-recording'); removeEventListener('keydown', down, true); removeEventListener('pointerdown', away, true); onEnd?.(); };
  const down = (e) => {
    e.preventDefault(); e.stopImmediatePropagation();
    if (e.key === 'Escape') { stop(); return; }
    const combo = comboOf(e); if (!combo) return;
    stop(); onKey(combo);
  };
  const away = () => stop();
  addEventListener('keydown', down, true); setTimeout(() => { if (keyRecording) addEventListener('pointerdown', away, true); }, 0);
  return stop;
}
/** The recorder right under a bar button (its right-click menu's "Change shortcut"). */
function recordKeyAt(anchor, keyName, title) {
  const tip = $('div', { cls: 'cvsw-keyrec', attr: { role: 'status' } }, $('span', { cls: 'cvsw-keyrec-title', text: 'Press the new keys' }), $('span', { cls: 'cvsw-keyrec-sub', text: `${title} is ${keyLabel(shared[keyName])} now. Escape cancels.` }));
  document.body.append(tip);
  const r = anchor.getBoundingClientRect(); const t = tip.getBoundingClientRect();
  tip.style.top = `${Math.round(r.bottom + 6)}px`; tip.style.left = `${Math.round(Math.max(8, Math.min(innerWidth - t.width - 8, r.right - t.width)))}px`;
  recordKey((combo) => void setKey(keyName, combo, title), () => tip.remove());
}
onShared(() => { for (const p of quickPanels) p.label(); if (workspace.layoutReady && !openPath) renderLibrary(); });

// ---- the top bar ------------------------------------------------------------------------
// Built once and then only its changing parts are swapped (the breadcrumbs, the count, the clip log
// toggle's slot), so nothing in it moves or flickers when a video opens or closes (Tony, 2026-10-02).
// From the left: the tree toggle, the app name, a hairline, breadcrumbs; on the right the quick
// panels (CLAUDE.md, Scratchpad: Button 31, .cvsw-barbtn, the class for every new bar button), then
// Open in full window (embeds), Reload (outside CTH Admin) and the clip log toggle's slot.
let barSkipped = false;
let barParts = null;
function buildBar() {
  const p = barParts = {};
  p.nav = $('div', { cls: 'cvsw-appbar-nav' });
  p.sideSlot = $('span', { cls: 'cvsw-side-slot' });
  p.side = sideToggle();
  const title = $('div', { cls: 'cvsw-appbar-title' }, cloud.embedded ? $('span', { cls: 'cvsw-logo' }, icon('video')) : null, $('span', { cls: 'cvsw-brand-name', text: APP_TITLE })); // the icon stays only in Notion embeds, where the name hides
  const right = $('div', { cls: 'cvsw-appbar-right' },
    claudePanel ? claudePanel.btn : null,
    scratchPanel ? scratchPanel.btn : null,
    cloud.embedded ? btn('Open in full window', 'external-link', () => openFullWindow(), { variant: 'ghost', iconOnly: true }) : null,
    cloud.inHub ? null : reloadBtn(), // in CTH Admin the rail's Refresh does it
    p.sideSlot);
  bar.replaceChildren($('div', { cls: 'cvsw-appbar-brand' }, treeToggle()), $('div', { cls: 'cvsw-appbar-main' }, title, $('span', { cls: 'cvsw-appbar-sep' }), p.nav), right);
}
function renderBar() {
  if (nameEditing(bar)) { barSkipped = true; return; } // renaming in the top bar
  if (!barParts) buildBar();
  let nav; let count = null;
  if (openPath) {
    const f = vault.getFileByPath(openPath);
    nav = crumbs(parentOf(openPath), f ? f.basename : baseName(openPath));
    const cur = nav.querySelector('.cvsw-crumb.is-current');
    if (f && cur) { cur.addClass('is-renamable'); cur.title = 'Click to rename'; cur.onclick = () => editName(cur, f.basename, (want) => renameItem(f, want), 'Video name'); const d = statusDotButton(f, { cls: 'cvsw-crumb-dot' }); if (d) cur.before(d); }
  } else {
    nav = folderCrumbs();
    const n = vault.loaded ? visibleKids(vault.getFolderByPath(currentFolder)).filter(isVideo).length : 0;
    if (n) count = $('span', { cls: 'cvsw-badge', text: String(n), attr: { title: `${n} video${n === 1 ? '' : 's'} in this folder` } });
  }
  barParts.nav.replaceChildren(nav, count || '');
  // the clip log toggle shows in the player only; its place is kept, so the buttons beside it never shift
  if (openPath) { if (!barParts.side.isConnected) barParts.sideSlot.replaceChildren(barParts.side); }
  else barParts.sideSlot.replaceChildren();
  syncBarToggles();
}
function btn(label, ic, onClick, { variant = 'outline', iconOnly = false, cls = '' } = {}) {
  const b = $('button', { cls: `cvsw-btn is-${variant}${iconOnly ? ' is-icon' : ''} ${cls}`.trim(), attr: { type: 'button', 'aria-label': label, title: iconOnly ? label : null } });
  if (ic) b.append(icon(ic));
  if (!iconOnly) b.append($('span', { text: label }));
  b.onclick = onClick;
  return b;
}

// Before the player's settings load, the saved copy says (index.html's head script
// paints the first frame from the same rule, so there is no white flash).
function savedTheme() { try { const d = JSON.parse(localStorage.getItem(DATA_KEY) || 'null'); return (d && (d.settings || d).theme) || 'auto'; } catch (_) { return 'auto'; } }
function applyTheme() {
  const t = plugin?.settings?.theme || savedTheme();
  const sys = matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = t === 'dark' || (t === 'auto' && sys);
  document.body.toggleClass('theme-dark', sys);
  document.body.toggleClass('cvsw-dark', dark);
  document.documentElement.classList.remove('cvsw-predark');
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  // the browser and installed-app title bar: the system colors, or the chosen theme's
  for (const m of document.querySelectorAll('meta[name="theme-color"]')) {
    if (!m.dataset.media) m.dataset.media = m.getAttribute('media') || '';
    if (t === 'auto') { m.setAttribute('content', /dark/.test(m.dataset.media) ? '#0a0a0a' : '#ffffff'); m.setAttribute('media', m.dataset.media); }
    else { m.setAttribute('content', dark ? '#0a0a0a' : '#ffffff'); m.removeAttribute('media'); }
  }
}
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => { applyTheme(); workspace.trigger('css-change'); });

// ---- routing ----------------------------------------------------------------------------

let currentFolder = VP;
let openPath = null;   // vault path of the open video
// A video link may carry a start: #/i/<id>&t=<seconds>&clip=<clip key> (also on #/v/ links).
let pendingLink = null; // { vp, t, clip } for the player about to open (HOST.takeLink)
function linkParams(hash) {
  const amp = hash.indexOf('&');
  if (amp < 0) return { base: hash, t: null, clip: null };
  const q = new URLSearchParams(hash.slice(amp + 1));
  const t = q.has('t') ? Number(q.get('t')) : null;
  return { base: hash.slice(0, amp), t: Number.isFinite(t) && t >= 0 ? t : null, clip: q.get('clip') || null };
}
function route() {
  const lp = linkParams(location.hash);
  const m = lp.base.match(/^#\/(f|v|i)\/(.*)$/);
  if (!m) return { kind: 'f', vp: VP };
  const link = lp.t != null || lp.clip ? { t: lp.t, clip: lp.clip } : null;
  if (m[1] === 'i') return { kind: 'i', id: decodeURIComponent(m[2]), vp: '', link };
  const rel = decodeRel(m[2]);
  return { kind: m[1], vp: rel ? `${VP}/${rel}` : VP, link };
}
// A #/i/<file id> link (what "Copy link" makes, for Notion embeds) names a
// video or folder by id, so it keeps working after a rename or a move.
function resolveRoute() {
  const r = route();
  if (r.kind !== 'i') return r;
  const f = vault.getAllLoadedFiles().find((x) => x.id === r.id);
  if (!f) return { kind: 'missing', id: r.id };
  return { kind: f instanceof VFolder ? 'f' : 'v', vp: f.path, link: r.link };
}
// The Mac app (cloud.local) copies links to the website too, so Notion and the phone can open them.
const LINK_ORIGIN = cloud.local ? 'https://videos.coachtonyhockey.com' : location.origin;
/** at: { t } seconds and / or { clip } key (Player.clipKey) for a link that opens there. */
function linkFor(vp, id, at = {}) {
  let tail = '';
  if (at.t != null && Number.isFinite(at.t)) tail += `&t=${Math.round(at.t * 1000) / 1000}`;
  if (at.clip) tail += `&clip=${encodeURIComponent(at.clip)}`;
  if (id && !String(id).startsWith('local-')) return `${LINK_ORIGIN}/#/i/${encodeURIComponent(id)}${tail}`; // local-: not in the cloud yet, link by path
  const rel = relOf(vp);
  return `${LINK_ORIGIN}/#/${rel ? `${vault.getFolderByPath(vp) ? 'f' : 'v'}/${encodeRel(rel)}${tail}` : ''}`;
}
async function copyLink(vp, id, at = {}) {
  const url = linkFor(vp, id, at);
  const what = at.clip ? 'Link to this clip copied.' : at.t != null ? 'Link at this time copied.' : 'Link copied.';
  await copyText(url, { title: 'Copy link', done: cloud.embedded ? what : `${what} Paste it in Notion and choose Embed; it keeps working if the video is renamed or moved.` });
}
const fullWindowUrl = () => `${location.origin}/${location.hash || '#/'}`;
function openFullWindow() { window.open(fullWindowUrl(), '_blank', 'noopener'); }
/** In an embed, some browser features (fullscreen, clipboard, downloads) can be switched off by the page around it. */
function blockedInEmbed(what) {
  new Notice(createFragment((f) => {
    f.appendText(`${what} is blocked inside this embed. `);
    const b = f.createEl('button', { text: 'Open in full window', cls: 'mod-cta cvs-notice-btn' });
    b.onclick = () => openFullWindow();
  }), 8000);
}
function go(kind, vp) {
  const rel = relOf(vp);
  const hash = kind === 'f' && !rel ? '#/' : `#/${kind}/${encodeRel(rel)}`;
  if (location.hash !== hash) location.hash = hash; else onRoute();
}
function openVaultFile(f) {
  if (!f) return;
  if (VIDEO_RE.test(f.path)) go('v', f.path);
  else if (f instanceof VFolder) go('f', f.path);
  else new Notice(`${f.name} is not a video, so it does not open here.`);
}
async function onRoute() {
  const r = resolveRoute();
  if (sel.size <= 1) sel.clear(); // a one-row pick ends when you go somewhere; 2+ stay picked
  if (isNarrow()) shell.removeClass('is-drawer-open');
  if (r.kind === 'missing') {
    await closeVideo();
    showMain($('div', { cls: 'cvsw-lib' }, $('div', { cls: 'cvsw-lib-body' }, !vault.loaded
      ? empty('cloud', 'Loading your videos', 'One moment.')
      : empty('search-x', 'This video is not here any more', 'It was deleted, or moved out of the videos folder. If it is still uploading, it opens here by itself when it arrives.'))));
    waitingId = r.id;
    return;
  }
  waitingId = null;
  if (r.kind === 'v') {
    currentFolder = parentOf(r.vp) || VP;
    if (openPath !== r.vp) { pendingLink = r.link ? { vp: r.vp, ...r.link } : null; await openVideo(r.vp); }
    else if (r.link) curPlayer()?.openLink(r.link);
  }
  else { const was = !!openPath; await closeVideo({ keep: was }); currentFolder = vault.getFolderByPath(r.vp) ? r.vp : VP; if (was) tableHot = true; renderLibrary({ fade: was }); } // back from a video: the table is where it was (scroll, ticks, the focused cell)
  if (r.kind !== 'v') { renderTree(); renderBar(); } // openVideo does both itself, in the frame the player appears
  // CTH Admin keeps one back / forward history across its apps: every place we land is reported
  if (cloud.inHub) { try { window.parent.postMessage({ cthHub: 'nav', url: location.hash || '#/' }, location.ancestorOrigins?.[0] || '*'); } catch (_) { /* fine */ } }
}
window.addEventListener('hashchange', () => void onRoute());

// ---- the file tree -----------------------------------------------------------------------

let treeFilter = '';
let filterOpen = false;   // the filter box shows after its button is pressed (or while it holds text)
let focusFilter = false;
const isVideo = (f) => f instanceof VFile && VIDEO_RE.test(f.name);
const visibleKids = (folder) => (folder?.children || []).filter((c) => !c.name.startsWith('.') && (c instanceof VFolder || isVideo(c)));
const videosUnder = (folder) => { let n = 0; const walk = (d) => { for (const c of d.children) { if (c instanceof VFolder) walk(c); else if (isVideo(c)) n++; } }; if (folder) walk(folder); return n; };
const sortKids = (a, b) => ((a instanceof VFolder) === (b instanceof VFolder) ? a.name.localeCompare(b.name, undefined, { numeric: true }) : a instanceof VFolder ? -1 : 1);

// Double-click (two clicks on the same row within 450 ms) renames it in place. The rows are
// rebuilt on every click (the first click opens), so this goes by path, not by element.
let lastTreeClick = null;
function treeDouble(f, undo) {
  const now = Date.now(); const l = lastTreeClick;
  if (l && l.vp === f.path && now - l.t < 450) { lastTreeClick = null; l.undo?.(); requestAnimationFrame(() => renameInTree(f.path)); return true; }
  lastTreeClick = { vp: f.path, t: now, undo };
  return false;
}
let treeEditing = false; let treeAgain = false;
/** Rename a tree row in place: Enter or clicking away saves, Escape cancels. */
function renameInTree(vp) {
  const f = vault.getAbstractFileByPath(vp);
  const row = tree.querySelector(`.cvsw-tree-list [data-vp="${CSS.escape(vp)}"]`);
  const name = row?.querySelector('.cvsw-row-name');
  if (!f || !name) return false;
  const current = f instanceof VFolder ? f.name : f.basename;
  // a text area so a long name wraps over lines like the row (Enter still saves; no line breaks)
  const input = $('textarea', { cls: 'cvsw-input cvsw-tree-rename', attr: { rows: '1', spellcheck: 'false', autocomplete: 'off', 'aria-label': f instanceof VFolder ? 'Folder name' : 'Video name' } });
  input.value = current;
  const fit = () => { input.style.height = '0px'; input.style.height = `${input.scrollHeight + 2}px`; };
  input.addEventListener('input', () => { if (/\n/.test(input.value)) input.value = input.value.replace(/\n+/g, ' '); fit(); });
  treeEditing = true; row.draggable = false; row.addClass('is-renaming');
  name.replaceWith(input);
  fit(); input.focus(); input.select();
  let done = false;
  const finish = async (ok) => {
    if (done) return;
    done = true; treeEditing = false;
    const want = input.value.trim();
    if (ok && want && want !== current) await renameItem(f, want);
    renderTree(); treeAgain = false;
  };
  input.addEventListener('click', (e) => e.stopPropagation());
  input.addEventListener('dblclick', (e) => e.stopPropagation());
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); void finish(true); }
    else if (e.key === 'Escape') { e.preventDefault(); void finish(false); }
  });
  input.addEventListener('blur', () => void finish(true));
  return true;
}
// Guide lines: a hairline per nesting level (shadcn tree view). The level of the folder you
// are in (the open video's folder, or the folder shown) is darker; while dragging, the
// target folder's level turns the drop color (markGuides).
function markGuides(vp, cls) {
  tree.querySelectorAll(`.cvsw-guide.${cls}`).forEach((g) => g.classList.remove(cls));
  if (vp && vp !== VP) tree.querySelectorAll(`.cvsw-guide[data-g="${CSS.escape(vp)}"]`).forEach((g) => g.classList.add(cls));
}

function renderTree() {
  if (treeEditing) { treeAgain = true; return; }
  const listOld = tree.querySelector('.cvsw-tree-list');
  const scroll = listOld?.scrollTop || 0;
  const focusVp = document.activeElement?.closest?.('.cvsw-tree-list .cvsw-row[data-vp]')?.dataset.vp; // keyboard focus stays on its row
  const hadFocus = document.activeElement?.classList.contains('cvsw-tree-search');
  const expanded = new Set(web.expanded);
  if (openPath) { let p = parentOf(openPath); while (p && p !== VP) { expanded.add(p); p = parentOf(p); } }
  const q = treeFilter.trim().toLowerCase();
  const list = $('div', { cls: 'cvsw-tree-list', attr: { role: 'tree' } });
  const matches = (f) => f.path.toLowerCase().includes(q);
  const folderHasMatch = (d) => d.children.some((c) => (c instanceof VFolder ? folderHasMatch(c) : isVideo(c) && matches(c)));
  const walk = (folder, depth) => {
    for (const f of visibleKids(folder).sort(sortKids)) {
      if (q && (f instanceof VFolder ? !folderHasMatch(f) : !matches(f))) continue;
      const dir = f instanceof VFolder;
      const open = dir && (q || expanded.has(f.path));
      const active = dir ? (!openPath && f.path === currentFolder) : f.path === openPath;
      const row = $('div', { cls: `cvsw-row${active ? ' is-active' : ''}${dir ? ' is-dir' : ''}`, attr: { role: 'treeitem', tabindex: '0', title: relOf(f.path), 'data-vp': f.path } });
      row.style.paddingLeft = `${8 + depth * 14}px`;
      if (web.treeGuides !== false) {
        const parts = relOf(f.path).split('/');
        for (let k = 0; k < depth; k++) { const g = $('span', { cls: 'cvsw-guide', attr: { 'data-g': `${VP}/${parts.slice(0, k + 1).join('/')}`, 'aria-hidden': 'true' } }); g.style.left = `${8 + k * 14 + 7}px`; row.append(g); }
      }
      if (dir) {
        const chev = $('span', { cls: 'cvsw-chev' }, icon(open ? 'chevron-down' : 'chevron-right'));
        chev.onclick = (e) => { e.stopPropagation(); toggleExpanded(f.path); };
        row.append(chev, icon(open ? 'folder-open' : 'folder'), $('span', { cls: 'cvsw-row-name', text: f.name }));
        row.onclick = (e) => { if (selClick(e, f.path)) return; if (treeDouble(f, () => toggleExpanded(f.path, !!open))) return; toggleExpanded(f.path, !open); go('f', f.path); }; // an open folder closes, a closed one opens; a double-click renames
      } else {
        const dot = treeDot(f); // the status dot sits in the icon slot (videos have no icon), or after the name
        row.append(...[$('span', { cls: 'cvsw-chev' }), web.statusPos === 'after' ? null : dot, $('span', { cls: 'cvsw-row-name', text: f.basename }), web.statusPos === 'after' ? dot : null].filter(Boolean));
        row.onclick = (e) => { if (selClick(e, f.path)) return; if (treeDouble(f)) return; go('v', f.path); };
      }
      // the row's own buttons (right edge, on hover): add videos and a new folder, inside a folder or beside a video
      const into = dir ? f.path : parentOf(f.path);
      const rb = (label, ic, fn) => { const b = $('button', { cls: 'cvsw-row-btn', attr: { type: 'button', 'aria-label': label } }, icon(ic)); b.onclick = (e) => { e.stopPropagation(); fn(); }; b.ondblclick = (e) => e.stopPropagation(); return b; };
      row.append($('span', { cls: 'cvsw-row-actions' }, rb(dir ? 'Add videos inside' : 'Add videos here', 'plus', () => pickUpload(into)), rb(dir ? 'New folder inside' : 'New folder here', 'folder-plus', () => void newFolder(into))));
      if (dir) row.setAttribute('aria-expanded', String(!!open));
      row.onkeydown = (e) => {
        if (treeKey(e)) return; // Cmd+A, Escape, Shift+arrows, and with 2+ picked: F2 / Enter, Cmd+D, Delete
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.shiftKey && !e.metaKey && !e.ctrlKey) { e.preventDefault(); const rows = treeRows(); rows[rows.indexOf(row) + (e.key === 'ArrowDown' ? 1 : -1)]?.focus(); return; }
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); row.click(); }
        if (e.key === 'F2') { e.preventDefault(); renameInTree(f.path); }
        if (!dir && e.key === 's' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey && web.statusDots !== false) { e.preventDefault(); const b = row.querySelector('.cvsw-sdot-btn'); statusMenu(null, [f], b || row); }
        if (dir && e.key === 'ArrowRight' && !open) toggleExpanded(f.path, true);
        if (dir && e.key === 'ArrowLeft' && open) toggleExpanded(f.path, false);
      };
      row.oncontextmenu = (e) => { e.preventDefault(); treeHot = true; if (sel.size >= 2 && sel.has(f.path)) { bulkMenu(e); return; } clearSel(); itemMenu(e, f); };
      wireDrag(row, f);
      if (dir) wireDrop(row, f.path, { expand: true });
      else wireDrop(row, parentOf(f.path), { mark: () => folderRow(parentOf(f.path)) });
      list.append(row);
      if (open) walk(f, depth + 1);
    }
  };
  const top = vault.getFolderByPath(VP);
  if (vault.loaded) walk(top, 0);
  if (vault.loaded && !list.childElementCount) list.append($('div', { cls: 'cvsw-tree-empty', text: q ? 'Nothing matches.' : 'No videos yet.' }));
  if (!vault.loaded) list.append($('div', { cls: 'cvsw-tree-empty', text: 'Loading...' }));

  const rootRow = $('button', { cls: `cvsw-row cvsw-rootrow${!openPath && currentFolder === VP ? ' is-active' : ''}`, attr: { type: 'button', title: 'All videos', 'data-vp': VP } }, icon(cloud.local ? 'hard-drive' : 'cloud'), $('span', { cls: 'cvsw-row-name', text: ROOT_NAME }));
  rootRow.onclick = () => { clearSel(); go('f', VP); };
  rootRow.oncontextmenu = (e) => { e.preventDefault(); treeMenu(e); }; // the top level's statuses live there too
  wireDrop(rootRow, VP);
  wireDrop(list, VP, { mark: rootRow }); // empty space under the rows = the top folder
  wireBand(list);
  list.addEventListener('contextmenu', (e) => { if (e.target.closest('.cvsw-row')) return; e.preventDefault(); treeMenu(e); });
  const search = $('input', { cls: 'cvsw-input cvsw-tree-search', type: 'search', placeholder: 'Filter', value: treeFilter, attr: { spellcheck: 'false', 'aria-label': 'Filter the file tree' } });
  // the filter narrows the tree and, in the library, lists every matching video under the folder shown
  search.oninput = () => { treeFilter = search.value; renderTree(); clearTimeout(search.t); search.t = setTimeout(() => { if (!openPath) renderLibrary(); }, 120); };
  search.onkeydown = (e) => { if (e.key === 'Escape') { e.preventDefault(); treeFilter = ''; filterOpen = false; renderTree(); if (!openPath) renderLibrary(); } };
  // the panel's header row: add videos (the app's main action), new folder, copy link, filter
  const linkOf = () => (openPath ? [openPath, vault.getFileByPath(openPath)?.id] : [currentFolder, vault.getFolderByPath(currentFolder)?.id]);
  const head = $('div', { cls: 'cvsw-tree-actions cvsw-panel-head' },
    btn('Add videos', 'plus', () => pickUpload(currentFolder), { variant: 'ghost', iconOnly: true }),
    btn('New folder', 'folder-plus', () => void newFolder(currentFolder), { iconOnly: true, variant: 'ghost' }),
    btn('Collapse all folders', 'chevrons-down-up', () => { web.expanded = []; saveWeb(); renderTree(); }, { iconOnly: true, variant: 'ghost' }),
    // (Copy Notion embed link retired 2026-10-02)
    $('span', { cls: 'cvsw-grow' }),
    btn(filterOpen || treeFilter ? 'Close the filter' : 'Filter the tree', 'search', () => { if (filterOpen || treeFilter) { filterOpen = false; treeFilter = ''; if (!openPath) renderLibrary(); } else { filterOpen = true; focusFilter = true; } renderTree(); }, { iconOnly: true, variant: filterOpen || treeFilter ? 'secondary' : 'ghost' }));
  tree.replaceChildren(
    head,
    filterOpen || treeFilter ? $('div', { cls: 'cvsw-search cvsw-tree-searchwrap' }, icon('search'), search) : '',
    rootRow, list, bulkBar,
    $('div', { cls: 'cvsw-tree-foot cvsw-panel-foot' }, btn('Settings', 'settings', () => openSettings(), { variant: 'ghost' }), btn('Keyboard shortcuts', 'keyboard', () => openShortcuts(), { variant: 'ghost', iconOnly: true })),
    treeResizer());
  tree.querySelector('.cvsw-tree-list').scrollTop = scroll;
  applySel();
  if (focusVp && !document.activeElement?.closest?.('.cvsw-tree-list .cvsw-row')) tree.querySelector(`.cvsw-tree-list .cvsw-row[data-vp="${CSS.escape(focusVp)}"]`)?.focus({ preventScroll: true });
  markGuides(openPath ? parentOf(openPath) : currentFolder, 'is-on');
  paintProgress();
  if (hadFocus || focusFilter) { focusFilter = false; search.focus(); search.setSelectionRange(search.value.length, search.value.length); }
}
const folderRow = (vp) => tree.querySelector(`[data-vp="${CSS.escape(vp)}"]`);
function toggleExpanded(p, force) {
  const has = web.expanded.includes(p);
  const want = force ?? !has;
  web.expanded = web.expanded.filter((x) => x !== p);
  if (want) web.expanded.push(p);
  saveWeb(); renderTree();
}

// ---- picking several rows in the tree (Tony, 2026-10-02; the same in CTH Docs) -------------------
// Finder rules: a click picks one row (the normal active look), Cmd+click adds or removes,
// Shift+click a range of visible rows, a rubber band from empty space, Cmd+A all visible rows,
// Escape clears. Folders and videos mix; with 2 or more picked, the bulk bar (Rename, Duplicate,
// Move, Delete, Clear) shows above the tree's footer and the right-click menu and keys act on all.
const sel = new Set();      // vault paths picked in the tree
let selAnchor = null;       // where a Shift range starts (the last plain or Cmd click)
let treeHot = false;        // the last click was in the tree, so its keys (Cmd+A, Escape) belong to it
const treeRows = () => [...tree.querySelectorAll('.cvsw-tree-list .cvsw-row[data-vp]')];
const visibleVps = () => treeRows().map((r) => r.dataset.vp);
const activeRowVp = () => tree.querySelector('.cvsw-tree-list .cvsw-row.is-active[data-vp]')?.dataset.vp || null;
/** The order rows have in the tree (folders first, by name), for every row, open or not. */
function treeOrder() {
  const m = new Map(); let i = 0;
  const walk = (d) => { for (const c of visibleKids(d).sort(sortKids)) { m.set(c.path, i++); if (c instanceof VFolder) walk(c); } };
  if (vault.loaded) walk(vault.getFolderByPath(VP));
  return m;
}
/** The picked items in tree order; tops: without anything inside a picked folder (it goes with the folder). */
function selItems({ tops = true } = {}) {
  const ord = treeOrder();
  let items = [...sel].map((p) => vault.getAbstractFileByPath(p)).filter((f) => f && f.path && f.path !== VP && f.path.startsWith(`${VP}/`));
  if (tops) items = items.filter((f) => !items.some((d) => d !== f && d instanceof VFolder && f.path.startsWith(`${d.path}/`)));
  return items.sort((a, b) => (ord.get(a.path) ?? 1e9) - (ord.get(b.path) ?? 1e9));
}
let relocating = 0; // moves and renames in flight (applySel leaves the selection alone meanwhile)
const plural = (n, word = 'item') => `${n} ${word}${n === 1 ? '' : 's'}`;
/** One picked row looks like the open one (no extra highlight); a picked row that is not open, or 2+, show the selection color. */
function applySel() {
  if (vault.loaded && !relocating) for (const p of [...sel]) if (!vault.index.has(p)) sel.delete(p); // never mid-move: a picked row's old path is gone before the selection follows it
  for (const r of treeRows()) {
    const on = sel.has(r.dataset.vp) && (sel.size >= 2 || !r.classList.contains('is-active'));
    r.classList.toggle('is-selected', on);
    r.setAttribute('aria-selected', String(sel.has(r.dataset.vp)));
  }
  renderBulkBar();
}
/** Before Cmd or Shift adds to it: the open row counts as picked (it looks picked). */
function seedSel() {
  if (sel.size) return;
  const a = activeRowVp();
  if (a) { sel.add(a); selAnchor = selAnchor || a; }
}
function selectRange(toVp, add) {
  const rows = visibleVps();
  const from = rows.indexOf(selAnchor ?? activeRowVp() ?? toVp);
  const to = rows.indexOf(toVp);
  if (!add) sel.clear();
  if (from < 0 || to < 0) { sel.add(toVp); selAnchor = toVp; return; }
  for (const p of rows.slice(Math.min(from, to), Math.max(from, to) + 1)) sel.add(p);
}
/** A click on a row: true when it only changed the selection (Cmd or Shift held), so nothing opens. */
function selClick(e, vp) {
  treeHot = true;
  const add = e.metaKey || e.ctrlKey;
  if (e.shiftKey) { seedSel(); selectRange(vp, add); applySel(); return true; }
  if (add) { seedSel(); if (sel.has(vp)) sel.delete(vp); else sel.add(vp); selAnchor = vp; applySel(); return true; }
  sel.clear(); selAnchor = vp; applySel();
  return false;
}
function clearSel() { if (!sel.size) return; sel.clear(); applySel(); }

// The bar: N selected, then Rename, Duplicate, Move, Delete, Clear (icon buttons with the black tooltip)
const bulkBar = $('div', { cls: 'cvsw-bulkbar', attr: { role: 'toolbar', 'aria-label': 'Selected items' } });
function renderBulkBar() {
  const n = sel.size;
  const on = n >= 2;
  tree.classList.toggle('has-bulk', on);
  if (!on) { bulkBar.classList.remove('is-on'); bulkBar.setAttribute('aria-hidden', 'true'); return; }
  const b = (label, ic, fn) => { const x = $('button', { cls: 'cvsw-bulk-btn', attr: { type: 'button', 'aria-label': label } }, icon(ic)); x.onclick = (e) => { e.stopPropagation(); fn(x); }; return x; };
  bulkBar.replaceChildren(
    $('span', { cls: 'cvsw-bulk-count', text: `${n} selected` }),
    b('Rename (F2)', 'pencil-line', () => openBulkRename()),
    b('Duplicate (⌘D)', 'copy-plus', () => void bulkDuplicate()),
    b('Move', 'folder-input', () => bulkMovePicker()),
    b('Delete (⌫)', 'trash-2', () => bulkDeleteConfirm()),
    b('Clear (esc)', 'x', () => clearSel()));
  const vids = selItems({ tops: false }).filter(isVideo); if (vids.length && web.statusDots !== false) bulkBar.querySelector('.cvsw-bulk-btn[aria-label="Move"]').before(b('Status', 'circle-dot', (x) => statusMenu(null, vids, x)));
  bulkBar.removeAttribute('aria-hidden');
  if (!bulkBar.classList.contains('is-on')) { void bulkBar.offsetWidth; bulkBar.classList.add('is-on'); } // slides up
}

/** Right-click a picked row (2+ picked): the bulk menu. */
function bulkMenu(e) {
  const all = selItems({ tops: false }); const tops = selItems();
  const m = new Menu();
  m.addItem((i) => i.setTitle(`Rename ${plural(all.length)}...`).setIcon('pencil-line').onClick(() => openBulkRename()));
  m.addItem((i) => i.setTitle('Duplicate').setIcon('copy-plus').onClick(() => void bulkDuplicate()));
  m.addItem((i) => i.setTitle('Move...').setIcon('folder-input').onClick(() => bulkMovePicker()));
  { const vids = all.filter(isVideo); if (vids.length) m.addItem((i) => i.setTitle('Status...').setIcon('circle-dot').onClick(() => statusMenu(null, vids, document.querySelector('.cvsw-bulkbar') || tree))); }
  m.addItem((i) => i.setTitle('Copy names').setIcon('clipboard-copy').onClick(() => void copyText(all.map((f) => (f instanceof VFolder ? f.name : f.basename)).join('\n'), { title: 'Copy names', done: 'Names copied' })));
  if (cloud.local && all[0]) m.addItem((i) => i.setTitle('Show in Finder').setIcon('external-link').onClick(() => void revealOnMac(all[0].path)));
  m.addSeparator();
  m.addItem((i) => i.setTitle(`Delete ${plural(tops.length)}`).setIcon('trash-2').setWarning(true).onClick(() => bulkDeleteConfirm()));
  m.showAtMouseEvent(e);
}

/** One toast for a bulk action: what failed (the first reason), or what happened. */
// ---- quiet confirmations and the app's undo stack (Tony, 2026-10-02) -------------------------------------
// A routine edit (one item) shows no toast unless Settings > General > Confirmations is Show all; errors, deletions,
// bulk actions on 2+ items and things that finish out of sight still do (obsidian-shim.js Notice, { routine }).
// Every action with an Undo goes on this stack (last 50): Cmd+Z undoes, Shift+Cmd+Z redoes (when it can), in the
// library and tree; the player keeps its own Cmd+Z, and text fields keep theirs.
const undos = []; const redos = [];
function pushUndo(text, undo, redo) { undos.push({ text, undo, redo }); if (undos.length > 50) undos.shift(); redos.length = 0; }
/** A toast's own Undo button was used: the same step leaves the stack. */
function dropUndo(undo) { const i = undos.findIndex((x) => x.undo === undo); if (i > -1) undos.splice(i, 1); }
addEventListener('keydown', (e) => {
  if (!(e.metaKey || e.ctrlKey) || e.altKey || e.key.toLowerCase() !== 'z' || e.defaultPrevented || openPath) return;
  const t = e.target; if (t?.closest?.('input, textarea, select, [contenteditable="true"], .modal-container')) return;
  const x = e.shiftKey ? redos.pop() : undos.pop(); if (!x) return;
  e.preventDefault();
  if (e.shiftKey) { void x.redo(); undos.push(x); } else { void x.undo(); if (x.redo) redos.push(x); }
});
function bulkToast(failed, tried, verb, ok) {
  if (failed.length) new Notice(`${failed.length} of ${tried} could not be ${verb}: ${failed[0]}`, 8000);
  else if (ok) new Notice(ok, typeof ok === 'string' ? undefined : 8000, { routine: tried < 2 }); // one item: a routine edit
}
/** A toast with Undo, which moves or renames each item back (newest first); the step goes on the undo stack too. */
function undoToast(text, pairs, verb, back) {
  const undo = () => undoMoves(pairs, verb, back);
  pushUndo(text, undo);
  return createFragment((fr) => {
    fr.appendText(`${text} `);
    const b = fr.createEl('button', { text: 'Undo', cls: 'cvs-notice-btn' });
    b.onclick = () => { if (b.disabled) return; b.disabled = true; dropUndo(undo); void undo(); };
  });
}
async function undoMoves(pairs, verb, back) {
  const errors = []; const opts = { quiet: true, errors }; let n = 0;
  for (const [from, to] of [...pairs].reverse()) {
    let f = vault.getAbstractFileByPath(to);
    if (!f) { await refresh({ quiet: true }); f = vault.getAbstractFileByPath(to); }
    if (!f) { errors.push(`"${baseName(to)}" is not there any more`); continue; }
    if (await relocate(f, from, opts)) n++;
  }
  await settleMoves(opts.reopen);
  bulkToast(errors, pairs.length, verb, n ? back : '');
}

async function bulkDuplicate() {
  const items = selItems(); if (!items.length) return;
  const taken = new Set(); const made = []; const errors = [];
  for (const f of items) { try { made.push(await copyItem(f, taken)); } catch (e) { errors.push(`${f.name}: ${e.message || e}`); } }
  await refresh({ quiet: true });
  if (made.length) { sel.clear(); for (const p of made) sel.add(p); selAnchor = made[0]; }
  renderTree();
  bulkToast(errors, items.length, 'duplicated', `Duplicated ${plural(made.length)}`);
}

/** Move the items into a folder: skips what is already there and folders into themselves. */
async function bulkMove(items, folderVp) {
  const errors = []; const opts = { quiet: true, errors }; const moved = []; let tried = 0;
  for (const f of items) {
    if (!canDrop(f, folderVp)) continue;
    tried++;
    const from = f.path; const to = await relocate(f, `${folderVp}/${f.name}`, opts);
    if (to) moved.push([from, to]);
  }
  await settleMoves(opts.reopen);
  const where = folderVp === VP ? ROOT_NAME : baseName(folderVp);
  if (!tried) { new Notice(`They are already in ${where}.`); return; }
  bulkToast(errors, tried, 'moved', moved.length ? undoToast(`Moved ${plural(moved.length)} to ${where}.`, moved, 'moved back', 'Moved back.') : '');
}
function bulkMovePicker() { const items = selItems(); if (items.length) moveManyPicker(items, { noun: 'item', pick: (to) => void bulkMove(items, to) }); }

function bulkDeleteConfirm() {
  const items = selItems(); if (!items.length) return;
  const n = items.length;
  const md = new Modal(app);
  md.titleEl.setText(`Delete ${plural(n)}?`);
  md.contentEl.createEl('p', { text: `They go to the ${cloud.local ? 'Trash on this Mac' : 'trash (kept 30 days)'}, so you can still get them back.` });
  const row = md.contentEl.createDiv({ cls: 'modal-button-container' });
  row.createEl('button', { text: 'Cancel' }).onclick = () => md.close();
  const del = row.createEl('button', { text: 'Delete', cls: 'mod-warning' });
  del.onclick = async () => {
    md.close();
    const within = (f, p) => p === f.path || p.startsWith(`${f.path}/`);
    const openGone = openPath && items.some((f) => within(f, openPath));
    if (openGone) await closeVideo();
    const errors = []; let done = 0;
    for (const f of items) {
      try {
        const note = f instanceof VFile ? plugin?.sidecarFor(f.path) : null;
        await vault.trash(f); done++;
        const nf = note && vault.getFileByPath(note); if (nf) await vault.trash(nf).catch(() => {});
      } catch (err) { noteTrouble(err); errors.push(`${f.name}: ${err.message || err}`); }
    }
    sel.clear();
    await refresh({ quiet: true });
    let here = currentFolder; while (here !== VP && !vault.getFolderByPath(here)) here = parentOf(here) || VP;
    if (openGone || here !== currentFolder) go('f', here); else renderTree();
    bulkToast(errors, n, 'deleted', `Moved ${plural(done)} to the Trash`);
  };
  md.open(); setTimeout(() => del.focus(), 0);
}

// Keys while the tree has focus (a row focused, or right after a click in it), never in a field
function treeKey(e) {
  if (isModalOpen() || e.isComposing || e.altKey) return false;
  if (e.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return false;
  const mod = e.metaKey || e.ctrlKey; const k = e.key;
  const stop = () => { e.preventDefault(); e.stopPropagation(); return true; };
  if (mod && !e.shiftKey && k.toLowerCase() === 'a') { const v = visibleVps(); if (!v.length) return false; sel.clear(); for (const p of v) sel.add(p); applySel(); return stop(); }
  if (k === 'Escape' && sel.size) { clearSel(); return stop(); }
  if (e.shiftKey && !mod && (k === 'ArrowUp' || k === 'ArrowDown')) {
    const rows = treeRows(); const cur = document.activeElement?.closest?.('.cvsw-tree-list .cvsw-row[data-vp]');
    let i = rows.indexOf(cur); if (i < 0) i = rows.findIndex((r) => r.dataset.vp === (selAnchor ?? activeRowVp()));
    if (i < 0) return false;
    seedSel(); if (!selAnchor) selAnchor = rows[i].dataset.vp;
    const next = rows[Math.max(0, Math.min(rows.length - 1, i + (k === 'ArrowDown' ? 1 : -1)))];
    selectRange(next.dataset.vp, false); applySel(); next.focus({ preventScroll: false });
    return stop();
  }
  if (sel.size < 2) return false;
  if ((k === 'F2' || k === 'Enter') && !mod && !e.shiftKey) { openBulkRename(); return stop(); }
  if (mod && !e.shiftKey && k.toLowerCase() === 'd') { void bulkDuplicate(); return stop(); }
  if (k === 'Delete' || k === 'Backspace') { bulkDeleteConfirm(); return stop(); }
  return false;
}
tree.addEventListener('pointerdown', () => { treeHot = true; }, true);
document.addEventListener('pointerdown', (e) => { if (!tree.contains(e.target)) treeHot = false; }, true);
document.addEventListener('keydown', (e) => {
  if (e.defaultPrevented) return;
  const a = document.activeElement;
  if (!(tree.contains(a) || (treeHot && (!a || a === document.body)))) return;
  treeKey(e);
});

// The rubber band: press on empty space in the tree and drag (after 4 px); every row it touches is
// picked live; Cmd or Shift at the start adds to what is picked; the list scrolls near its edges.
// A plain click on empty space clears the selection.
let band = null;
function wireBand(list) {
  list.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.pointerType === 'touch') return;
    if (e.target.closest('.cvsw-row, button, input, textarea, select, a')) return;
    const r0 = list.getBoundingClientRect();
    if (e.clientX > r0.left + list.clientWidth) return; // the scrollbar
    const add = e.metaKey || e.ctrlKey || e.shiftKey;
    const at = (x, y) => { const l = tree.querySelector('.cvsw-tree-list'); const r = l.getBoundingClientRect(); return { x: x - r.left + l.scrollLeft, y: y - r.top + l.scrollTop }; };
    band = { x0: e.clientX, y0: e.clientY, start: at(e.clientX, e.clientY), add, base: new Set(add ? sel : []), on: false, px: e.clientX, py: e.clientY, el: null, raf: 0 };
    const b = band;
    const paint = () => {
      const l = tree.querySelector('.cvsw-tree-list'); if (!l) return;
      const p = at(b.px, b.py);
      const x = Math.min(b.start.x, p.x); const y = Math.min(b.start.y, p.y); const w = Math.abs(p.x - b.start.x); const h = Math.abs(p.y - b.start.y);
      if (!b.el || b.el.parentElement !== l) { b.el?.remove(); b.el = $('div', { cls: 'cvsw-band', attr: { 'aria-hidden': 'true' } }); l.append(b.el); }
      Object.assign(b.el.style, { left: `${x}px`, top: `${y}px`, width: `${w}px`, height: `${h}px` });
      sel.clear(); for (const p0 of b.base) sel.add(p0);
      for (const row of treeRows()) {
        const t = row.offsetTop; const lft = row.offsetLeft;
        if (t < y + h && t + row.offsetHeight > y && lft < x + w && lft + row.offsetWidth > x) sel.add(row.dataset.vp);
      }
      applySel();
    };
    const tick = () => { // scroll near the top or bottom edge while the band is out
      b.raf = 0; if (band !== b || !b.on) return;
      const l = tree.querySelector('.cvsw-tree-list'); if (!l) return;
      const r = l.getBoundingClientRect(); const edge = 28;
      const dy = b.py < r.top + edge ? -Math.ceil((r.top + edge - b.py) / 3) : b.py > r.bottom - edge ? Math.ceil((b.py - (r.bottom - edge)) / 3) : 0;
      if (dy) { const before = l.scrollTop; l.scrollTop += dy; if (l.scrollTop !== before) paint(); b.raf = requestAnimationFrame(tick); }
    };
    const move = (ev) => {
      b.px = ev.clientX; b.py = ev.clientY;
      if (!b.on) { if (Math.hypot(ev.clientX - b.x0, ev.clientY - b.y0) < 4) return; b.on = true; document.documentElement.classList.add('cvsw-banding'); if (!b.add) selAnchor = null; }
      paint();
      if (!b.raf) b.raf = requestAnimationFrame(tick);
    };
    const up = () => {
      removeEventListener('pointermove', move, true); removeEventListener('pointerup', up, true); removeEventListener('pointercancel', up, true);
      cancelAnimationFrame(b.raf); b.el?.remove(); document.documentElement.classList.remove('cvsw-banding');
      if (!b.on && !b.add) clearSel(); // a plain click on empty space
      if (band === b) band = null;
    };
    addEventListener('pointermove', move, true); addEventListener('pointerup', up, true); addEventListener('pointercancel', up, true);
  });
}

// ---- smart rename (Rename N items) ------------------------------------------------------------
const BAD_NAME = /[/\\:*?"<>|]/;
const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'vs', 'with']);
const capFirst = (w) => w.replace(/\p{L}/u, (c) => c.toUpperCase());
/** Title Case (small words lower except the first), Sentence case, lowercase, UPPERCASE. All-caps
 *  words of 2 to 4 letters (AAA, PK) and words with digits stay as they are. */
function changeCase(s, mode) {
  if (mode === 'lower') return s.toLowerCase();
  if (mode === 'upper') return s.toUpperCase();
  let first = true;
  return s.replace(/\S+/g, (w) => {
    const isFirst = first; first = false;
    const core = w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    if (/\d/.test(w) || /^[A-Z]{2,4}$/.test(core)) return w;
    const lw = w.toLowerCase();
    if (mode === 'sentence') return isFirst ? capFirst(lw) : lw;
    return !isFirst && SMALL_WORDS.has(core.toLowerCase()) ? lw : capFirst(lw);
  });
}
/** "Breakout 3", "Breakout copy 2" -> "Breakout" (the Number mode's starting name). */
function stripNumber(s) {
  let t = String(s); let prev;
  do { prev = t; t = t.replace(/\s*\bcopy(\s*\d+)?\s*$/i, '').replace(/[\s_-]*\(?\d+\)?\s*$/, '').trim(); } while (t !== prev && t);
  return t || String(s).trim();
}
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
function openBulkRename() { const items = selItems({ tops: false }); if (items.length) new BulkRename(items).open(); }
class BulkRename extends Modal {
  constructor(items) {
    super(app);
    this.items = items;
    const b0 = items[0] instanceof VFolder ? items[0].name : items[0].basename;
    this.st = { mode: 'replace', find: '', repl: '', matchCase: false, regex: false, add: '', addAt: 'after', name: stripNumber(b0), start: 1, digits: 1, numAt: 'after', sep: ' ', caseMode: 'title' };
  }
  seg(options, value, onPick, label) {
    const g = $('div', { cls: 'cvsw-seg', attr: { role: 'radiogroup', 'aria-label': label } });
    for (const [v, t] of options) {
      const b = $('button', { cls: `cvsw-seg-btn${v === value ? ' is-on' : ''}`, attr: { type: 'button', role: 'radio', 'aria-checked': String(v === value), 'data-v': String(v) } }, $('span', { text: t }));
      b.onclick = () => { g.querySelectorAll('.cvsw-seg-btn').forEach((x) => { x.classList.toggle('is-on', x === b); x.setAttribute('aria-checked', String(x === b)); }); onPick(v); };
      g.append(b);
    }
    return g;
  }
  field(label, input) { return $('label', { cls: 'cvsw-rn-field' }, $('span', { text: label }), input); }
  text(key, attrs = {}) {
    const i = $('input', { type: attrs.type || 'text', attr: { spellcheck: 'false', autocomplete: 'off', ...attrs } }); i.value = String(this.st[key]);
    i.addEventListener('input', () => { this.st[key] = attrs.type === 'number' ? Math.max(0, Math.floor(Number(i.value) || 0)) : i.value; this.update(); });
    return i;
  }
  check(key, label) {
    const c = $('input', { type: 'checkbox', cls: 'cvsw-dt-check' }); c.checked = !!this.st[key];
    c.onchange = () => { this.st[key] = c.checked; this.update(); };
    return $('label', { cls: 'cvsw-rn-check' }, c, $('span', { text: label }));
  }
  onOpen() {
    this.modalEl.addClass('cvsw-bulk-rename');
    this.titleEl.setText(`Rename ${plural(this.items.length)}`);
    this.modeEl = this.seg([['replace', 'Replace'], ['add', 'Add'], ['number', 'Number'], ['case', 'Case']], this.st.mode, (v) => { this.st.mode = v; this.controls(); this.update(); }, 'Rename mode');
    this.modeEl.addClass('cvsw-rn-modes');
    this.ctl = $('div', { cls: 'cvsw-rn-controls' });
    this.hint = $('div', { cls: 'cvsw-rn-hint', attr: { role: 'alert' } });
    this.list = $('div', { cls: 'cvsw-rn-list', attr: { 'aria-label': 'Preview' } });
    this.count = $('span', { cls: 'cvsw-rn-count' });
    const cancel = $('button', { text: 'Cancel', attr: { type: 'button' } }); cancel.onclick = () => this.close();
    this.go = $('button', { text: 'Rename', cls: 'mod-cta', attr: { type: 'button' } }); this.go.onclick = () => void this.run();
    this.contentEl.append(this.modeEl, this.ctl, this.hint, this.list, $('div', { cls: 'cvsw-rn-foot' }, this.count, cancel, this.go));
    this.modalEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.isComposing && !e.target.closest('button')) { e.preventDefault(); void this.run(); } });
    this.controls(); this.update();
  }
  controls() {
    const st = this.st; const c = this.ctl; c.replaceChildren();
    if (st.mode === 'replace') {
      c.append($('div', { cls: 'cvsw-rn-grid' }, this.field('Find', this.text('find')), this.field('Replace with', this.text('repl'))),
        $('div', { cls: 'cvsw-rn-checks' }, this.check('matchCase', 'Match case'), this.check('regex', 'Use patterns')));
    } else if (st.mode === 'add') {
      c.append($('div', { cls: 'cvsw-rn-grid' }, this.field('Text', this.text('add')), this.field('Where', this.seg([['before', 'Before name'], ['after', 'After name']], st.addAt, (v) => { st.addAt = v; this.update(); }, 'Where'))));
    } else if (st.mode === 'number') {
      c.append($('div', { cls: 'cvsw-rn-grid' }, this.field('Name', this.text('name')), this.field('Start at', this.text('start', { type: 'number', min: '0', step: '1' })),
        this.field('Digits', this.seg([[1, '1'], [2, '01'], [3, '001']], st.digits, (v) => { st.digits = v; this.update(); }, 'Digits')),
        this.field('Number', this.seg([['after', 'After name'], ['before', 'Before name']], st.numAt, (v) => { st.numAt = v; this.update(); }, 'Number')),
        this.field('Separator', this.text('sep', { placeholder: 'None' }))));
    } else {
      c.append(this.seg([['title', 'Title Case'], ['sentence', 'Sentence case'], ['lower', 'lowercase'], ['upper', 'UPPERCASE']], st.caseMode, (v) => { st.caseMode = v; this.update(); }, 'Case'));
    }
    setTimeout(() => c.querySelector('input:not([type=checkbox])')?.focus(), 0);
  }
  /** Every item's new name and anything wrong with it. */
  plan() {
    const st = this.st; let err = ''; let re = null;
    if (st.mode === 'replace' && st.find) {
      try { re = new RegExp(st.regex ? st.find : escapeRe(st.find), `g${st.matchCase ? '' : 'i'}`); }
      catch (e) { err = `This pattern is not valid: ${String(e.message || e).replace(/^Invalid regular expression: /, '')}`; }
    }
    const rows = this.items.map((f, i) => {
      const dir = f instanceof VFolder; const base = dir ? f.name : f.basename;
      let nb = base;
      if (!err) {
        if (st.mode === 'replace') { if (re) nb = st.regex ? base.replace(re, st.repl) : base.replace(re, () => st.repl); }
        else if (st.mode === 'add') nb = st.addAt === 'before' ? `${st.add}${base}` : `${base}${st.add}`;
        else if (st.mode === 'number') { const num = String(st.start + i).padStart(st.digits, '0'); const nm = st.name.trim(); nb = !nm ? num : st.numAt === 'before' ? `${num}${st.sep}${nm}` : `${nm}${st.sep}${num}`; }
        else nb = changeCase(base, st.caseMode);
      }
      nb = nb.replace(/\s+/g, ' ').trim();
      return { f, base, nb, name: dir || !f.extension ? nb : `${nb}.${f.extension}`, changed: nb !== base, bad: '' };
    });
    const byPath = new Map(rows.map((r) => [r.f.path, r]));
    for (const r of rows) {
      if (!r.changed) continue;
      if (!r.nb) { r.bad = 'The name is empty'; continue; }
      if (BAD_NAME.test(r.nb)) { r.bad = 'A name cannot hold / \\ : * ? " < > |'; continue; }
      if (r.nb.startsWith('.')) { r.bad = 'A name cannot start with a dot'; continue; }
      const parent = vault.getFolderByPath(parentOf(r.f.path));
      const lo = r.name.toLowerCase();
      for (const c of parent?.children || []) {
        if (c === r.f) continue;
        const other = byPath.get(c.path);
        if ((other ? other.name : c.name).toLowerCase() !== lo) continue;
        r.bad = other?.changed ? 'Two items would have this name' : `There is already ${c instanceof VFolder ? 'a folder' : 'an item'} called "${c.name}" here`;
        break;
      }
    }
    const n = rows.filter((r) => r.changed).length;
    return { rows, err, n, ok: !err && n > 0 && !rows.some((r) => r.bad) };
  }
  update() {
    const p = this.plan();
    this.hint.setText(p.err); this.hint.toggleClass('is-on', !!p.err);
    this.list.replaceChildren(...p.rows.map((r) => $('div', { cls: `cvsw-rn-row${r.changed ? '' : ' is-same'}${r.bad ? ' is-bad' : ''}` },
      $('span', { cls: 'cvsw-rn-old', text: r.base }), icon('arrow-right', 'cvsw-rn-arrow'), $('span', { cls: 'cvsw-rn-new', text: r.changed ? r.nb || '(empty)' : r.base }),
      r.bad ? $('span', { cls: 'cvsw-rn-why', text: r.bad }) : null)));
    this.count.setText(`${p.n} will change`);
    this.go.disabled = !p.ok;
  }
  async run() {
    const p = this.plan(); if (!p.ok || this.running) return;
    this.running = true; this.close();
    // deepest first, so a folder renamed later never breaks the paths below it; a name another
    // picked item still holds waits for it (a swap parks one under a passing name)
    const depth = (r) => r.f.path.split('/').length;
    const pending = p.rows.filter((r) => r.changed).sort((a, b) => depth(b) - depth(a));
    const errors = []; const opts = { quiet: true, errors }; const done = [];
    const step = async (r, to) => { const from = r.f.path; const got = await relocate(r.f, to, opts); if (got) done.push([from, got]); return got; };
    let renamed = 0; let guard = 0;
    while (pending.length && guard++ < 2000) {
      let moved = false;
      for (const r of [...pending]) {
        const to = `${parentOf(r.f.path)}/${r.name}`; const lo = to.toLowerCase();
        if (pending.some((o) => o !== r && o.f.path.toLowerCase() === lo)) continue;
        if (r.f instanceof VFolder && pending.some((o) => o.f.path.startsWith(`${r.f.path}/`))) continue;
        pending.splice(pending.indexOf(r), 1); moved = true;
        if (await step(r, to)) renamed++;
      }
      if (!moved && pending.length) {
        const r = pending[0]; const ext = r.f instanceof VFile && r.f.extension ? `.${r.f.extension}` : '';
        if (!(await step(r, `${parentOf(r.f.path)}/${r.base} (renaming ${Date.now().toString(36)})${ext}`))) pending.shift();
      }
    }
    await settleMoves(opts.reopen);
    bulkToast(errors, p.n, 'renamed', renamed ? undoToast(`Renamed ${plural(renamed)}.`, done, 'renamed back', 'Renamed back.') : '');
  }
}

// ---- the library (a folder as video cards) ------------------------------------------------

const thumbs = new Map();
let thumbRun = 0;
const when = (t) => {
  if (!t) return '';
  const d = new Date(t); const now = new Date();
  if (d.toDateString() === now.toDateString()) return `Today ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
};
const fmtSize = (n) => (n > 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n > 1e6 ? `${Math.round(n / 1e6)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const sidecarOf = (vp) => (plugin?.settings ? plugin.sidecarFor(vp) : null); // null until the settings have loaded

function crumbs(folderVp, tail) {
  const nav = $('nav', { cls: 'cvsw-crumbs', attr: { 'aria-label': 'Folder' } });
  const parts = [[ROOT_NAME, VP]];
  let acc = VP;
  for (const seg of relOf(folderVp) ? relOf(folderVp).split('/') : []) { acc = `${acc}/${seg}`; parts.push([seg, acc]); }
  if (tail) parts.push([tail, null]);
  parts.forEach(([label, vp], i) => {
    if (i) nav.append(icon('chevron-right', 'cvsw-crumb-sep'));
    const last = i === parts.length - 1;
    const b = $('button', { cls: `cvsw-crumb${last ? ' is-current' : ''}`, text: label, attr: { type: 'button' } });
    if (!last && vp) b.onclick = () => go('f', vp);
    if (vp) wireDrop(b, vp);
    nav.append(b);
  });
  return nav;
}

/** The library's breadcrumbs: click the current folder's name to rename it. */
function folderCrumbs() {
  const nav = crumbs(currentFolder);
  const f = currentFolder !== VP && vault.getFolderByPath(currentFolder);
  const cur = nav.querySelector('.cvsw-crumb.is-current');
  if (f && cur) {
    cur.addClass('is-renamable');
    cur.title = 'Click to rename this folder';
    cur.onclick = () => editName(cur, f.name, (want) => renameItem(f, want), 'Folder name');
  }
  return nav;
}

function renderLibrary({ fade = false } = {}) {
  if (openPath) return;
  const lib = $('div', { cls: 'cvsw-lib' });
  const body = $('div', { cls: 'cvsw-lib-body' });
  const folder = vault.getFolderByPath(currentFolder) || vault.getFolderByPath(VP);
  const view = curView();
  const q = treeFilter.trim().toLowerCase();
  let files = []; let folders = [];
  const under = (keep) => { const out = []; const walk = (d) => { for (const c of d.children) { if (c instanceof VFolder) { if (!c.name.startsWith('.')) walk(c); } else if (isVideo(c) && keep(c)) out.push(c); } }; if (folder) walk(folder); return out; };
  if (q) files = under((c) => c.path.toLowerCase().includes(q));
  else { const kids = visibleKids(folder); files = kids.filter(isVideo); folders = kids.filter((c) => c instanceof VFolder).sort(sortKids); }
  files.sort((a, b) => (web.sort === 'name' ? a.name.localeCompare(b.name, undefined, { numeric: true }) : b.stat.mtime - a.stat.mtime));

  const sortBtn = $('button', { cls: 'cvsw-sort', attr: { type: 'button', title: 'Change the order of the videos' } }, icon('arrow-up-down'), $('span', { text: web.sort === 'name' ? 'Name A to Z' : 'Last modified' }));
  sortBtn.onclick = () => { web.sort = web.sort === 'name' ? 'modified' : 'name'; saveWeb(); renderLibrary(); };
  if (vault.loaded) {
    // Table and Gallery alike (Tony, 2026-10-02: the open canvas): the header line, the toolbar, then the table card or the
    // gallery's cards; both go through the view's search, filters and sorts. The table's search covers this folder and
    // every folder inside it, or every folder inside when the view says so.
    const lq = libQuery.trim().toLowerCase(); const tag = lq.replace(/^#/, '');
    let tf = files; let tfolders = [];
    if (lq) tf = under((c) => c.path.toLowerCase().includes(lq) || allTagsOf(c).some((t) => t.includes(tag)));
    else if (view.recursive && !q) tf = under(() => true);
    else if (!q) tfolders = folders;
    nameSub = !!lq; // searching: the folder under each name (with sub-folders included the Folder column shows instead)
    const waiting = q || lq ? null : pendingSection(currentFolder);
    renderLibTable(body, folder, tf, tfolders, lq, waiting);
    lib.append(body); wireDrop(lib, currentFolder); wireTableBand(body); showMain(lib, { fade }); settleTable(); document.title = APP_TITLE; renderBar(); void fillThumbs();
    return;
  }
  if (!vault.loaded) body.append(!navigator.onLine || !netOk ? empty('wifi-off', 'Offline', 'Your videos show up here as soon as the connection is back.') : empty('cloud', 'Loading your videos', 'One moment.'));
  else {
    if (folders.length) {
      body.append($('div', { cls: 'cvsw-section', text: 'Folders' }));
      const grid = $('div', { cls: 'cvsw-folders' });
      for (const f of folders) { const t = $('button', { cls: 'cvsw-folder', attr: { type: 'button' } }, icon('folder'), $('span', { cls: 'cvsw-folder-name', text: f.name }), $('span', { cls: 'cvsw-folder-count', text: String(videosUnder(f)) })); t.onclick = () => go('f', f.path); t.oncontextmenu = (e) => { e.preventDefault(); itemMenu(e, f); }; wireDrag(t, f); wireDrop(t, f.path); grid.append(t); }
      body.append(grid);
    }
    const waiting = q ? null : pendingSection(currentFolder);
    if (waiting) body.append(waiting);
    // the view tabs sit at the right of the Videos row (on their own when there are no videos, so the table is always one click away)
    body.append($('div', { cls: 'cvsw-section-row' }, $('div', { cls: 'cvsw-section', text: !files.length ? '' : q ? `Results for "${treeFilter.trim()}"` : 'Videos' }), $('div', { cls: 'cvsw-section-tools' }, files.length ? sortBtn : null, viewTabs())));
    if (files.length) {
      const grid = $('div', { cls: 'cvsw-grid' });
      for (const f of files) grid.append(card(f, !!q));
      body.append(grid);
    } else if (q) body.append(empty('search-x', 'No videos match', `Nothing here is called anything like "${treeFilter.trim()}".`));
    else if (!folders.length && !waiting) body.append(empty('film', currentFolder === VP ? 'No videos yet' : 'This folder is empty', 'Upload an mp4 or mov, or drop one here. Videos added on the Mac or any other device show up on their own.'));
  }
  lib.append(body);
  wireDrop(lib, currentFolder);
  showMain(lib, { fade });
  document.title = APP_TITLE;
  renderBar();
  void fillThumbs();
}
// ---- the main area's screens ------------------------------------------------------------------------
// A screen (the library, the player) goes in with showMain. Between the table and the player it is one
// short cross-fade (140 ms; Tony, 2026-10-02: the switch used to jump): the new screen fades in on top of
// the old one, which stays put underneath and is removed when the fade ends, so the main area is never
// empty and nothing around it moves. No fade with reduce motion, when the setting is off, or for a
// re-render of the same place. An old screen's `__onGone` runs when it leaves the page.
let mainFade = null;
const FADE_MS = 140;
function showMain(el, { fade = false } = {}) {
  if (mainFade) mainFade.finish();
  const olds = [...main.children];
  const gone = () => { for (const o of olds) { o.remove(); try { o.__onGone?.(); } catch (e) { console.error(e); } } };
  if (!fade || !olds.length || web.fade === false || document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches) { main.replaceChildren(el); gone(); return; }
  for (const o of olds) { o.classList.add('cvsw-leaving'); o.inert = true; }
  el.classList.add('cvsw-entering');
  main.append(el);
  const f = mainFade = { finish() { if (mainFade !== f) return; mainFade = null; clearTimeout(f.t); el.classList.remove('cvsw-entering'); gone(); } };
  f.t = setTimeout(() => f.finish(), FADE_MS + 20);
}
// ---- still uploading from the Mac -----------------------------------------------------------
// The library lists what the server has. Videos added to ~/CTH/videos on the Mac show
// here first as "uploading" cards (the bridge reports its queue: GET /api/fs/syncstatus),
// then turn into normal cards when their upload finishes. Polled every 10 s while any wait.
let sync = { pending: [], current: null, at: 0, now: 0 };
let syncTimer = 0; let syncKey = '';
async function loadSync() {
  clearTimeout(syncTimer);
  try { sync = await cloud.syncStatus(); } catch (_) { return; }
  const key = JSON.stringify([sync.pending.map((x) => x.path), sync.current, Math.floor((sync.now - sync.at) / 180000)]);
  if (key !== syncKey) { syncKey = key; const el = main.querySelector('.cvsw-pending'); if (!openPath && workspace.layoutReady) { if (el) el.replaceWith(pendingSection(currentFolder) || $('div')); else if (sync.pending.length) renderLibrary(); } }
  if (sync.pending.length && !document.hidden) syncTimer = setTimeout(() => void loadSync(), 10000);
}
document.addEventListener('visibilitychange', () => { if (!document.hidden && started && sync.pending.length) void loadSync(); });
function pendingSection(folderVp) {
  const here = toCloud(folderVp);
  if (here == null || !sync.pending.length) return null;
  const items = sync.pending.filter((x) => VIDEO_RE.test(x.path) && parentOf(x.path).replace(/^\/$/, '') === here && !vault.getFileByPath(vaultOf(x.path)));
  if (!items.length) return null;
  const stale = sync.now - sync.at > 180000; // no report for 3 minutes: the Mac is asleep or off
  const total = items.reduce((n, x) => n + x.size, 0);
  const sec = $('div', { cls: 'cvsw-pending' },
    $('div', { cls: 'cvsw-section', text: `Uploading from the Mac · ${items.length} video${items.length === 1 ? '' : 's'}, ${fmtSize(total)}${stale ? ' · waiting for the Mac (asleep or off)' : ''}` }));
  const grid = $('div', { cls: 'cvsw-grid' });
  for (const x of items) {
    const now = sync.current && sync.current.path.toLowerCase() === x.path.toLowerCase() ? sync.current : null;
    const pct = now ? now.pct : 0;
    const bar = $('div', { cls: 'cvsw-pending-bar' }); bar.style.setProperty('--pct', `${pct}%`);
    const c = $('div', { cls: 'cvsw-card is-pending', attr: { 'aria-label': `${baseName(x.path)}, uploading`, title: 'This video is still uploading from the Mac. It opens here when the upload finishes.' } },
      $('div', { cls: 'cvsw-card-pic' }, $('span', { cls: 'cvsw-pending-icon' }, icon(now ? 'upload' : 'clock')), now ? bar : null),
      $('div', { cls: 'cvsw-card-meta' }, $('div', { cls: 'cvsw-card-name', text: baseName(x.path).replace(/\.[^.]+$/, '') }),
        $('div', { cls: 'cvsw-card-sub', text: [fmtSize(x.size), now ? `Uploading ${pct}%` : stale ? 'Waiting for the Mac' : 'Waiting to upload'].join(' · ') })));
    grid.append(c);
  }
  sec.append(grid);
  return sec;
}
function empty(ic, title, desc) { return $('div', { cls: 'cvsw-empty' }, $('div', { cls: 'cvsw-empty-icon' }, icon(ic)), $('div', { cls: 'cvsw-empty-title', text: title }), $('div', { cls: 'cvsw-empty-desc', text: desc })); }

function card(f, showFolder) {
  const img = $('img', { cls: 'cvsw-thumb', attr: { alt: '', draggable: 'false' } });
  img.__key = `${f.path.toLowerCase()}@${f.rev}`; img.__path = toCloud(f.path);
  const ci = clipInfo.get(f.path); const hasClips = ci ? ci.clips > 0 : !!vault.getFileByPath(sidecarOf(f.path) || '');
  // the card's properties (the Gallery view's Card properties: its shown columns, the first three with a value)
  const v = curView(); const keys = v.layout === 'gallery' ? v.columns.order.filter((k) => v.columns.shown.includes(k) && !['name', 'status', 'tags'].includes(k)) : ['size', 'modified'];
  const txt = (k) => { const raw = libRaw(f, k); if (raw == null || raw === '' || (k === 'clips' && !raw)) return null; return k === 'length' ? fmtLen(raw) : k === 'size' ? fmtSize(raw) : k === 'modified' || k === 'created' ? when(raw) : k === 'clips' ? plural(raw, 'clip') : String(raw); };
  const sub = [showFolder ? baseName(parentOf(f.path)) : null, ...keys.map(txt).filter(Boolean).slice(0, 3)].filter(Boolean).join(' · ');
  const c = $('div', { cls: 'cvsw-card', attr: { tabindex: '0', role: 'button', 'aria-label': f.basename } },
    $('div', { cls: 'cvsw-card-pic' }, img, $('span', { cls: 'cvsw-play' }, icon('play')), hasClips ? $('span', { cls: 'cvsw-corner', text: 'Clips' }) : null),
    $('div', { cls: 'cvsw-card-meta' }, $('div', { cls: 'cvsw-card-name' }, cardDot(f), $('span', { text: f.basename })), $('div', { cls: 'cvsw-card-sub', text: sub })));
  c.dataset.vp = f.path;
  c.onclick = () => go('v', f.path);
  c.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go('v', f.path); } };
  c.oncontextmenu = (e) => { e.preventDefault(); itemMenu(e, f); };
  wireDrag(c, f);
  wireHoverScrub(c.querySelector('.cvsw-card-pic'), f);
  return c;
}
// Hover scrub on gallery cards (Tony, 2026-10-02): the pointer's place across the thumbnail is the place in the video,
// and a two-finger horizontal swipe scrubs too (the player's curve). Frames come from the player's own WebCodecs
// decoder (openScrubSource, frames cached), drawn on a canvas over the poster; a thin line along the bottom and the time
// in a chip show where; leaving puts the poster back and closes the decoder. Only the hovered card decodes, and nothing
// starts before a hover. Space while hovering plays or pauses it inline (muted). A click still opens the player.
let hoverScrub = null;
function wireHoverScrub(pic, f) {
  if (!pic) return;
  pic.addEventListener('pointerenter', (e) => { if (e.pointerType === 'touch') return; startHoverScrub(pic, f); hoverScrub?.at(e.clientX); });
  pic.addEventListener('pointermove', (e) => { if (hoverScrub?.pic === pic) hoverScrub.at(e.clientX); });
  pic.addEventListener('pointerleave', () => { if (hoverScrub?.pic === pic) hoverScrub.stop(); });
  pic.addEventListener('wheel', (e) => { if (hoverScrub?.pic === pic) hoverScrub.wheel(e); }, { passive: false });
}
function startHoverScrub(pic, f) {
  hoverScrub?.stop();
  const canvas = $('canvas', { cls: 'cvsw-card-scrub', attr: { 'aria-hidden': 'true' } }); const line = $('div', { cls: 'cvsw-card-line' }); const chip = $('span', { cls: 'cvsw-card-time' });
  const id = `hover:${f.path}`; let src = null; let dead = false; let t = 0; let raf = 0; let shown = false; let vid = null; let aim = null; let last = 0; let idle = 0; let primedFor = null;
  const dur = () => src?.duration || metaOf(f)?.dur || 0;
  const draw = () => {
    raf = 0; if (dead || !src) return;
    const r = src.request(t, 0, 0);
    if (primedFor !== t) { primedFor = t; src.prime?.(t); } // a new place: start decoding there (the player primes the same way)
    if (r && r.c) {
      const W = pic.clientWidth; const H = pic.clientHeight; if (canvas.width !== W * devicePixelRatio) { canvas.width = W * devicePixelRatio; canvas.height = H * devicePixelRatio; }
      const ctx = canvas.getContext('2d'); const k = Math.max(canvas.width / r.c.width, canvas.height / r.c.height); const w = r.c.width * k; const h = r.c.height * k;
      ctx.drawImage(r.c, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h); canvas.dataset.t = String(Math.round(r.t * 1000)); if (Math.abs(r.t - t) > Math.max(0.1, (src.frameSec || 0.04) * 2)) raf = requestAnimationFrame(draw); // a nearby frame for now: ask again until the exact one is decoded
      if (!shown) { shown = true; pic.addClass('is-scrubbing'); }
    } else raf = requestAnimationFrame(draw); // not decoded yet: ask again next frame
  };
  const paint = () => { const d = dur(); line.style.width = `${d ? (t / d) * 100 : 0}%`; chip.setText(fmtLen(t)); pic.dataset.scrubT = String(Math.round(t * 1000)); if (!raf) raf = requestAnimationFrame(draw); };
  pic.append(canvas, line, chip);
  const hs = hoverScrub = {
    pic,
    at(x) { if (vid) return; const r = pic.getBoundingClientRect(); const d = dur(); if (!d) return; t = Math.max(0, Math.min(d - 0.001, ((x - r.left) / r.width) * d)); paint(); },
    wheel(e) {
      const s = plugin?.settings || {}; const ax = Math.abs(e.deltaX); const ay = Math.abs(e.deltaY);
      const delta = ax > ay ? e.deltaX : 0; if (!delta || !dur() || vid) return;
      e.preventDefault(); e.stopPropagation();
      const now = performance.now(); const dt = last ? now - last : 8; last = now;
      const step = HOST.internals?.deltaSeconds ? HOST.internals.deltaSeconds(delta, dt, s.sensitivity || 1, false) : delta / 100;
      aim = Math.max(0, Math.min(dur() - 0.001, (aim == null ? t : aim) + (s.reverse ? 1 : -1) * step)); t = aim; paint();
      clearTimeout(idle); idle = setTimeout(() => { aim = null; last = 0; }, 260);
    },
    toggle() {
      if (vid) { if (vid.paused) vid.play().catch(() => {}); else vid.pause(); return; }
      vid = $('video', { cls: 'cvsw-card-inline', attr: { playsinline: '', muted: '' } }); vid.muted = true; pic.append(vid); pic.addClass('is-playing');
      cloud.tempLink(toCloud(f.path)).then((url) => { if (dead) return; vid.src = url; vid.currentTime = t; vid.play().catch(() => {}); }).catch(() => {});
      vid.ontimeupdate = () => { t = vid.currentTime; line.style.width = `${dur() ? (t / dur()) * 100 : 0}%`; chip.setText(fmtLen(t)); };
    },
    stop() {
      dead = true; cancelAnimationFrame(raf); clearTimeout(idle);
      if (vid) { try { vid.pause(); } catch (_) { /* fine */ } vid.removeAttribute('src'); vid.remove(); }
      canvas.remove(); line.remove(); chip.remove(); pic.removeClass('is-scrubbing', 'is-playing'); delete pic.dataset.scrubT;
      if (src) HOST.internals?.releaseScrubSource?.(id);
      if (hoverScrub === hs) hoverScrub = null;
    },
  };
  const provider = HOST.provider?.(absOf(f.path));
  if (provider && HOST.internals?.openScrubSource) void HOST.internals.openScrubSource(id, provider, HOST.internals.cacheBytesFor ? HOST.internals.cacheBytesFor(plugin?.settings || {}) : 256 * 1024 * 1024) /* the player's own frame budget: a small one evicts the frame before it is shown */.then((r) => { if (dead) { if (r.src) HOST.internals.releaseScrubSource(id); return; } src = r.src; if (src) paint(); }).catch(() => {});
}
addEventListener('keydown', (e) => {
  if (!hoverScrub || e.key !== ' ' || e.metaKey || e.ctrlKey || e.altKey || e.target?.closest?.('input, textarea, [contenteditable="true"]') || isModalOpen?.()) return;
  e.preventDefault(); hoverScrub.toggle();
}, true);
// ---- the folder table (Tony, 2026-10-02; one spec with CTH Docs) -----------------------------------
// Each folder shows as a table database (shadcn/studio datatable-example-06, columns like
// datatable-columns-02 and 03; ~/CTH/design/shadcn/blocks), or as the gallery of cards. What a folder
// shows is a VIEW (a tab in the card's header): its layout, columns (order, shown, widths, wrap), the
// frozen column, sorts, filters and their buttons, rows per page, sub-folders, footer calculations and
// grouping. Views are per folder, saved with the shared settings (settings.tableViews in
// videos/.cth-video.json, so they sync) and follow the folder when it is renamed or moved; a folder
// with none of its own uses the default set. The last view used per folder is kept in this browser.
const LIB_COLS = [
  { key: 'name', label: 'Name', icon: 'film', w: 340, fixed: true, type: 'text' },
  { key: 'status', label: 'Status', icon: 'circle-dot', w: 120, type: 'status', auto: true },
  { key: 'clips', label: 'Clips', icon: 'scissors', w: 84, type: 'number', unit: 'clips' },
  { key: 'tags', label: 'Tags', icon: 'tag', w: 240, type: 'tags' },
  { key: 'length', label: 'Length', icon: 'clock', w: 96, type: 'length', unit: 'minutes' },
  { key: 'size', label: 'Size', icon: 'hard-drive', w: 92, type: 'size', unit: 'MB' },
  { key: 'modified', label: 'Modified', icon: 'calendar', w: 140, type: 'date' },
  { key: 'created', label: 'Created', icon: 'calendar-plus', w: 140, type: 'date' },
  { key: 'resolution', label: 'Resolution', icon: 'scaling', w: 120, type: 'select' },
  { key: 'type', label: 'Type', icon: 'file', w: 76, type: 'select' },
  { key: 'folder', label: 'Folder', icon: 'folder', w: 160, type: 'select' },
];
const colOf = (k) => LIB_COLS.find((c) => c.key === k);
// Simple: Name, the key columns, Modified. Details: everything but Folder. All (the same three in CTH Docs).
const LIB_PRESETS = { Simple: ['name', 'clips', 'tags', 'modified'], Details: ['name', 'clips', 'tags', 'length', 'size', 'modified', 'created', 'resolution', 'type'], All: LIB_COLS.map((c) => c.key) };
const CHECK_COL_W = 40;
// what this browser chose before views existed (cthvw.lib.v1): it seeds the built-in Table view, and its
// `view` still says which layout a folder opens on the first time (Notion embeds: the gallery)
const LIB_KEY = 'cthvw.lib.v1';
let libSt = null;
const libState = () => {
  if (libSt) return libSt;
  const d = { view: cloud.embedded ? 'gallery' : 'table', order: LIB_COLS.map((c) => c.key), shown: ['name', 'clips', 'tags', 'length', 'size', 'modified'], widths: {}, sort: { key: 'modified', desc: true }, per: 0 };
  let saved = {}; try { saved = JSON.parse(localStorage.getItem(LIB_KEY) || '{}') || {}; } catch (_) { /* fine */ }
  libSt = { ...d, ...saved };
  for (const c of LIB_COLS) if (!libSt.order.includes(c.key)) libSt.order.push(c.key);
  return libSt;
};
const saveLib = () => { try { localStorage.setItem(LIB_KEY, JSON.stringify(libSt)); } catch (_) { /* private window */ } };

// ---- views ----------------------------------------------------------------------------------------
const clone = (x) => JSON.parse(JSON.stringify(x));
const newId = () => Math.random().toString(36).slice(2, 9);
function baseView(layout) {
  const l = libState();
  return { id: layout, name: layout === 'gallery' ? 'Gallery' : 'Table', layout, columns: { order: [...l.order], shown: [...l.shown], widths: { ...l.widths }, wrap: [] }, frozen: null, sorts: [{ key: l.sort?.key || 'modified', desc: l.sort?.desc !== false }], filters: {}, filterButtons: ['tags', 'clips'], rowsPerPage: l.per && l.per !== 50 ? l.per : 0, recursive: false, calcs: {}, groupBy: null };
}
/** Every folder starts with two views, "Table" and "Gallery". */
const builtinViews = () => [baseView('table'), baseView('gallery')];
function normView(v) {
  const layout = v && v.layout === 'gallery' ? 'gallery' : 'table';
  const d = baseView(layout); const c = (v && v.columns) || {};
  const order = [...new Set([...(Array.isArray(c.order) ? c.order : []), ...LIB_COLS.map((x) => x.key)])].filter(colOf);
  const arr = (x, fb) => (Array.isArray(x) ? x : fb); const obj = (x) => (x && typeof x === 'object' && !Array.isArray(x) ? x : {});
  return { ...d, ...v, id: String(v?.id || d.id), name: String(v?.name || d.name), layout,
    columns: { order, shown: arr(c.shown, d.columns.shown).filter(colOf), widths: obj(c.widths), wrap: arr(c.wrap, []).filter(colOf), statusOff: !!c.statusOff, labels: Object.fromEntries(Object.entries(obj(c.labels)).filter(([k, x]) => colOf(k) && typeof x === 'string' && x.trim())) },
    frozen: colOf(v?.frozen) ? v.frozen : null, sorts: arr(v?.sorts, d.sorts).filter((s) => s && colOf(s.key)), filters: obj(v?.filters),
    filterButtons: arr(v?.filterButtons, d.filterButtons).filter(colOf), rowsPerPage: Number(v?.rowsPerPage) > 0 ? Number(v.rowsPerPage) : 0, recursive: !!v?.recursive, calcs: obj(v?.calcs), groupBy: colOf(v?.groupBy) ? v.groupBy : null };
}
let tempViews = { defaults: null, folders: {} }; // until the shared settings have loaded
function viewStore() {
  const s = plugin?.settings; if (!s) return tempViews;
  if (!s.tableViews || typeof s.tableViews !== 'object' || Array.isArray(s.tableViews)) s.tableViews = { defaults: null, folders: {} };
  if (!s.tableViews.folders || typeof s.tableViews.folders !== 'object') s.tableViews.folders = {};
  return s.tableViews;
}
let viewSaveTimer = 0;
function saveViews() { clearTimeout(viewSaveTimer); viewSaveTimer = setTimeout(() => { if (plugin?.settings) void plugin.saveData({ settings: plugin.settings, positions: plugin.positions }); }, 300); }
const viewKey = (folderVp) => relOf(folderVp); // '' is the top folder
/** The folder's views: its own, else the default set, else the built-in two. */
function folderViews(folderVp) {
  const st = viewStore(); const own = st.folders[viewKey(folderVp)];
  const list = Array.isArray(own) && own.length ? own : Array.isArray(st.defaults) && st.defaults.length ? st.defaults : builtinViews();
  return list.map(normView);
}
const hasOwnViews = (folderVp) => { const o = viewStore().folders[viewKey(folderVp)]; return Array.isArray(o) && o.length > 0; };
/** The folder's own set, made from what it shows now the first time something changes. */
function ownViews(folderVp) {
  const st = viewStore(); const k = viewKey(folderVp);
  st.folders[k] = hasOwnViews(folderVp) ? st.folders[k].map(normView) : clone(folderViews(folderVp));
  return st.folders[k];
}
const LASTVIEW_KEY = 'cthvw.lastview.v1';
const lastView = (() => { try { return JSON.parse(localStorage.getItem(LASTVIEW_KEY) || '{}') || {}; } catch (_) { return {}; } })();
const saveLastView = () => { try { localStorage.setItem(LASTVIEW_KEY, JSON.stringify(lastView)); } catch (_) { /* private window */ } };
/** The view a folder shows: the last one used here, else the first of the layout this browser prefers. */
function curView(folderVp = currentFolder) {
  const views = folderViews(folderVp);
  return views.find((v) => v.id === lastView[viewKey(folderVp)]) || views.find((v) => v.layout === libState().view) || views[0];
}
function pickView(id) { lastView[viewKey(currentFolder)] = id; saveLastView(); libPage = 0; libChecked.clear(); cellFocus = null; closePop(); renderLibrary(); }
/** Change the current view (fn gets the stored view and its set); it saves by itself. */
function editView(fn, { render = true } = {}) {
  const id = curView().id; const views = ownViews(currentFolder);
  const v = views.find((x) => x.id === id) || views[0];
  fn(v, views); saveViews();
  if (render) renderLibrary();
}
/** A folder was renamed or moved: its views, last view and scroll place (and those of the folders inside) go with it. */
function rekeyViews(oldVp, newVp) {
  const a = relOf(oldVp); const b = relOf(newVp); const st = viewStore(); let hit = false;
  const move = (map) => { for (const k of Object.keys(map)) if (k === a || k.startsWith(`${a}/`)) { map[b + k.slice(a.length)] = map[k]; delete map[k]; hit = true; } };
  move(st.folders); move(statusMap()); if (hit) saveViews();
  move(lastView); saveLastView();
  for (const k of [...scrollMem.keys()]) if (k === oldVp || k.startsWith(`${oldVp}/`)) { scrollMem.set(newVp + k.slice(oldVp.length), scrollMem.get(k)); scrollMem.delete(k); }
}

// ---- popovers (filters, Columns, cell editors): one at a time, anchored to a live element ------------
// A popover lives on <body> and survives the table re-rendering under it: its anchor is found again
// (anchorSel) and it follows the anchor on scroll, so nothing ever closes because the page moved.
let popOpen = null; let popShut = { key: '', at: 0 };
function closePop(p = popOpen, { byAnchor = false } = {}) {
  if (!p || popOpen !== p) return;
  popOpen = null; if (byAnchor) popShut = { key: p.key, at: Date.now() }; // its own button closed it: the click that follows must not reopen it
  removeEventListener('pointerdown', p.away, true); removeEventListener('keydown', p.esc, true); removeEventListener('resize', p.place); removeEventListener('scroll', p.place, true);
  p.el.remove(); p.onClose?.();
}
/** Clicking a popover's own button while it is open only closes it. */
const popJustShut = (key) => popShut.key === key && Date.now() - popShut.at < 250;
function openPop(anchor, { key = '', cls = '', align = 'start', anchorSel = null, onClose = null, label = '' } = {}) {
  closePop();
  const el = $('div', { cls: `cvsw-pop ${cls}`.trim(), attr: { role: 'dialog', 'aria-label': label || null } });
  document.body.append(el);
  const p = { el, key, anchor, onClose };
  p.place = () => {
    if (!p.anchor?.isConnected && anchorSel) p.anchor = document.querySelector(anchorSel) || p.anchor;
    if (!p.anchor?.isConnected) return;
    const r = p.anchor.getBoundingClientRect(); const b = el.getBoundingClientRect();
    let y = r.bottom + 4; if (y + b.height > innerHeight - 8 && r.top - b.height - 4 >= 8) y = r.top - b.height - 4;
    const x = align === 'end' ? r.right - b.width : r.left;
    el.style.left = `${Math.round(Math.max(8, Math.min(x, innerWidth - b.width - 8)))}px`; el.style.top = `${Math.round(Math.max(8, Math.min(y, innerHeight - b.height - 8)))}px`;
  };
  p.away = (e) => { if (el.contains(e.target) || e.target.closest?.('.menu, .modal-container, .cvs-tooltip')) return; closePop(p, { byAnchor: !!p.anchor?.contains(e.target) }); };
  p.esc = (e) => { if (e.key !== 'Escape' || document.querySelector('.cvsw-fallback-menu, .modal-container')) return; e.preventDefault(); e.stopPropagation(); closePop(p); };
  el.addEventListener('keydown', (e) => { if (e.key !== 'Escape') e.stopPropagation(); }); // typing in a popover is never a table or player key
  popOpen = p;
  setTimeout(() => { if (popOpen !== p) return; addEventListener('pointerdown', p.away, true); addEventListener('keydown', p.esc, true); addEventListener('resize', p.place); addEventListener('scroll', p.place, true); }, 0);
  return p;
}

// ---- what a row holds -------------------------------------------------------------------------------
let libQuery = ''; let libPage = 0; const libChecked = new Set(); let checkAnchor = null;
let tableHot = false;        // the last click was in the table, so its keys belong to it
let cellFocus = null;        // { vp, col }: the cell the arrow keys are on
const groupShut = new Set(); // collapsed groups: "<folder>|<view id>|<group>"
const scrollMem = new Map(); // folder -> { top, x, y }: where its table was scrolled to
// what a video's clips note says (clip count, the clips' tags, the video's own tags), read once per note revision
const clipInfo = new Map();
const NO_INFO = { clips: 0, tags: [], ftags: [], status: '', rev: '' };
/** The video's own tags: the `tags:` list in its clips note's frontmatter (words, no #). */
function noteFileTags(text) {
  const m = /^---\r?\n([\s\S]*?)\r?\n---/.exec(text || ''); if (!m) return [];
  try { const y = YAML.parse(m[1]) || {}; const t = y.tags; const list = Array.isArray(t) ? t : typeof t === 'string' ? t.split(/[\s,]+/) : []; return [...new Set(list.map((x) => String(x || '').replace(/^#+/, '').trim().toLowerCase()).filter(Boolean))]; } catch (_) { return []; }
}
async function loadClipInfo(f) {
  const np = sidecarOf(f.path); const nf = np && vault.getFileByPath(np);
  if (!nf) { clipInfo.set(f.path, { ...NO_INFO }); return clipInfo.get(f.path); }
  const have = clipInfo.get(f.path); if (have && have.rev === nf.rev) return have;
  try {
    const text = await vault.read(nf);
    const lines = text.split('\n').filter((l) => /^\s*-\s*\d{1,2}:\d{2}:\d{2}/.test(l));
    const tags = new Set();
    for (const l of lines) { const m = /\|\s*tags\s+(#[^|]*)/.exec(l); if (m) for (const t of m[1].split(/\s+/)) { const w = t.replace(/^#+/, '').trim(); if (w) tags.add(w); } }
    const info = { clips: lines.length, tags: [...tags].sort(), ftags: noteFileTags(text), status: noteStatus(text), rev: nf.rev };
    clipInfo.set(f.path, info); return info;
  } catch (_) { return { ...NO_INFO }; }
}
const metaOf = (f) => thumbStore[`${f.path.toLowerCase()}@${f.rev}`]?.meta || thumbs.get(`meta:${f.path.toLowerCase()}@${f.rev}`) || null;
const fmtLen = (s) => { if (!s && s !== 0) return ''; const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const x = Math.floor(s % 60); return h ? `${h}:${String(m).padStart(2, '0')}:${String(x).padStart(2, '0')}` : `${m}:${String(x).padStart(2, '0')}`; };
/** Every tag on a video: its own and its clips' (the Tags filter and the search match both). */
const allTagsOf = (f) => { const ci = clipInfo.get(f.path); return ci ? [...new Set([...(ci.ftags || []), ...ci.tags])] : []; };
/** The value behind a cell: a number, a string, a list (tags) or null when there is none. */
function libRaw(f, key) {
  const ci = clipInfo.get(f.path); const mt = metaOf(f);
  switch (key) {
    case 'name': return f.basename;
    case 'status': return videoStatus(f).name;
    case 'clips': return ci ? ci.clips : null;
    case 'tags': return ci ? ci.ftags || [] : [];
    case 'length': return mt && mt.dur ? mt.dur : null;
    case 'size': return f.stat.size;
    case 'modified': return f.stat.mtime || null;
    case 'created': return f.stat.ctime || null;
    case 'resolution': return mt && mt.w ? `${mt.w} x ${mt.h}` : '';
    case 'type': return (f.extension || '').toUpperCase();
    case 'folder': return relOf(parentOf(f.path)) || ROOT_NAME;
    default: return null;
  }
}
const rawEmpty = (key, v) => v == null || v === '' || (Array.isArray(v) && !v.length) || (key === 'clips' && v === 0);
function libValue(f, key) { // what a column sorts by
  const v = libRaw(f, key);
  if (key === 'tags') return v.join(' ');
  if (key === 'status') return statusOrder(f, statusSetFor(parentOf(f.path))); // the workflow order, not the name
  if (key === 'resolution') { const mt = metaOf(f); return mt ? mt.w * mt.h : -1; }
  if (typeof v === 'string') return v.toLowerCase();
  return v == null ? -1 : v;
}
function libCell(f, key) {
  const ci = clipInfo.get(f.path); const mt = metaOf(f);
  const muted = (t) => $('span', { cls: 'cvsw-td-muted cvsw-td-text', text: t });
  const none = () => $('span', { cls: 'cvsw-td-muted cvsw-td-none', text: '-' });
  switch (key) {
    case 'name': {
      const img = $('img', { cls: 'cvsw-thumb cvsw-td-thumb', attr: { alt: '', draggable: 'false', decoding: 'sync' } });
      img.__key = `${f.path.toLowerCase()}@${f.rev}`; img.__path = toCloud(f.path);
      const hit = thumbs.get(img.__key) || thumbStore[img.__key]?.url; if (hit) img.src = hit; // already known: there on the first frame
      const name = $('span', { cls: 'cvsw-td-name', text: f.basename, attr: { title: 'Double-click to rename' } });
      name.ondblclick = (e) => { e.stopPropagation(); editName(name, f.basename, (want) => renameItem(f, want), 'Video name'); };
      const sub = nameSub ? muted(relOf(parentOf(f.path)) || ROOT_NAME) : null;
      // Notion's OPEN pill (Tony, 2026-10-02): on row hover or keyboard focus, over the end of the name; opens the player
      const open = $('button', { cls: 'cvsw-td-open', attr: { type: 'button', 'aria-label': 'Open', title: 'Open (Enter)' } }, icon('panel-right'), $('span', { text: 'OPEN' }));
      open.onclick = (e) => { e.stopPropagation(); go('v', f.path); };
      return $('div', { cls: 'cvsw-td-namecell' }, $('span', { cls: 'cvsw-td-pic', attr: { 'data-vp': f.path } }, img), $('div', { cls: 'cvsw-td-namewrap' }, name, sub), open);
    }
    case 'status': { const v = videoStatus(f); const b = statusDotButton(f, { cls: 'cvsw-td-dot' }); return $('span', { cls: 'cvsw-td-status cvsw-td-text' }, b, $('span', { text: v.name, attr: v.unknown ? { title: `Saved as “${v.raw}”, which is not one of this folder's statuses` } : {} })); }
    case 'clips': return ci ? (ci.clips ? $('span', { cls: 'cvsw-td-text cvsw-td-num', text: String(ci.clips) }) : none()) : muted('');
    case 'tags': return ci ? ((ci.ftags || []).length ? $('div', { cls: 'cvsw-td-tags' }, ...ci.ftags.map((t) => $('span', { cls: 'cvsw-tag', text: `#${t}` }))) : none()) : muted('');
    case 'length': return mt ? $('span', { cls: 'cvsw-td-text cvsw-td-num', text: fmtLen(mt.dur) }) : muted('');
    case 'size': return $('span', { cls: 'cvsw-td-text cvsw-td-num', text: fmtSize(f.stat.size) });
    case 'modified': return muted(when(f.stat.mtime));
    case 'created': return muted(when(f.stat.ctime));
    case 'resolution': return mt && mt.w ? muted(`${mt.w} x ${mt.h}`) : muted('');
    case 'type': return $('span', { cls: 'cvsw-tag is-plain', text: (f.extension || '').toUpperCase() });
    case 'folder': return muted(relOf(parentOf(f.path)) || ROOT_NAME);
    default: return '';
  }
}

// ---- filters ------------------------------------------------------------------------------------------
// A filter per column, by the column's type: tags and select { mode: any | all | none, values },
// text { contains }, number / length / size { min, max } (clips, minutes, MB), date { preset | from, to },
// and for every type { empty: 'empty' | 'not' }.
const DAY = 86400000;
const filterCount = (flt) => (!flt ? 0 : (flt.values?.length || 0) + (flt.contains ? 1 : 0) + (flt.min != null && flt.min !== '' ? 1 : 0) + (flt.max != null && flt.max !== '' ? 1 : 0) + (flt.preset ? 1 : 0) + (flt.from ? 1 : 0) + (flt.to ? 1 : 0) + (flt.empty ? 1 : 0));
const numOf = (c, raw) => (raw == null ? null : c.type === 'length' ? raw / 60 : c.type === 'size' ? raw / 1e6 : raw);
/** "1:30" is a minute and a half; otherwise a plain number in the column's unit. */
const parseAmount = (c, s) => { const t = String(s ?? '').trim(); if (!t) return null; if (c.type === 'length' && t.includes(':')) { const p = t.split(':').map(Number); if (p.some((x) => !Number.isFinite(x))) return null; return p.reduce((a, x) => a * 60 + x, 0) / 60; } const n = Number(t); return Number.isFinite(n) ? n : null; };
function passes(f, v) {
  for (const [key, flt] of Object.entries(v.filters)) {
    const c = colOf(key); if (!c || !filterCount(flt)) continue;
    const raw = key === 'tags' ? allTagsOf(f) : libRaw(f, key);
    if (flt.empty === 'empty' && !rawEmpty(key, raw)) return false;
    if (flt.empty === 'not' && rawEmpty(key, raw)) return false;
    if (c.type === 'tags' || c.type === 'select' || c.type === 'status') {
      const want = flt.values || []; if (!want.length) continue;
      const have = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const any = want.some((x) => have.includes(x));
      if (flt.mode === 'none' ? any : flt.mode === 'all' ? !want.every((x) => have.includes(x)) : !any) return false;
    } else if (c.type === 'text') {
      if (flt.contains && !String(raw || '').toLowerCase().includes(String(flt.contains).toLowerCase())) return false;
    } else if (c.type === 'date') {
      const t = raw || 0; const now = new Date(); const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      if (flt.preset === 'today' && t < sod) return false;
      if (flt.preset === '7d' && t < sod - 6 * DAY) return false;
      if (flt.preset === '30d' && t < sod - 29 * DAY) return false;
      if (flt.preset === 'year' && t < new Date(now.getFullYear(), 0, 1).getTime()) return false;
      if (flt.from && t < new Date(`${flt.from}T00:00:00`).getTime()) return false;
      if (flt.to && t >= new Date(`${flt.to}T00:00:00`).getTime() + DAY) return false;
    } else {
      const n = numOf(c, raw); const lo = parseAmount(c, flt.min); const hi = parseAmount(c, flt.max);
      if ((lo != null || hi != null) && n == null) return false;
      if (lo != null && n < lo) return false;
      if (hi != null && n > hi) return false;
    }
  }
  return true;
}
/** The filter popover for a column, fitted to its type. `files` are the rows before filtering (for the choices). */
function openFilter(key, anchor) {
  if (popJustShut(`f:${key}`)) return;
  const c = colOf(key);
  const p = openPop(anchor, { key: `f:${key}`, cls: 'cvsw-fpop', anchorSel: `.cvsw-dt-fbtn[data-fkey="${key}"]`, label: `Filter by ${c.label}` });
  const get = () => curView().filters[key] || {};
  const set = (patch) => { editView((v) => { const next = { ...(v.filters[key] || {}), ...patch }; for (const k of Object.keys(next)) if (next[k] == null || next[k] === '' || (Array.isArray(next[k]) && !next[k].length)) delete next[k]; if (filterCount(next)) v.filters[key] = next; else delete v.filters[key]; }); libPage = 0; draw(); };
  let q = '';
  const draw = () => {
    const flt = get(); const keepFocus = p.el.contains(document.activeElement) ? document.activeElement.dataset.f : null; const caret = document.activeElement?.selectionStart;
    const body = $('div', { cls: 'cvsw-fpop-body' });
    const field = (name, type, ph, val, on, label) => { const i = $('input', { cls: 'cvsw-fpop-input', type, attr: { placeholder: ph || null, 'data-f': name, 'aria-label': label || ph, spellcheck: 'false', autocomplete: 'off' } }); i.value = val ?? ''; let t = 0; i.oninput = () => { clearTimeout(t); t = setTimeout(() => on(i.value), 160); }; return i; };
    if (c.type === 'tags' || c.type === 'select' || c.type === 'status') {
      const modes = c.type === 'tags' ? [['any', 'Has any'], ['all', 'Has all'], ['none', 'Has none']] : [['any', 'Is any of'], ['none', 'Is none of']];
      const seg = $('div', { cls: 'cvsw-seg cvsw-fpop-modes', attr: { role: 'radiogroup', 'aria-label': 'Match' } });
      for (const [m, t] of modes) { const b = $('button', { cls: `cvsw-seg-btn${(flt.mode || 'any') === m ? ' is-on' : ''}`, attr: { type: 'button', role: 'radio', 'aria-checked': String((flt.mode || 'any') === m) } }, $('span', { text: t })); b.onclick = () => set({ mode: m === 'any' ? null : m }); seg.append(b); }
      const counts = new Map();
      if (c.type === 'status') for (const s of statusSetFor(currentFolder).statuses) counts.set(s.name, 0); // the workflow order first
      for (const f of tableFiles) { const raw = key === 'tags' ? allTagsOf(f) : libRaw(f, key); for (const x of Array.isArray(raw) ? raw : raw ? [raw] : []) counts.set(x, (counts.get(x) || 0) + 1); }
      for (const x of flt.values || []) if (!counts.has(x)) counts.set(x, 0);
      const search = field('q', 'search', `Search ${c.label.toLowerCase()}`, q, () => {}); search.oninput = () => { q = search.value; paintList(); };
      const list = $('div', { cls: 'cvsw-fpop-list', attr: { role: 'listbox', 'aria-multiselectable': 'true' } });
      const paintList = () => {
        const cur = get().values || []; const ql = q.trim().toLowerCase().replace(/^#/, '');
        const items = [...counts].filter(([x]) => !ql || String(x).toLowerCase().includes(ql)); if (c.type !== 'status') items.sort((a, b) => String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }));
        list.replaceChildren(...items.map(([x, n]) => { const on = cur.includes(x); const b = $('button', { cls: `cvsw-fpop-opt${on ? ' is-on' : ''}`, attr: { type: 'button', role: 'option', 'aria-selected': String(on) } }, $('span', { cls: 'cvsw-fpop-box' }, on ? icon('check') : null), $('span', { cls: 'cvsw-fpop-optname', text: c.type === 'tags' ? `#${x}` : String(x) }), $('span', { cls: 'cvsw-fpop-n', text: String(n) })); b.onclick = () => set({ values: on ? cur.filter((y) => y !== x) : [...cur, x] }); return b; }));
        if (!items.length) list.append($('div', { cls: 'cvsw-fpop-none', text: counts.size ? 'Nothing matches.' : c.type === 'tags' ? 'No tags here yet.' : 'Nothing to choose from yet.' }));
      };
      paintList();
      body.append(seg, $('label', { cls: 'cvsw-fpop-search' }, icon('search'), search), list);
    } else if (c.type === 'text') {
      body.append($('label', { cls: 'cvsw-fpop-row' }, $('span', { text: 'Contains' }), field('contains', 'text', 'Text', flt.contains, (x) => set({ contains: x.trim() }), 'Contains')));
    } else if (c.type === 'date') {
      const chips = $('div', { cls: 'cvsw-fpop-chips' });
      for (const [v, t] of [['today', 'Today'], ['7d', 'Past 7 days'], ['30d', 'Past 30 days'], ['year', 'This year']]) { const b = $('button', { cls: `cvsw-fpop-chip${flt.preset === v ? ' is-on' : ''}`, text: t, attr: { type: 'button', 'aria-pressed': String(flt.preset === v) } }); b.onclick = () => set({ preset: flt.preset === v ? null : v, from: null, to: null }); chips.append(b); }
      body.append(chips, $('div', { cls: 'cvsw-fpop-range' }, $('label', { cls: 'cvsw-fpop-row' }, $('span', { text: 'From' }), field('from', 'date', '', flt.from, (x) => set({ from: x, preset: null }), 'From')), $('label', { cls: 'cvsw-fpop-row' }, $('span', { text: 'To' }), field('to', 'date', '', flt.to, (x) => set({ to: x, preset: null }), 'To'))));
    } else {
      const ph = c.type === 'length' ? 'min or m:ss' : c.unit;
      body.append($('div', { cls: 'cvsw-fpop-range' }, $('label', { cls: 'cvsw-fpop-row' }, $('span', { text: 'At least' }), field('min', 'text', ph, flt.min, (x) => set({ min: x.trim() }), 'At least')), $('label', { cls: 'cvsw-fpop-row' }, $('span', { text: 'At most' }), field('max', 'text', ph, flt.max, (x) => set({ max: x.trim() }), 'At most'))));
    }
    const emp = $('div', { cls: 'cvsw-fpop-chips' });
    for (const [v, t] of [['empty', 'Empty'], ['not', 'Not empty']]) { const b = $('button', { cls: `cvsw-fpop-chip${flt.empty === v ? ' is-on' : ''}`, text: t, attr: { type: 'button', 'aria-pressed': String(flt.empty === v) } }); b.onclick = () => set({ empty: flt.empty === v ? null : v }); emp.append(b); }
    const clear = $('button', { cls: 'cvsw-fpop-clear', text: 'Clear', attr: { type: 'button' } }); clear.disabled = !filterCount(flt);
    clear.onclick = () => { q = ''; editView((v) => { delete v.filters[key]; }); libPage = 0; draw(); };
    p.el.replaceChildren($('div', { cls: 'cvsw-fpop-title', text: c.label }), body, $('div', { cls: 'cvsw-fpop-foot' }, emp, clear));
    p.place();
    if (keepFocus) { const i = p.el.querySelector(`[data-f="${keepFocus}"]`); if (i) { i.focus(); try { i.setSelectionRange(caret, caret); } catch (_) { /* a date field */ } } }
  };
  draw();
  setTimeout(() => p.el.querySelector('input')?.focus({ preventScroll: true }), 0);
}

// ---- the Columns menu: show and hide, the filter button toggle, drag to reorder, presets, icons --------------
function openColumns(anchor) {
  if (popJustShut('cols')) return;
  const p = openPop(anchor, { key: 'cols', cls: 'cvsw-cpop', align: 'end', anchorSel: '.cvsw-dt-colbtn', label: curView().layout === 'gallery' ? 'Card properties' : 'Columns' });
  const draw = () => {
    const v = curView(); const shownN = v.columns.shown.filter((k) => !colOf(k).fixed).length;
    const list = $('div', { cls: 'cvsw-cpop-list', attr: { 'data-sort-list': 'cols', 'data-sort-axis': 'y' } });
    for (const k of v.columns.order) {
      const c = colOf(k); const on = c.fixed || v.columns.shown.includes(k) || (c.auto && !v.columns.statusOff && tableFiles.length > 0); const fb = v.filterButtons.includes(k);
      const check = $('button', { cls: `cvsw-cpop-check${on ? ' is-on' : ''}`, attr: { type: 'button', role: 'checkbox', 'aria-checked': String(on), 'aria-label': `Show ${c.label}` } }, $('span', { cls: 'cvsw-fpop-box' }, on ? icon('check') : null), $('span', { cls: 'cvsw-cpop-name', text: colLabel(c, v) }));
      if (c.fixed) check.disabled = true;
      check.onclick = () => { editView((x) => { x.columns.shown = on ? x.columns.shown.filter((y) => y !== k) : [...new Set([...x.columns.shown, k])]; if (c.auto) x.columns.statusOff = on; if (on && x.frozen === k) x.frozen = null; }); draw(); };
      const ft = $('button', { cls: `cvsw-cpop-filter${fb ? ' is-on' : ''}`, attr: { type: 'button', 'aria-pressed': String(fb), 'aria-label': 'Filter button' } }, icon('list-filter'));
      ft.onclick = () => { editView((x) => { x.filterButtons = fb ? x.filterButtons.filter((y) => y !== k) : [...x.filterButtons, k]; }); draw(); };
      list.append($('div', { cls: 'cvsw-cpop-row', attr: { 'data-sort-item': k } }, $('span', { cls: 'cvsw-cpop-grip', attr: { 'data-sort-handle': '', 'aria-hidden': 'true' } }, icon('grip-vertical')), check, ft));
    }
    const presets = $('div', { cls: 'cvsw-cpop-presets' }, ...Object.entries(LIB_PRESETS).map(([name, keys]) => { const b = $('button', { cls: 'cvsw-fpop-chip', text: name, attr: { type: 'button' } }); b.onclick = () => { editView((x) => { x.columns.shown = [...keys]; }); draw(); }; return b; }));
    const reset = $('button', { cls: 'cvsw-cpop-item', attr: { type: 'button' } }, icon('rotate-ccw'), $('span', { text: 'Reset order and widths' }));
    reset.onclick = () => { editView((x) => { x.columns.order = LIB_COLS.map((c) => c.key); x.columns.widths = {}; }); draw(); };
    const sw = $('button', { cls: `cvsw-cpop-item cvsw-cpop-switch${shared.columnIcons ? ' is-on' : ''}`, attr: { type: 'button', role: 'switch', 'aria-checked': String(!!shared.columnIcons) } }, $('span', { text: 'Column icons' }), $('span', { cls: 'cvsw-cpop-toggle' }));
    sw.onclick = async () => { await setShared({ columnIcons: !shared.columnIcons }); draw(); };
    p.el.replaceChildren($('div', { cls: 'cvsw-fpop-title', text: `Columns (${shownN}/${LIB_COLS.length - 1})` }), list, $('div', { cls: 'cvsw-cpop-sep' }), $('div', { cls: 'cvsw-cpop-label', text: 'Presets' }), presets, reset, $('div', { cls: 'cvsw-cpop-sep' }), sw);
    HOST.internals?.makeSortable?.(list, { onDrop: ({ fromIndex, toIndex }) => { editView((x) => { const o = [...x.columns.order]; const [m] = o.splice(fromIndex, 1); o.splice(toIndex, 0, m); x.columns.order = o; }); draw(); } });
    p.place();
  };
  draw();
}

// ---- the table -----------------------------------------------------------------------------------------
let nameSub = false;   // the folder shows under each name (searching, or sub-folders included)
let tableFiles = [];   // the folder's videos before filtering (what the filter popovers offer)
let tableRows = [];    // after search, filters and sorts (every page)
let tablePage = [];    // the rows on this page, in order
let tableCols = [];    // the columns shown, in order
const groupOf = (f, key) => {
  const c = colOf(key); const raw = libRaw(f, key);
  if (rawEmpty(key, raw)) return c.type === 'tags' ? 'No tags' : key === 'clips' ? 'No clips' : `No ${c.label.toLowerCase()}`;
  if (key === 'status') return videoStatus(f).name;
  if (c.type === 'tags') return raw.map((t) => `#${t}`).join(' ');
  if (c.type === 'date') return new Date(raw).toLocaleDateString([], { month: 'long', year: 'numeric' });
  if (c.type === 'length') return raw < 60 ? 'Under 1 min' : raw < 300 ? '1 to 5 min' : raw < 1200 ? '5 to 20 min' : 'Over 20 min';
  if (c.type === 'size') return raw < 1e8 ? 'Under 100 MB' : raw < 1e9 ? '100 MB to 1 GB' : 'Over 1 GB';
  if (key === 'clips') return plural(raw, 'clip');
  if (key === 'name') return String(raw).charAt(0).toUpperCase();
  return String(raw);
};
const CALCS = { done: 'Done', count: 'Count', values: 'Count values', empty: 'Count empty', sum: 'Sum', avg: 'Average', min: 'Min', max: 'Max', earliest: 'Earliest', latest: 'Latest' };
const calcsFor = (c) => ['count', 'values', 'empty', ...(['number', 'length', 'size'].includes(c.type) ? ['sum', 'avg', 'min', 'max'] : c.type === 'date' ? ['earliest', 'latest'] : c.type === 'status' ? ['done'] : [])];
function calcText(c, kind, rows) {
  const vals = rows.map((f) => libRaw(f, c.key)); const full = vals.filter((x) => !rawEmpty(c.key, x));
  if (kind === 'done') { const set = statusSetFor(currentFolder); const last = set.statuses[set.statuses.length - 1]; const n = last ? rows.filter((f) => videoStatus(f).name === last.name).length : 0; return `${n}/${rows.length}${rows.length ? ` (${Math.round((n / rows.length) * 100)}%)` : ''}`; }
  if (kind === 'count') return String(rows.length);
  if (kind === 'values') return String(full.length);
  if (kind === 'empty') return String(rows.length - full.length);
  if (!full.length) return '-';
  const fmt = (n) => (c.type === 'length' ? fmtLen(Math.round(n)) : c.type === 'size' ? fmtSize(n) : c.type === 'date' ? when(n) : String(Math.round(n * 100) / 100));
  const sum = full.reduce((a, x) => a + x, 0);
  return fmt(kind === 'sum' ? sum : kind === 'avg' ? sum / full.length : kind === 'min' || kind === 'earliest' ? Math.min(...full) : Math.max(...full));
}
const checkedFiles = () => [...libChecked].map((p) => vault.getFileByPath(p)).filter(Boolean);
/** Header menu > Rename: the header's title becomes a box; Enter or a click away saves the label in the view, Escape cancels. */
function renameColumn(hb, c) {
  const span = hb.querySelector('.cvsw-dt-thname'); if (!span) return;
  const inp = $('input', { cls: 'cvsw-dt-threname', type: 'text', attr: { 'aria-label': 'Column name', spellcheck: 'false', maxlength: '40' } }); inp.value = colLabel(c);
  let done = false;
  const finish = (save) => { if (done) return; done = true; const val = inp.value.replace(/\s+/g, ' ').trim(); if (!save || val === colLabel(c)) { renderLibrary(); return; } editView((x) => { x.columns.labels = x.columns.labels || {}; if (!val || val === c.label) delete x.columns.labels[c.key]; else x.columns.labels[c.key] = val; }); };
  inp.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); finish(true); } else if (e.key === 'Escape') { e.preventDefault(); finish(false); } };
  inp.onblur = () => finish(true); inp.onclick = (e) => e.stopPropagation(); inp.onpointerdown = (e) => e.stopPropagation();
  hb.draggable = false; span.replaceWith(inp); inp.focus(); inp.select();
}
/** A column's label in a view: Tony's own name for it (header menu > Rename), else its built-in label. */
const colLabel = (c, v = curView()) => (c && v?.columns?.labels?.[c.key]) || c?.label || '';
/** Rows per page, the range and the page buttons (the table's footer row, under the gallery). */
function pagerParts(rows, per, pages) {
  const perOpts = [...new Set([25, 50, 100, shared.rowsPerPage, per, 100000])].filter((n) => n > 0).sort((a, b) => a - b);
  const perSel = $('select', { cls: 'cvsw-dt-per', attr: { 'aria-label': 'Rows per page' } }, ...perOpts.map((n) => { const o = $('option', { text: n >= 100000 ? 'All' : String(n), attr: { value: String(n) } }); if (n === per) o.selected = true; return o; }));
  perSel.onchange = () => { libPage = 0; const n = Number(perSel.value); editView((x) => { x.rowsPerPage = n === shared.rowsPerPage ? 0 : n; }); };
  const from = rows.length ? (per >= 100000 ? 1 : libPage * per + 1) : 0; const to = per >= 100000 ? rows.length : Math.min(rows.length, (libPage + 1) * per);
  const turn = (d) => { libPage += d; const m = scrollMem.get(currentFolder); if (m) m.y = 0; cellFocus = null; renderLibrary(); };
  const prev = btn('Previous page', 'chevron-left', () => turn(-1), { variant: 'ghost', iconOnly: true }); prev.disabled = libPage <= 0;
  const next = btn('Next page', 'chevron-right', () => turn(1), { variant: 'ghost', iconOnly: true }); next.disabled = libPage >= pages - 1 || per >= 100000;
  return { perSel, range: $('span', { cls: 'cvsw-dt-range', text: `${from}-${to} of ${rows.length}` }), prev, next };
}
/** The gallery under the same header and toolbar: folder tiles, then a card per video (the view's filters and sorts), the pager under the grid. */
function renderGalleryBody(wrap, rows, pageRows, folders, { per, pages, active, lq, files }) {
  const area = $('div', { cls: 'cvsw-gal' });
  if (folders.length) {
    const grid = $('div', { cls: 'cvsw-folders' });
    for (const f of folders) { const t = $('button', { cls: 'cvsw-folder', attr: { type: 'button' } }, icon('folder'), $('span', { cls: 'cvsw-folder-name', text: f.name }), $('span', { cls: 'cvsw-folder-count', text: String(videosUnder(f)) })); t.onclick = () => go('f', f.path); t.oncontextmenu = (e) => { e.preventDefault(); itemMenu(e, f); }; wireDrag(t, f); wireDrop(t, f.path); grid.append(t); }
    area.append(grid);
  }
  if (pageRows.length) { const grid = $('div', { cls: 'cvsw-grid' }); for (const f of pageRows) grid.append(card(f, !!lq || !!treeFilter.trim())); area.append(grid); }
  else if (active && (files.length || lq)) area.append(empty('search-x', 'No videos match', 'Nothing here matches the search or the filters.'));
  else if (!folders.length) area.append(empty('film', currentFolder === VP ? 'No videos yet' : 'This folder is empty', 'Upload an mp4 or mov, or drop one here. Videos added on the Mac or any other device show up on their own.'));
  wrap.append(area);
  if (rows.length > Math.min(per, 25)) { const pg = pagerParts(rows, per, pages); wrap.append($('div', { cls: 'cvsw-dt-foot' }, $('div', { cls: 'cvsw-dt-foot-l' }, $('span', { text: 'Rows per page' }), pg.perSel), $('div', { cls: 'cvsw-dt-foot-r' }, pg.range, pg.prev, pg.next))); }
}
function renderLibTable(body, folder, files, folders, lq, waiting = null) {
  const v = curView(); const fkey = `${currentFolder}|${v.id}`;
  const anyStatus = files.length > 0; // every video has a status
  const cols = tableCols = v.columns.order.map(colOf).filter((c) => c.fixed || v.columns.shown.includes(c.key) || (c.auto && anyStatus && !v.columns.statusOff) || (c.key === 'folder' && v.recursive)); // Folder shows while sub-folders are included
  const widthOf = (c) => (Number(v.columns.widths[c.key]) > 0 ? Math.max(56, Number(v.columns.widths[c.key])) : fitCache.get(`${fkey}|${c.key}`) || c.w); // Tony's width, else the measured fit (fitColumns)
  tableFiles = files;
  let rows = files.filter((f) => passes(f, v));
  const cmp = (a, b) => { for (const s of v.sorts) { const A = libValue(a, s.key); const B = libValue(b, s.key); const r = typeof A === 'number' && typeof B === 'number' ? A - B : String(A).localeCompare(String(B), undefined, { numeric: true }); if (r) return s.desc ? -r : r; } return a.name.localeCompare(b.name, undefined, { numeric: true }); };
  rows.sort(cmp);
  if (v.groupBy) { const order = []; const by = new Map(); if (v.groupBy === 'status') for (const s of statusSetFor(currentFolder).statuses) { order.push(s.name); by.set(s.name, []); } for (const f of rows) { const g = groupOf(f, v.groupBy); if (!by.has(g)) { by.set(g, []); order.push(g); } by.get(g).push(f); } rows = order.flatMap((g) => by.get(g)); }
  tableRows = rows;
  const per = v.rowsPerPage || shared.rowsPerPage || 100; const pages = Math.max(1, Math.ceil(rows.length / per)); libPage = Math.max(0, Math.min(libPage, pages - 1));
  const pageRows = tablePage = per >= 100000 ? rows : rows.slice(libPage * per, (libPage + 1) * per);
  for (const p of [...libChecked]) if (!vault.getFileByPath(p)) libChecked.delete(p);
  const activeFilters = Object.values(v.filters).some((x) => filterCount(x));
  const active = !!lq || activeFilters;

  const rh = shared.rowHeight; const thumbH = Math.max(18, Math.min(64, rh - 16));
  const wrap = $('div', { cls: 'cvsw-dt', attr: { 'data-view': v.id } });
  wrap.style.setProperty('--dt-row-h', `${rh}px`); wrap.style.setProperty('--dt-thumb-h', `${thumbH}px`);
  // the header line on the canvas: the title and its count, the view tabs at the right (the same place in Table and Gallery)
  const total = files.length; const gallery = v.layout === 'gallery';
  const add = $('button', { cls: 'cvsw-btn is-default is-icon cvsw-dt-add', attr: { type: 'button', 'aria-label': 'Add videos', title: 'Add videos' } }, icon('plus')); add.onclick = () => pickUpload(currentFolder);
  wrap.append($('div', { cls: 'cvsw-dt-head' },
    $('div', { cls: 'cvsw-dt-title' }, $('span', { text: folder?.path === VP ? ROOT_NAME : folder?.name || ROOT_NAME }), $('span', { cls: 'cvsw-dt-sub', text: `${active ? `${rows.length} of ` : ''}${total} video${total === 1 ? '' : 's'}${folders.length ? ` · ${folders.length} folder${folders.length === 1 ? '' : 's'}` : ''}` })),
    viewTabs(), dtBulk));
  // the toolbar: search, the filter buttons, then Columns and Actions
  const search = $('input', { cls: 'cvsw-dt-search-input', type: 'search', value: libQuery, attr: { placeholder: 'Search videos...', 'aria-label': 'Search videos', spellcheck: 'false' } });
  search.oninput = () => { libQuery = search.value; libPage = 0; renderLibrary(); const s = main.querySelector('.cvsw-dt-search-input'); if (s) { s.focus(); s.setSelectionRange(s.value.length, s.value.length); } };
  search.onkeydown = (e) => { if (e.key === 'Escape' && search.value) { e.stopPropagation(); libQuery = ''; renderLibrary(); } };
  const fbar = $('div', { cls: 'cvsw-dt-filters', attr: { 'data-sort-list': 'filters' } });
  for (const k of v.filterButtons) {
    const c = colOf(k); const n = filterCount(v.filters[k]);
    const b = $('button', { cls: `cvsw-btn is-outline is-sm cvsw-dt-fbtn${n ? ' is-on' : ''}`, attr: { type: 'button', 'data-sort-item': k, 'data-fkey': k, 'aria-haspopup': 'dialog' } }, $('span', { text: colLabel(c, v) }), n ? $('span', { cls: 'cvsw-dt-count', text: String(n) }) : null);
    b.onclick = () => openFilter(k, b);
    fbar.append(b);
  }
  HOST.internals?.makeSortable?.(fbar, { onDrop: ({ fromIndex, toIndex }) => editView((x) => { const o = [...x.filterButtons]; const [m] = o.splice(fromIndex, 1); o.splice(toIndex, 0, m); x.filterButtons = o; }) });
  const clear = active ? btn('Clear filters', null, () => { libQuery = ''; libPage = 0; if (activeFilters) editView((x) => { x.filters = {}; }); else renderLibrary(); }, { variant: 'ghost', cls: 'is-sm cvsw-dt-clear' }) : null;
  const colBtn = $('button', { cls: 'cvsw-btn is-outline is-icon cvsw-dt-colbtn', attr: { type: 'button', 'aria-label': gallery ? 'Card properties' : 'Columns', title: gallery ? 'Card properties' : 'Columns', 'aria-haspopup': 'dialog' } }, icon('columns-3')); colBtn.onclick = () => openColumns(colBtn);
  // Sort: the view's sorts (the gallery's order too; the table's header menus sort as well)
  const sortBtn = $('button', { cls: `cvsw-btn is-outline is-icon cvsw-dt-sortbtn${v.sorts.length ? ' is-on' : ''}`, attr: { type: 'button', 'aria-label': 'Sort', title: v.sorts.length ? `Sort: ${v.sorts.map((x) => colLabel(colOf(x.key), v)).join(', ')}` : 'Sort', 'aria-haspopup': 'menu' } }, icon('arrow-up-down'));
  sortBtn.onclick = () => {
    const m = new Menu();
    if (v.sorts.length) m.addLabel('Sorted by');
    v.sorts.forEach((x, i) => m.addItem((it) => it.setTitle(`${colLabel(colOf(x.key), v)}${v.sorts.length > 1 ? ` (${i + 1})` : ''}`).setIcon(x.desc ? 'arrow-down' : 'arrow-up').setShortcut(x.desc ? 'Descending' : 'Ascending').onClick(() => editView((y) => { y.sorts = y.sorts.map((z) => (z.key === x.key ? { ...z, desc: !z.desc } : z)); }))));
    if (v.sorts.length) { m.addItem((it) => it.setTitle('Clear sorts').setIcon('list-x').onClick(() => editView((y) => { y.sorts = []; }))); m.addSeparator(); }
    m.addLabel(v.sorts.length ? 'Add a sort' : 'Sort by');
    for (const c of LIB_COLS) if (!v.sorts.some((x) => x.key === c.key)) m.addItem((it) => it.setTitle(colLabel(c, v)).setIcon(c.icon).onClick(() => editView((y) => { y.sorts = [...y.sorts, { key: c.key, desc: c.type === 'date' }]; })));
    m.showBelow(sortBtn, 'end');
  };
  const actBtn = $('button', { cls: 'cvsw-btn is-outline is-icon cvsw-dt-actbtn', attr: { type: 'button', 'aria-label': 'Actions', 'aria-haspopup': 'menu' } }, icon('ellipsis'));
  actBtn.onclick = () => {
    const m = new Menu();
    m.addItem((i) => i.setTitle('Group by').setIcon('rows-3').setShortcut(v.groupBy ? colOf(v.groupBy).label : 'None').onClick(() => { const g = new Menu(); g.addLabel('Group by'); g.addItem((x) => x.setTitle('None').setChecked(!v.groupBy).onClick(() => editView((y) => { y.groupBy = null; }))); for (const c of LIB_COLS) g.addItem((x) => x.setTitle(c.label).setIcon(c.icon).setChecked(v.groupBy === c.key).onClick(() => editView((y) => { y.groupBy = c.key; }))); g.showBelow(main.querySelector('.cvsw-dt-actbtn') || actBtn, 'end'); }));
    m.addItem((i) => i.setTitle('Include sub-folders').setIcon('folder-tree').setChecked(v.recursive).onClick(() => { libPage = 0; editView((y) => { y.recursive = !y.recursive; }); }));
    m.addItem((i) => i.setTitle('Export CSV').setIcon('file-text').onClick(() => exportCsv(tableRows, tableCols)));
    m.addItem((i) => i.setTitle('Select all').setIcon('list-checks').setDisabled(!rows.length).onClick(() => { for (const f of tableRows) libChecked.add(f.path); applyChecked(); }));
    m.addItem((i) => i.setTitle('Reset view').setIcon('rotate-ccw').onClick(() => resetView()));
    m.addSeparator();
    m.addItem((i) => i.setTitle('New folder').setIcon('folder-plus').onClick(() => void newFolder(currentFolder)));
    if (cloud.local) m.addItem((i) => i.setTitle('Show in Finder').setIcon('external-link').onClick(() => void revealOnMac(currentFolder)));
    m.showBelow(actBtn, 'end');
  };
  wrap.append($('div', { cls: 'cvsw-dt-tools' },
    $('div', { cls: 'cvsw-dt-tools-l' }, $('label', { cls: 'cvsw-dt-search' }, icon('search'), search), fbar, clear),
    $('div', { cls: 'cvsw-dt-tools-r' }, sortBtn, colBtn, actBtn, add)));
  if (waiting) wrap.append(waiting);
  if (gallery) { renderGalleryBody(wrap, rows, pageRows, folders, { per, pages, active, lq, files }); body.append(wrap); return; }
  // the table
  const table = $('table', { cls: 'cvsw-dt-table' });
  const colgroup = $('colgroup', {}, $('col', { attr: { style: `width:${CHECK_COL_W}px` } }), ...cols.map((c) => $('col', { attr: { style: `width:${widthOf(c)}px` } })), $('col'));
  table.style.minWidth = `${CHECK_COL_W + cols.reduce((n, c) => n + widthOf(c), 0)}px`;
  const frozenAt = v.frozen ? cols.findIndex((c) => c.key === v.frozen) : -1;
  const freezeCls = (ci) => (frozenAt < 0 ? '' : ci <= frozenAt ? ` is-frozen${ci === frozenAt ? ' is-frozen-last' : ''}` : '');
  const headBox = $('input', { type: 'checkbox', cls: 'cvsw-dt-check', attr: { 'aria-label': 'Select all on this page' } });
  headBox.onchange = () => { for (const f of tablePage) { if (headBox.checked) libChecked.add(f.path); else libChecked.delete(f.path); } applyChecked(); };
  const tr = $('tr', {}, $('th', { cls: `cvsw-dt-checkcell${frozenAt >= 0 ? ' is-frozen' : ''}` }, headBox));
  cols.forEach((c, ci) => {
    const si = v.sorts.findIndex((s) => s.key === c.key); const sorted = si >= 0; const srt = v.sorts[si];
    const hb = $('button', { cls: 'cvsw-dt-th', attr: { type: 'button', 'aria-haspopup': 'menu' } }, shared.columnIcons ? icon(c.icon) : null, $('span', { cls: 'cvsw-dt-thname', text: colLabel(c, v) }), sorted ? icon(srt.desc ? 'arrow-down' : 'arrow-up', 'cvsw-dt-sorted') : null, sorted && v.sorts.length > 1 ? $('span', { cls: 'cvsw-dt-sortn', text: String(si + 1) }) : null);
    hb.oncontextmenu = (e) => { e.preventDefault(); hb.onclick(); }; // a right-click opens the same menu
    hb.onclick = () => {
      const m = new Menu();
      // Rename gives the column a label of Tony's in this view (Reset puts the built-in one back)
      m.addItem((i) => i.setTitle('Rename').setIcon('pencil').onClick(() => renameColumn(hb, c)));
      if (v.columns.labels?.[c.key]) m.addItem((i) => i.setTitle(`Reset name (${c.label})`).setIcon('rotate-ccw').onClick(() => editView((x) => { delete x.columns.labels[c.key]; })));
      m.addSeparator();
      m.addItem((i) => i.setTitle('Sort ascending').setIcon('arrow-up').setChecked(sorted && !srt.desc && v.sorts.length === 1).onClick(() => editView((x) => { x.sorts = [{ key: c.key, desc: false }]; })));
      m.addItem((i) => i.setTitle('Sort descending').setIcon('arrow-down').setChecked(sorted && srt.desc && v.sorts.length === 1).onClick(() => editView((x) => { x.sorts = [{ key: c.key, desc: true }]; })));
      if (sorted && v.sorts.length > 1) m.addItem((i) => i.setTitle('Remove from sort').setIcon('list-x').onClick(() => editView((x) => { x.sorts = x.sorts.filter((s) => s.key !== c.key); })));
      else m.addItem((i) => i.setTitle('Add to sort').setIcon('list-plus').setDisabled(sorted).onClick(() => editView((x) => { x.sorts = [...x.sorts, { key: c.key, desc: false }]; })));
      m.addSeparator();
      m.addItem((i) => i.setTitle('Filter button').setIcon('list-filter').setChecked(v.filterButtons.includes(c.key)).onClick(() => editView((x) => { x.filterButtons = x.filterButtons.includes(c.key) ? x.filterButtons.filter((k) => k !== c.key) : [...x.filterButtons, c.key]; })));
      m.addItem((i) => i.setTitle(v.frozen === c.key ? 'Unfreeze' : 'Freeze up to here').setIcon(v.frozen === c.key ? 'pin-off' : 'pin').onClick(() => editView((x) => { x.frozen = x.frozen === c.key ? null : c.key; })));
      m.addItem((i) => i.setTitle('Wrap text').setIcon('wrap-text').setChecked(v.columns.wrap.includes(c.key)).onClick(() => editView((x) => { x.columns.wrap = x.columns.wrap.includes(c.key) ? x.columns.wrap.filter((k) => k !== c.key) : [...x.columns.wrap, c.key]; })));
      m.addItem((i) => i.setTitle('Fit width').setIcon('chevrons-left-right').onClick(() => fitWidth(c.key)));
      m.addSeparator();
      const mv = (d) => editView((x) => { const vis = x.columns.order.filter((k) => cols.some((y) => y.key === k)); const i0 = vis.indexOf(c.key); const j = i0 + d; if (j < 0 || j >= vis.length) return; const a = x.columns.order.indexOf(vis[i0]); const b = x.columns.order.indexOf(vis[j]); [x.columns.order[a], x.columns.order[b]] = [x.columns.order[b], x.columns.order[a]]; });
      const soon = 'Custom video properties are coming next';
      m.addItem((i) => { i.setTitle('Insert left').setIcon('arrow-left-to-line').setDisabled(true); i.tip = soon; });
      m.addItem((i) => { i.setTitle('Insert right').setIcon('arrow-right-to-line').setDisabled(true); i.tip = soon; });
      m.addItem((i) => { i.setTitle('Duplicate').setIcon('copy-plus').setDisabled(true); i.tip = soon; });
      m.addItem((i) => i.setTitle('Move left').setIcon('chevron-left').setDisabled(ci === 0).onClick(() => mv(-1)));
      m.addItem((i) => i.setTitle('Move right').setIcon('chevron-right').setDisabled(ci === cols.length - 1).onClick(() => mv(1)));
      if (!c.fixed) m.addItem((i) => i.setTitle('Hide').setIcon('eye-off').onClick(() => editView((x) => { x.columns.shown = x.columns.shown.filter((k) => k !== c.key); if (c.auto) x.columns.statusOff = true; if (x.frozen === c.key) x.frozen = null; })));
      m.addSeparator();
      m.addItem((i) => { i.setTitle('Delete property').setIcon('trash-2').setDisabled(true); i.tip = soon; });
      m.showBelow(hb);
    };
    const grip = $('span', { cls: 'cvsw-dt-resize', attr: { title: 'Drag to resize. Double-click to fit.' } });
    grip.onpointerdown = (e) => {
      if (e.detail > 1) return;
      e.preventDefault(); e.stopPropagation(); const x0 = e.clientX; const w0 = widthOf(c); const col = colgroup.children[ci + 1]; let w = w0;
      try { grip.setPointerCapture(e.pointerId); } catch (_) { /* a made-up pointer */ }
      const move = (ev) => { w = Math.max(64, Math.round(w0 + ev.clientX - x0)); col.style.width = `${w}px`; table.style.minWidth = `${CHECK_COL_W + cols.reduce((n, y) => n + (y === c ? w : widthOf(y)), 0)}px`; placeFrozen(); };
      const up = () => { grip.removeEventListener('pointermove', move); if (w !== w0) editView((x) => { x.columns.widths[c.key] = w; }, { render: false }); v.columns.widths[c.key] = w; };
      grip.addEventListener('pointermove', move); grip.addEventListener('pointerup', up, { once: true }); grip.addEventListener('pointercancel', up, { once: true });
    };
    grip.onclick = (e) => e.stopPropagation();
    grip.ondblclick = (e) => { e.stopPropagation(); fitWidth(c.key); };
    const th = $('th', { cls: freezeCls(ci).trim(), attr: { 'data-col': c.key, 'aria-sort': sorted ? (srt.desc ? 'descending' : 'ascending') : null } }, hb, grip);
    // drag a header onto another to put it there (datatable-columns-03)
    hb.draggable = true;
    hb.addEventListener('dragstart', (e) => { e.dataTransfer.setData('application/x-cvsw-col', c.key); e.dataTransfer.effectAllowed = 'move'; th.addClass('is-dragging'); });
    hb.addEventListener('dragend', () => { th.removeClass('is-dragging'); for (const x of tr.querySelectorAll('.is-drop-before, .is-drop-after')) x.removeClass('is-drop-before', 'is-drop-after'); });
    th.addEventListener('dragover', (e) => { if (![...e.dataTransfer.types].includes('application/x-cvsw-col')) return; e.preventDefault(); const r = th.getBoundingClientRect(); const after = e.clientX > r.left + r.width / 2; for (const x of tr.querySelectorAll('.is-drop-before, .is-drop-after')) x.removeClass('is-drop-before', 'is-drop-after'); th.addClass(after ? 'is-drop-after' : 'is-drop-before'); });
    th.addEventListener('dragleave', (e) => { if (!th.contains(e.relatedTarget)) th.removeClass('is-drop-before', 'is-drop-after'); });
    th.addEventListener('drop', (e) => {
      const from = e.dataTransfer.getData('application/x-cvsw-col'); if (!from || from === c.key) return; e.preventDefault();
      const after = th.hasClass('is-drop-after'); editView((x) => { const o = x.columns.order.filter((k) => k !== from); o.splice(o.indexOf(c.key) + (after ? 1 : 0), 0, from); x.columns.order = o; });
    });
    tr.append(th);
  });
  tr.append($('th', { cls: 'cvsw-dt-fill', attr: { 'aria-hidden': 'true' } }));
  const tbody = $('tbody');
  const cellCls = (c, ci) => `${v.columns.wrap.includes(c.key) ? 'is-wrap' : ''}${freezeCls(ci)}`.trim();
  // folders first (this folder's own, when not searching or showing sub-folders)
  for (const d of folders) {
    const row = $('tr', { cls: 'cvsw-dt-folder' }, $('td', { cls: `cvsw-dt-checkcell${frozenAt >= 0 ? ' is-frozen' : ''}` }), ...cols.map((c, ci) => $('td', { cls: cellCls(c, ci), attr: { 'data-col': c.key } }, c.key === 'name' ? $('div', { cls: 'cvsw-td-namecell' }, $('span', { cls: 'cvsw-td-pic is-folder' }, icon('folder')), $('div', { cls: 'cvsw-td-namewrap' }, $('span', { cls: 'cvsw-td-name', text: d.name }), $('span', { cls: 'cvsw-td-muted cvsw-td-text', text: `${videosUnder(d)} video${videosUnder(d) === 1 ? '' : 's'}` }))) : '')), $('td', { cls: 'cvsw-dt-fill' }));
    row.onclick = () => { if (bandJustEnded()) return; go('f', d.path); }; row.oncontextmenu = (e) => { e.preventDefault(); itemMenu(e, d); }; wireDrag(row, d); wireDrop(row, d.path);
    tbody.append(row);
  }
  let lastGroup = null;
  const nInGroup = v.groupBy ? rows.reduce((m, f) => { const g = groupOf(f, v.groupBy); m.set(g, (m.get(g) || 0) + 1); return m; }, new Map()) : null;
  for (const f of pageRows) {
    if (v.groupBy) {
      const g = groupOf(f, v.groupBy); const shut = groupShut.has(`${fkey}|${g}`);
      if (g !== lastGroup) {
        lastGroup = g;
        const gst = v.groupBy === 'status' ? statusSetFor(currentFolder).statuses.find((x) => x.name === g) : null;
        const gb = $('button', { cls: 'cvsw-dt-groupbtn', attr: { type: 'button', 'aria-expanded': String(!shut) } }, icon(shut ? 'chevron-right' : 'chevron-down'), $('span', { cls: 'cvsw-dt-groupname' }, gst ? statusDot({ st: gst }) : null, $('span', { text: g })), $('span', { cls: 'cvsw-dt-groupn', text: String(nInGroup.get(g)) }));
        gb.onclick = () => { const k = `${fkey}|${g}`; if (groupShut.has(k)) groupShut.delete(k); else groupShut.add(k); renderLibrary(); };
        tbody.append($('tr', { cls: 'cvsw-dt-group', attr: { 'data-group': g } }, $('td', { attr: { colspan: String(cols.length + 2) } }, gb)));
      }
      if (shut) continue;
    }
    const box = $('input', { type: 'checkbox', cls: 'cvsw-dt-check', attr: { 'aria-label': `Select ${f.basename}` } });
    box.onclick = (e) => { e.stopPropagation(); tableHot = true; checkRow(f.path, box.checked, e.shiftKey); };
    const row = $('tr', { attr: { 'data-vp': f.path } }, $('td', { cls: `cvsw-dt-checkcell${frozenAt >= 0 ? ' is-frozen' : ''}` }, box), ...cols.map((c, ci) => $('td', { cls: cellCls(c, ci), attr: { 'data-col': c.key } }, libCell(f, c.key))), $('td', { cls: 'cvsw-dt-fill' }));
    row.onclick = (e) => { if (bandJustEnded() || e.target.closest('.cvsw-name-input, .cvsw-dt-checkcell')) return; rowClick(e, f); };
    row.oncontextmenu = (e) => { e.preventDefault(); itemMenu(e, f); };
    wireDrag(row, f);
    tbody.append(row);
  }
  // grouped by status: the set's empty statuses show too, each in its place in the workflow order
  if (v.groupBy === 'status' && !lq) {
    const names = statusSetFor(currentFolder).statuses;
    const has = (n) => tbody.querySelector(`tr.cvsw-dt-group[data-group="${CSS.escape(n)}"]`);
    names.forEach((st, i) => {
      if (has(st.name)) return;
      const gb = $('button', { cls: 'cvsw-dt-groupbtn', attr: { type: 'button', 'aria-expanded': 'true' } }, icon('chevron-down'), $('span', { cls: 'cvsw-dt-groupname' }, statusDot({ st }), $('span', { text: st.name })), $('span', { cls: 'cvsw-dt-groupn', text: '0' }));
      const row = $('tr', { cls: 'cvsw-dt-group is-empty', attr: { 'data-group': st.name } }, $('td', { attr: { colspan: String(cols.length + 2) } }, gb));
      const next = names.slice(i + 1).map((x) => has(x.name)).find(Boolean) || tbody.querySelector('tr.cvsw-dt-group:not([data-group])');
      if (next) next.before(row); else { const others = [...tbody.querySelectorAll('tr.cvsw-dt-group')].filter((g) => !names.some((x) => x.name === g.dataset.group)); if (others[0]) others[0].before(row); else tbody.append(row); }
    });
  }
  if (!pageRows.length && !folders.length) tbody.append($('tr', { cls: 'cvsw-dt-empty' }, $('td', { attr: { colspan: String(cols.length + 2) } }, active && (files.length || lq) ? 'No videos match.' : 'No videos here yet. Add some, or drop them here.')));
  // one footer row (Tony, 2026-10-02): Rows per page under the checkbox and first column (sticky left), each other
  // column's calculation under it ("Calculate" on hover), the pager sticky at the card's right edge
  const pg = pagerParts(rows, per, pages);
  const firstW = CHECK_COL_W + (cols[0] ? widthOf(cols[0]) : 0);
  const perCell = $('td', { cls: 'cvsw-dt-footl is-frozen', attr: { colspan: '2' } }, $('span', { cls: 'cvsw-dt-footper', attr: firstW < 210 ? { title: 'Rows per page' } : {} }, firstW >= 210 ? $('span', { text: 'Rows per page' }) : null, pg.perSel));
  const calcRow = $('tr', { cls: 'cvsw-dt-calc' }, perCell);
  cols.forEach((c, ci) => {
    if (ci === 0) return;
    const kind = calcsFor(c).includes(v.calcs[c.key]) ? v.calcs[c.key] : null;
    const cb = $('button', { cls: `cvsw-dt-calcbtn${kind ? ' is-on' : ''}${['number', 'length', 'size'].includes(c.type) ? ' is-num' : ''}`, attr: { type: 'button', 'aria-label': `Calculate ${colLabel(c, v)}`, 'aria-haspopup': 'menu' } }, kind ? $('span', { cls: 'cvsw-dt-calcname', text: CALCS[kind] }) : $('span', { cls: 'cvsw-dt-calcname', text: 'Calculate' }), kind ? $('span', { cls: 'cvsw-dt-calcval', text: calcText(c, kind, rows) }) : null);
    cb.onclick = () => { const m = new Menu(); m.addLabel(colLabel(c, v)); m.addItem((i) => i.setTitle('None').setChecked(!kind).onClick(() => editView((x) => { delete x.calcs[c.key]; }))); for (const k of calcsFor(c)) m.addItem((i) => i.setTitle(CALCS[k]).setChecked(kind === k).onClick(() => editView((x) => { x.calcs[c.key] = k; }))); m.showBelow(cb); };
    calcRow.append($('td', { cls: freezeCls(ci).trim(), attr: { 'data-col': c.key } }, cb));
  });
  calcRow.append($('td', { cls: 'cvsw-dt-fill cvsw-dt-footr' }, $('div', { cls: 'cvsw-dt-pager' }, pg.range, pg.prev, pg.next)));
  table.append(colgroup, $('thead', {}, tr), tbody, $('tfoot', {}, calcRow));
  const scroller = $('div', { cls: 'cvsw-dt-scroll' }, table);
  wrap.append($('div', { cls: 'cvsw-dt-card' }, scroller));
  body.append(wrap);
  scroller.addEventListener('scroll', () => { scroller.toggleClass('is-scrolled-x', scroller.scrollLeft > 0); const m = scrollMem.get(currentFolder) || {}; m.x = scroller.scrollLeft; m.y = scroller.scrollTop; scrollMem.set(currentFolder, m); }, { passive: true });
  // fill clip info and lengths as they arrive
  const need = files.filter((f) => { const ci = clipInfo.get(f.path); const nf = vault.getFileByPath(sidecarOf(f.path) || ''); return !ci || (nf && ci.rev !== nf.rev); });
  if (need.length) void Promise.all(need.map(loadClipInfo)).then(() => { if (!openPath && main.querySelector('.cvsw-dt')) renderLibrary(); });
}
/** After the table is in the page: where it was scrolled to, the frozen columns' places, ticks and the focus ring. */
function settleTable() {
  const sc = main.querySelector('.cvsw-dt-scroll'); const body = main.querySelector('.cvsw-lib-body'); const m = scrollMem.get(currentFolder);
  if (body) { if (m?.top) body.scrollTop = m.top; body.addEventListener('scroll', () => { const x = scrollMem.get(currentFolder) || {}; x.top = body.scrollTop; scrollMem.set(currentFolder, x); }, { passive: true }); }
  if (sc && m) { if (m.x) sc.scrollLeft = m.x; if (m.y) sc.scrollTop = m.y; sc.toggleClass('is-scrolled-x', sc.scrollLeft > 0); }
  fitColumns(); placeFrozen(); applyChecked(); paintCellFocus();
  popOpen?.place();
}
/** Frozen columns stay put while the rest scroll sideways: each sticks at the width of the ones before it. */
function placeFrozen() {
  const table = main.querySelector('.cvsw-dt-table'); if (!table) return;
  const cols = [...table.querySelectorAll('colgroup col')]; const lefts = []; let x = 0;
  for (const c of cols) { lefts.push(x); x += parseFloat(c.style.width) || 0; }
  for (const row of table.rows) { let col = 0; for (const cell of row.cells) { if (cell.classList.contains('is-frozen')) cell.style.left = `${lefts[col]}px`; col += cell.colSpan || 1; } } // by column, so a cell spanning two (the footer's Rows per page) keeps the rest in place
}
/** Fit a column to its widest cell on this page (its menu, or a double-click on its edge). */
/** Fit width (a header's menu, a double-click on its edge): the column goes back to fitting itself (Tony's width is dropped). */
function fitWidth(key) { editView((x) => { delete x.columns.widths[key]; }); }
// Fit widths (Tony, 2026-10-02): a column without a width of Tony's is as wide as its full header (title, sort mark,
// icon, padding) and its widest value on this page (min 56, max 360; Name at least 200), and the title is never cut,
// whatever the cap. Measured from copies of the header and the cells laid out at their natural width in a hidden
// table, after every render and again once the fonts are in. Kept per folder and view so a re-render starts right.
const fitCache = new Map();
function fitColumns() {
  const table = main.querySelector('.cvsw-dt-table:not(.cvsw-dt-measure)'); const sc = main.querySelector('.cvsw-dt-scroll'); if (!table || !sc) return;
  const v = curView(); const fkey = `${currentFolder}|${v.id}`;
  const auto = tableCols.filter((c) => !(Number(v.columns.widths[c.key]) > 0)); if (!auto.length) return;
  const m = $('table', { cls: 'cvsw-dt-table cvsw-dt-measure', attr: { 'aria-hidden': 'true' } }); const hr = $('tr'); const br = $('tr');
  for (const c of auto) {
    const th = $('th'); const src = table.querySelector(`thead th[data-col="${c.key}"] .cvsw-dt-th`); if (src) th.append(src.cloneNode(true)); hr.append(th);
    const td = $('td'); for (const x of table.querySelectorAll(`tbody td[data-col="${c.key}"] > *`)) td.append(x.cloneNode(true)); br.append(td);
  }
  m.append($('thead', {}, hr), $('tbody', {}, br)); sc.append(m);
  let changed = false;
  auto.forEach((c, i) => {
    const head = Math.ceil(hr.children[i].getBoundingClientRect().width) + 2; const w = Math.ceil(br.children[i].getBoundingClientRect().width) + 2;
    const fit = Math.max(head, c.key === 'name' ? Math.min(480, Math.max(200, w)) : Math.min(360, Math.max(56, w)));
    if (fitCache.get(`${fkey}|${c.key}`) !== fit) { fitCache.set(`${fkey}|${c.key}`, fit); changed = true; }
  });
  m.remove();
  if (!changed) return;
  const cols = table.querySelectorAll('colgroup col'); let total = CHECK_COL_W;
  tableCols.forEach((c, i) => { const w = Number(v.columns.widths[c.key]) > 0 ? Number(v.columns.widths[c.key]) : fitCache.get(`${fkey}|${c.key}`) || c.w; if (cols[i + 1]) cols[i + 1].style.width = `${w}px`; total += w; });
  table.style.minWidth = `${total}px`;
  placeFrozen();
}
if (document.fonts?.ready) void document.fonts.ready.then(() => { fitCache.clear(); if (!openPath && main.querySelector('.cvsw-dt-table')) fitColumns(); });
function resetView() {
  const cur = curView(); const before = clone(viewStore());
  editView((x) => { const d = baseView(x.layout); Object.assign(x, d, { id: x.id, name: x.name, layout: x.layout, columns: { order: LIB_COLS.map((c) => c.key), shown: ['name', 'clips', 'tags', 'length', 'size', 'modified'], widths: {}, wrap: [] }, sorts: [{ key: 'modified', desc: true }], rowsPerPage: 0 }); });
  libPage = 0;
  viewUndo(`"${cur.name}" is back to how it started.`, before);
}
/** A toast with Undo for a change to the views (delete, reset): puts the whole set back. */
function viewUndo(text, before) {
  const undo = () => { const st = viewStore(); st.defaults = before.defaults; st.folders = before.folders; saveViews(); if (!openPath) renderLibrary(); };
  pushUndo(text, undo);
  new Notice(createFragment((fr) => { fr.appendText(`${text} `); const b = fr.createEl('button', { text: 'Undo', cls: 'cvs-notice-btn' }); b.onclick = () => { if (b.disabled) return; b.disabled = true; dropUndo(undo); undo(); }; }), 4000, { routine: !/^Deleted/.test(text) }); // a deleted view keeps its toast
}

// ---- view tabs (where Table | Gallery used to be) ------------------------------------------------------
let tabRenaming = null; // the view id being named in place
function viewTabs() {
  const views = folderViews(currentFolder); const cur = curView();
  const seg = $('div', { cls: 'cvsw-seg cvsw-vtabs-list', attr: { role: 'tablist', 'aria-label': 'Views', 'data-sort-list': 'views' } });
  for (const v of views) {
    const on = v.id === cur.id;
    const b = $('button', { cls: `cvsw-seg-btn cvsw-vtab${on ? ' is-on' : ''}`, attr: { type: 'button', role: 'tab', 'aria-selected': String(on), 'data-sort-item': v.id, 'data-view': v.id } }, icon(v.layout === 'gallery' ? 'layout-grid' : 'table-2'), $('span', { cls: 'cvsw-vtab-name', text: v.name }));
    b.onclick = (e) => { if (tabRenaming) return; if (on) tabMenu(e, v, b); else pickView(v.id); };
    b.oncontextmenu = (e) => { e.preventDefault(); if (!on) { pickView(v.id); const nb = main.querySelector(`.cvsw-vtab[data-view="${v.id}"]`); if (nb) tabMenu(e, v, nb); } else tabMenu(e, v, b); };
    seg.append(b);
  }
  HOST.internals?.makeSortable?.(seg, { onDrop: ({ fromIndex, toIndex }) => { const views2 = ownViews(currentFolder); const [m] = views2.splice(fromIndex, 1); views2.splice(toIndex, 0, m); saveViews(); renderLibrary(); } });
  const add = $('button', { cls: 'cvsw-vtab-add', attr: { type: 'button', 'aria-label': 'New view' } }, icon('plus'));
  add.onclick = () => addView(cur);
  const box = $('div', { cls: 'cvsw-vtabs' }, seg, add);
  if (tabRenaming) requestAnimationFrame(() => startTabRename(tabRenaming));
  return box;
}
function uniqueViewName(views, want) { const names = new Set(views.map((v) => v.name.toLowerCase())); if (!names.has(want.toLowerCase())) return want; const stem = want.replace(/\s+\d+$/, ''); for (let n = 2; ; n++) if (!names.has(`${stem} ${n}`.toLowerCase())) return `${stem} ${n}`; }
/** A new view: a copy of the current one, named in place. */
function addView(from, after = false) {
  const views = ownViews(currentFolder);
  const nv = { ...clone(from), id: newId(), name: uniqueViewName(views, from.name) };
  const at = after ? views.findIndex((v) => v.id === from.id) + 1 : views.length;
  views.splice(at || views.length, 0, nv); saveViews();
  tabRenaming = nv.id; pickView(nv.id);
}
function startTabRename(id) {
  const b = main.querySelector(`.cvsw-vtab[data-view="${id}"]`); const span = b?.querySelector('.cvsw-vtab-name');
  if (!span) { tabRenaming = null; return; }
  tabRenaming = id;
  const cur = span.textContent;
  const input = $('input', { cls: 'cvsw-vtab-input', type: 'text', attr: { spellcheck: 'false', autocomplete: 'off', 'aria-label': 'View name' } }); input.value = cur;
  const size = () => { input.style.width = `${Math.max(3, input.value.length + 1)}ch`; };
  span.replaceWith(input); size(); input.focus(); input.select();
  let done = false;
  const finish = (ok) => {
    if (done) return; done = true; tabRenaming = null;
    const want = input.value.replace(/\s+/g, ' ').trim();
    if (ok && want && want !== cur) { const views = ownViews(currentFolder); const v = views.find((x) => x.id === id); if (v) { v.name = want; saveViews(); } }
    renderLibrary();
  };
  input.addEventListener('input', size);
  input.addEventListener('click', (e) => e.stopPropagation());
  input.addEventListener('pointerdown', (e) => e.stopPropagation());
  input.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); finish(true); } else if (e.key === 'Escape') { e.preventDefault(); finish(false); } });
  input.addEventListener('blur', () => finish(true));
}
function tabMenu(e, v, anchor) {
  const views = folderViews(currentFolder); const i = views.findIndex((x) => x.id === v.id);
  const m = new Menu();
  m.addItem((x) => x.setTitle('Rename').setIcon('pencil').onClick(() => startTabRename(v.id)));
  m.addItem((x) => x.setTitle('Duplicate').setIcon('copy').onClick(() => addView(v, true)));
  m.addSeparator(); m.addLabel('Layout');
  for (const [l, t, ic] of [['table', 'Table', 'table-2'], ['gallery', 'Gallery', 'layout-grid']]) m.addItem((x) => x.setTitle(t).setIcon(ic).setChecked(v.layout === l).onClick(() => { if (v.layout !== l) { closePop(); editView((y) => { y.layout = l; }); } }));
  m.addSeparator();
  const mv = (d) => { const own = ownViews(currentFolder); const a = own.findIndex((x) => x.id === v.id); const b = a + d; if (b < 0 || b >= own.length) return; [own[a], own[b]] = [own[b], own[a]]; saveViews(); renderLibrary(); };
  m.addItem((x) => x.setTitle('Move left').setIcon('chevron-left').setDisabled(i <= 0).onClick(() => mv(-1)));
  m.addItem((x) => x.setTitle('Move right').setIcon('chevron-right').setDisabled(i >= views.length - 1).onClick(() => mv(1)));
  m.addSeparator();
  m.addItem((x) => x.setTitle('Use for all folders').setIcon('copy-check').onClick(() => { const before = clone(viewStore()); viewStore().defaults = clone(folderViews(currentFolder)); saveViews(); viewUndo('Folders without their own views now start with these.', before); }));
  m.addItem((x) => x.setTitle('Reset to default').setIcon('rotate-ccw').setDisabled(!hasOwnViews(currentFolder)).onClick(() => { const before = clone(viewStore()); delete viewStore().folders[viewKey(currentFolder)]; saveViews(); libPage = 0; renderLibrary(); viewUndo('This folder shows the default views again.', before); }));
  m.addSeparator();
  m.addItem((x) => x.setTitle('Delete').setIcon('trash-2').setWarning(true).setDisabled(views.length <= 1).onClick(() => { const before = clone(viewStore()); const own = ownViews(currentFolder); const a = own.findIndex((y) => y.id === v.id); if (a < 0 || own.length <= 1) return; own.splice(a, 1); saveViews(); pickView(own[Math.max(0, a - 1)].id); viewUndo(`Deleted the view "${v.name}".`, before); }));
  if (e && e.type === 'contextmenu') m.showAtMouseEvent(e); else m.showBelow(anchor);
}

// ---- ticked rows: the floating bulk bar, ranges, the rubber band, keys -------------------------------------
// The bar floats over the toolbar row (it never pushes the table down): N selected, then Tags, Rename,
// Move, Duplicate, Delete, Clear. It is one element kept across re-renders, so it only fades in once.
const dtBulk = $('div', { cls: 'cvsw-dt-bulkbar', attr: { role: 'toolbar', 'aria-label': 'Selected videos', 'aria-hidden': 'true' } });
function renderDtBulk() {
  const n = libChecked.size;
  if (!n) { dtBulk.classList.remove('is-on'); dtBulk.setAttribute('aria-hidden', 'true'); return; }
  const b = (label, ic, fn) => { const x = $('button', { cls: 'cvsw-bulk-btn', attr: { type: 'button', 'aria-label': label } }, icon(ic)); x.onclick = (e) => { e.stopPropagation(); fn(x); }; return x; };
  dtBulk.replaceChildren($('span', { cls: 'cvsw-dt-bulkn', text: `${n} selected` }), $('span', { cls: 'cvsw-dt-bulksep' }),
    b('Tags', 'tag', (x) => openBulkTags(x)),
    ...(web.statusDots === false ? [] : [b('Status', 'circle-dot', (x) => statusMenu(null, checkedFiles(), x))]),
    b('Rename', 'pencil-line', () => { const items = checkedFiles(); if (items.length) new BulkRename(items).open(); }),
    b('Move', 'folder-input', () => { const items = checkedFiles(); if (items.length) moveManyPicker(items); }),
    b('Duplicate', 'copy-plus', () => void duplicateChecked()),
    b('Delete', 'trash-2', () => { const items = checkedFiles(); if (items.length) confirmDeleteMany(items); }),
    b('Clear (esc)', 'x', () => { libChecked.clear(); applyChecked(); }));
  dtBulk.removeAttribute('aria-hidden');
  if (!dtBulk.classList.contains('is-on')) { void dtBulk.offsetWidth; dtBulk.classList.add('is-on'); }
}
/** Show what is ticked without rebuilding the table: row fills, boxes, the header box, the bar. */
function applyChecked() {
  const table = main.querySelector('.cvsw-dt-table');
  if (table) {
    for (const r of table.querySelectorAll('tbody tr[data-vp]')) { const on = libChecked.has(r.dataset.vp); r.classList.toggle('is-checked', on); r.setAttribute('aria-selected', String(on)); const box = r.querySelector('.cvsw-dt-check'); if (box) box.checked = on; }
    const head = table.querySelector('thead .cvsw-dt-check');
    if (head) { const all = tablePage.length > 0 && tablePage.every((f) => libChecked.has(f.path)); head.checked = all; head.indeterminate = !all && tablePage.some((f) => libChecked.has(f.path)); }
  }
  renderDtBulk();
}
function checkRow(vp, on, range) {
  const order = tablePage.map((f) => f.path);
  if (range && checkAnchor && order.includes(checkAnchor)) { const a = order.indexOf(checkAnchor); const b = order.indexOf(vp); for (const p of order.slice(Math.min(a, b), Math.max(a, b) + 1)) { if (on) libChecked.add(p); else libChecked.delete(p); } }
  else if (on) libChecked.add(vp); else libChecked.delete(vp);
  checkAnchor = vp; applyChecked();
}
async function duplicateChecked() {
  const items = checkedFiles(); if (!items.length) return;
  const taken = new Set(); const made = []; const errors = [];
  for (const f of items) { try { made.push(await copyItem(f, taken)); } catch (e) { errors.push(`${f.name}: ${e.message || e}`); } }
  await refresh({ quiet: true });
  if (made.length) { libChecked.clear(); for (const p of made) libChecked.add(p); }
  if (!openPath) renderLibrary();
  bulkToast(errors, items.length, 'duplicated', `Duplicated ${plural(made.length, 'video')}`);
}
/** A click on a row: its thumbnail (the quick look), a Tags or Clips cell (its editor), or anywhere else (the video). */
function rowClick(e, f) {
  const td = e.target.closest('td[data-col]'); const col = td?.dataset.col; const vp = f.path;
  if (e.target.closest('.cvsw-td-pic') && web.quickLook !== false) { tableHot = true; quickLook(f, e.target.closest('.cvsw-td-pic')); return; }
  if (col === 'tags') { openTagEditor(td, f, { anchorSel: `.cvsw-dt-table tr[data-vp="${CSS.escape(vp)}"] td[data-col="tags"]` }); return; }
  if (col === 'clips') { openClipsEditor(td, f, { anchorSel: `.cvsw-dt-table tr[data-vp="${CSS.escape(vp)}"] td[data-col="clips"]` }); return; }
  if (col === 'status') { statusMenu(null, [f], td.querySelector('.cvsw-sdot-btn') || td); return; }
  go('v', vp);
}

// ---- editing in the table: tags, clips, the quick look (Tony, 2026-10-02; CTH Videos only) ----------------
// A video's own tags live in its clips note's frontmatter (`tags:`, words without #); its clips are the
// note's lines. Both are edited here through the player's own note code (HOST.internals), never by
// rewriting what is not ours: the frontmatter keeps every other key, and a clips note that is not
// format 2 yet is shown read-only until the player migrates it.
// "DZ Breakout, good #97 Avery" is dz-breakout, good, 97-avery: tags split on # or commas, spaces inside one become hyphens (the player's rule)
const tagWords = (text) => { const out = []; for (const part of String(text || '').split(/,|(?=#)/)) { const t = part.trim().replace(/^#+/, '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-_/]/g, '').replace(/-{2,}/g, '-').replace(/^-+|-+$/g, ''); if (t && !out.includes(t)) out.push(t); } return out; };
/** The note's text with its `tags:` set (or removed when empty); a note is made for a video that has none. */
function withFileTags(text, tags, videoVp) {
  const block = tags.length ? ['tags:', ...tags.map((t) => `  - ${t}`)] : [];
  const m = /^---\r?\n([\s\S]*?)\r?\n---(\r?\n|$)/.exec(text || '');
  if (!m) return `---\nvideo: "${videoVp}"\ntype: video-clips\n${text ? '' : 'format: 2\n'}${block.map((l) => `${l}\n`).join('')}---\n${text ? (text.startsWith('\n') ? '' : '\n') + text : '\n'}`;
  const lines = m[1].split(/\r?\n/); const out = [];
  for (let i = 0; i < lines.length; i++) {
    if (/^tags\s*:/.test(lines[i])) { while (i + 1 < lines.length && /^(\s+\S|-\s)/.test(lines[i + 1])) i++; continue; } // the old list, however it was written
    out.push(lines[i]);
  }
  return `---\n${[...out, ...block].join('\n')}\n---${m[2]}${text.slice(m[0].length)}`;
}
const noteWrites = new Map(); // note path -> the write in flight (edits to one note go one after another)
function noteQueue(np, fn) { const run = (noteWrites.get(np) || Promise.resolve()).catch(() => {}).then(fn); noteWrites.set(np, run); run.finally(() => { if (noteWrites.get(np) === run) noteWrites.delete(np); }); return run; }
async function writeFileTags(f, tags) {
  const np = sidecarOf(f.path); if (!np) throw new Error('The clips note has no place yet.');
  tags = [...new Set(tags)];
  await noteQueue(np, async () => {
    const nf = vault.getFileByPath(np);
    if (!nf) { if (tags.length) await vault.create(np, withFileTags('', tags, f.path)); }
    else { const text = await vault.read(nf); const next = withFileTags(text, tags, f.path); if (next !== text) await vault.modify(nf, next); }
  });
  const ci = clipInfo.get(f.path) || { ...NO_INFO };
  clipInfo.set(f.path, { ...ci, ftags: tags, rev: vault.getFileByPath(np)?.rev || '' });
}
/** Every tag in the library with how many videos carry it (own and clips' tags). Notes not read yet are read now. */
let allInfoRun = null;
function loadAllClipInfo() {
  if (allInfoRun) return allInfoRun;
  const todo = vault.getFiles().filter((f) => isVideo(f) && !clipInfo.has(f.path));
  allInfoRun = (async () => { const w = async () => { while (todo.length) await loadClipInfo(todo.shift()); }; await Promise.all([w(), w(), w(), w()]); })().finally(() => { allInfoRun = null; });
  return allInfoRun;
}
/** Notes that changed (or were never read): read them again, then redraw the dots. */
let infoRefreshing = false;
async function refreshClipInfo() {
  if (infoRefreshing || !plugin?.settings) return; infoRefreshing = true;
  try {
    const stale = vault.getFiles().filter((f) => { if (!isVideo(f)) return false; const ci = clipInfo.get(f.path); const nf = vault.getFileByPath(sidecarOf(f.path) || ''); return !ci || (nf ? ci.rev !== nf.rev : ci.rev !== ''); });
    if (!stale.length) return;
    const w = async () => { while (stale.length) await loadClipInfo(stale.shift()); }; await Promise.all([w(), w(), w(), w()]);
    paintStatuses();
  } finally { infoRefreshing = false; }
}
function libraryTags() {
  const m = new Map();
  for (const f of vault.getFiles()) if (isVideo(f)) for (const t of allTagsOf(f)) m.set(t, (m.get(t) || 0) + 1);
  return m;
}
/** Swap one cell's content in place (after an edit), without rebuilding the table. */
function refreshCell(vp, col) {
  const td = main.querySelector(`.cvsw-dt-table tbody tr[data-vp="${CSS.escape(vp)}"] td[data-col="${col}"]`); const f = vault.getFileByPath(vp);
  if (td && f) td.replaceChildren(libCell(f, col));
}

// The tag editor: chips with x, type to add (Enter), suggestions from every tag in the library, Backspace
// takes the last one. From a Tags cell, the tree's right-click "Tags", or the bulk bar (then it adds,
// removes or sets the tags on every ticked video).
function tagChips(tags, onRemove) { return tags.map((t) => { const x = $('span', { cls: 'cvsw-tchip' }, $('span', { text: `#${t}` }), onRemove ? (() => { const b = $('button', { cls: 'cvsw-tchip-x', attr: { type: 'button', 'aria-label': `Remove #${t}` } }, icon('x')); b.onclick = () => onRemove(t); return b; })() : null); return x; }); }
function openTagEditor(anchor, f, { anchorSel = null } = {}) {
  const key = `tags:${f.path}`; if (popJustShut(key)) return;
  const p = openPop(anchor, { key, cls: 'cvsw-tpop', anchorSel, label: `Tags for ${f.basename}` });
  let tags = [...(clipInfo.get(f.path)?.ftags || [])]; let q = ''; let hi = 0;
  const save = () => { refreshCell(f.path, 'tags'); void writeFileTags(f, tags).catch((e) => new Notice(`Could not save the tags: ${e.message || e}`)); };
  const input = $('input', { cls: 'cvsw-tpop-input', type: 'text', attr: { placeholder: tags.length ? 'Add a tag' : 'Add a tag, like #breakout', spellcheck: 'false', autocomplete: 'off', 'aria-label': 'Add a tag' } });
  const chips = $('div', { cls: 'cvsw-tpop-chips' }); const sugg = $('div', { cls: 'cvsw-tpop-sugg', attr: { role: 'listbox' } }); const onClips = $('div', { cls: 'cvsw-tpop-clips' });
  const add = (words) => { const w = tagWords(words).filter((x) => !tags.includes(x)); if (!w.length) return; tags = [...tags, ...w]; q = ''; input.value = ''; save(); draw(); };
  const remove = (t) => { tags = tags.filter((x) => x !== t); save(); draw(); };
  const suggestions = () => { const ql = q.trim().toLowerCase().replace(/^#/, ''); return [...libraryTags()].filter(([t]) => !tags.includes(t) && (!ql || t.includes(ql))).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 8); };
  const draw = () => {
    const had = document.activeElement === input; chips.replaceChildren(...tagChips(tags, remove), input); if (had) input.focus({ preventScroll: true });
    const list = suggestions(); hi = Math.max(0, Math.min(hi, list.length - 1));
    const typed = tagWords(q)[0];
    sugg.replaceChildren(...list.map(([t, n], i) => { const b = $('button', { cls: `cvsw-tpop-opt${i === hi ? ' is-hi' : ''}`, attr: { type: 'button', role: 'option', 'aria-selected': String(i === hi) } }, $('span', { cls: 'cvsw-tpop-optname', text: `#${t}` }), $('span', { cls: 'cvsw-fpop-n', text: String(n) })); b.onmousedown = (e) => e.preventDefault(); b.onclick = () => add(t); return b; }));
    if (typed && !list.some(([t]) => t === typed)) { const b = $('button', { cls: 'cvsw-tpop-opt is-new', attr: { type: 'button' } }, $('span', { cls: 'cvsw-tpop-optname', text: `Add #${typed}` })); b.onmousedown = (e) => e.preventDefault(); b.onclick = () => add(typed); sugg.append(b); }
    if (!sugg.childElementCount) sugg.append($('div', { cls: 'cvsw-fpop-none', text: libraryTags().size ? 'Every tag is on it.' : 'Type a tag and press Enter.' }));
    const ct = (clipInfo.get(f.path)?.tags || []).filter((t) => !tags.includes(t));
    onClips.replaceChildren(...(ct.length ? [$('div', { cls: 'cvsw-cpop-label', text: 'On its clips' }), $('div', { cls: 'cvsw-tpop-chips is-quiet' }, ...tagChips(ct, null))] : []));
    p.place();
  };
  input.oninput = () => { q = input.value; hi = 0; draw(); input.focus(); };
  input.onkeydown = (e) => {
    if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); const list = suggestions(); if (q.trim()) add(q); else if (list[hi]) add(list[hi][0]); }
    else if (e.key === 'Backspace' && !input.value && tags.length) { e.preventDefault(); remove(tags[tags.length - 1]); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); hi++; draw(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); hi--; draw(); }
    else if (e.key === 'Tab' && suggestions()[hi]) { e.preventDefault(); add(suggestions()[hi][0]); }
  };
  p.el.append($('div', { cls: 'cvsw-fpop-title' }, $('span', { text: 'Tags' }), $('span', { cls: 'cvsw-tpop-sub', text: f.basename })), chips, sugg, onClips);
  draw();
  setTimeout(() => input.focus({ preventScroll: true }), 0);
  void loadAllClipInfo().then(() => { if (popOpen === p) draw(); });
}
/** The bulk bar's Tags: add, remove or set tags on every ticked video, one toast with Undo. */
function openBulkTags(anchor) {
  const files = checkedFiles(); if (!files.length) return;
  if (popJustShut('bulktags')) return;
  const p = openPop(anchor, { key: 'bulktags', cls: 'cvsw-tpop', anchorSel: '.cvsw-dt-bulkbar .cvsw-bulk-btn[aria-label="Tags"]', label: `Tags for ${plural(files.length, 'video')}` });
  let mode = 'add'; let tags = []; let q = '';
  const input = $('input', { cls: 'cvsw-tpop-input', type: 'text', attr: { placeholder: 'Type a tag, Enter adds', spellcheck: 'false', autocomplete: 'off', 'aria-label': 'Tag' } });
  const chips = $('div', { cls: 'cvsw-tpop-chips' }); const sugg = $('div', { cls: 'cvsw-tpop-sugg', attr: { role: 'listbox' } });
  const seg = $('div', { cls: 'cvsw-seg cvsw-fpop-modes', attr: { role: 'radiogroup', 'aria-label': 'What to do' } });
  const apply = $('button', { cls: 'cvsw-tpop-apply', attr: { type: 'button' } });
  const draw = () => {
    seg.querySelectorAll('.cvsw-seg-btn').forEach((b) => { b.classList.toggle('is-on', b.dataset.v === mode); b.setAttribute('aria-checked', String(b.dataset.v === mode)); });
    const had = document.activeElement === input; chips.replaceChildren(...tagChips(tags, (t) => { tags = tags.filter((x) => x !== t); draw(); }), input); if (had) input.focus({ preventScroll: true });
    const ql = q.trim().toLowerCase().replace(/^#/, ''); const typed = tagWords(q)[0];
    const list = [...libraryTags()].filter(([t]) => !tags.includes(t) && (!ql || t.includes(ql))).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 6);
    sugg.replaceChildren(...list.map(([t, n]) => { const b = $('button', { cls: 'cvsw-tpop-opt', attr: { type: 'button', role: 'option' } }, $('span', { cls: 'cvsw-tpop-optname', text: `#${t}` }), $('span', { cls: 'cvsw-fpop-n', text: String(n) })); b.onmousedown = (e) => e.preventDefault(); b.onclick = () => { tags = [...tags, t]; q = ''; input.value = ''; draw(); input.focus(); }; return b; }));
    if (typed && !list.some(([t]) => t === typed)) { const b = $('button', { cls: 'cvsw-tpop-opt is-new', attr: { type: 'button' } }, $('span', { cls: 'cvsw-tpop-optname', text: `Add #${typed}` })); b.onmousedown = (e) => e.preventDefault(); b.onclick = () => { tags = [...tags, typed]; q = ''; input.value = ''; draw(); input.focus(); }; sugg.append(b); }
    apply.setText(mode === 'add' ? `Add to ${plural(files.length, 'video')}` : mode === 'remove' ? `Remove from ${plural(files.length, 'video')}` : `Set on ${plural(files.length, 'video')}`);
    apply.disabled = !tags.length && mode !== 'set';
    p.place();
  };
  for (const [v, t] of [['add', 'Add'], ['remove', 'Remove'], ['set', 'Set']]) { const b = $('button', { cls: 'cvsw-seg-btn', attr: { type: 'button', role: 'radio', 'data-v': v } }, $('span', { text: t })); b.onclick = () => { mode = v; draw(); }; seg.append(b); }
  input.oninput = () => { q = input.value; draw(); };
  input.onkeydown = (e) => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); if (q.trim()) { for (const w of tagWords(q)) if (!tags.includes(w)) tags.push(w); q = ''; input.value = ''; draw(); } else void run(); } else if (e.key === 'Backspace' && !input.value && tags.length) { e.preventDefault(); tags.pop(); draw(); } };
  const run = async () => {
    if (apply.disabled) return;
    const before = files.map((f) => [f, [...(clipInfo.get(f.path)?.ftags || [])]]);
    const after = before.map(([f, was]) => [f, mode === 'add' ? [...new Set([...was, ...tags])] : mode === 'remove' ? was.filter((t) => !tags.includes(t)) : [...tags]]);
    closePop(p);
    const errors = []; let n = 0;
    for (const [f, next] of after) { if (next.join() === (clipInfo.get(f.path)?.ftags || []).join()) continue; try { await writeFileTags(f, next); n++; } catch (e) { errors.push(`${f.basename}: ${e.message || e}`); } }
    if (!openPath) renderLibrary();
    const undo = async () => { for (const [f, was] of before) { try { await writeFileTags(f, was); } catch (_) { /* told below */ } } if (!openPath) renderLibrary(); new Notice('Tags put back.'); };
    bulkToast(errors, after.length, 'tagged', n ? createFragment((fr) => { fr.appendText(`${mode === 'remove' ? 'Untagged' : 'Tagged'} ${plural(n, 'video')}. `); const b = fr.createEl('button', { text: 'Undo', cls: 'cvs-notice-btn' }); b.onclick = () => { if (b.disabled) return; b.disabled = true; void undo(); }; }) : 'Nothing to change.');
  };
  apply.onclick = () => void run();
  p.el.append($('div', { cls: 'cvsw-fpop-title' }, $('span', { text: 'Tags' }), $('span', { cls: 'cvsw-tpop-sub', text: plural(files.length, 'video') })), seg, chips, sugg, $('div', { cls: 'cvsw-tpop-foot' }, apply));
  draw();
  setTimeout(() => input.focus({ preventScroll: true }), 0);
  void loadAllClipInfo().then(() => { if (popOpen === p) draw(); });
}

// The clips popover: the note's clips (name, Start, In, Out, tags), each renamed, re-timed or re-tagged by
// typing, or deleted, without opening the player. "Open in the editor" at the bottom.
function openClipsEditor(anchor, f, { anchorSel = null } = {}) {
  const key = `clips:${f.path}`; if (popJustShut(key)) return;
  const I = HOST.internals || {};
  const p = openPop(anchor, { key, cls: 'cvsw-kpop', anchorSel, label: `Clips of ${f.basename}` });
  const np = sidecarOf(f.path); let nf = np && vault.getFileByPath(np);
  let text = ''; let clips = []; let legacy = false; let busy = false;
  const dec = Math.max(0, Math.min(3, Number(plugin?.settings?.sidebarDecimals ?? 1)));
  const fmt = (t) => (t == null ? '' : I.fmtTag ? I.fmtTag(t, dec) : String(t));
  const parse = (s) => { const v = I.parseTagTime ? I.parseTagTime(s) : Number(s); return v == null || !Number.isFinite(v) ? null : v; };
  const write = async () => {
    if (!np || !I.serializeTagsNote) return;
    const body = I.serializeTagsNote(f.path, clips, text, clips.orphans || []);
    await noteQueue(np, async () => { nf = vault.getFileByPath(np); if (nf) await vault.modify(nf, body); else if (clips.length) await vault.create(np, body); });
    text = body;
    const ci = clipInfo.get(f.path) || { ...NO_INFO }; const tags = new Set(); for (const c of clips) for (const t of c.tags || []) tags.add(t);
    clipInfo.set(f.path, { ...ci, clips: clips.length, tags: [...tags].sort(), rev: vault.getFileByPath(np)?.rev || '' });
    refreshCell(f.path, 'clips'); refreshCell(f.path, 'tags');
  };
  const field = (cls, value, onDone, label, mono = false) => {
    const i = $('input', { cls: `cvsw-kpop-in ${cls}${mono ? ' is-mono' : ''}`, type: 'text', attr: { spellcheck: 'false', autocomplete: 'off', 'aria-label': label } }); i.value = value;
    if (legacy) i.readOnly = true;
    const done = () => { if (i.value === value || legacy) return; const ok = onDone(i.value); if (ok === false) { i.value = value; } else { value = i.value; void write().then(draw); } };
    i.onchange = done;
    i.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); i.blur(); } else if (e.key === 'Escape') { if (i.value !== value) { e.stopPropagation(); i.value = value; i.blur(); } } };
    return i;
  };
  const draw = () => {
    const sorted = [...clips].sort((a, b) => a.t - b.t);
    const rows = sorted.map((c) => {
      const del = $('button', { cls: 'cvsw-kpop-del', attr: { type: 'button', 'aria-label': `Delete ${c.title || 'this clip'}` } }, icon('trash-2'));
      del.disabled = legacy;
      del.onclick = () => { const before = text; clips = clips.filter((x) => x !== c); void write().then(() => { draw(); new Notice(createFragment((fr) => { fr.appendText(`Deleted the clip ${c.title ? `"${c.title}"` : ''}. `); const b = fr.createEl('button', { text: 'Undo', cls: 'cvs-notice-btn' }); b.onclick = () => { if (b.disabled) return; b.disabled = true; void noteQueue(np, async () => { nf = vault.getFileByPath(np); if (nf) await vault.modify(nf, before); else await vault.create(np, before); }).then(() => { text = before; clips = I.parseTagsNote ? I.parseTagsNote(before) : []; const ci = clipInfo.get(f.path) || { ...NO_INFO }; clipInfo.set(f.path, { ...ci, clips: clips.length, rev: vault.getFileByPath(np)?.rev || '' }); refreshCell(f.path, 'clips'); if (popOpen === p) draw(); }); }; }), 8000); }); };
      return $('div', { cls: 'cvsw-kpop-row' },
        field('cvsw-kpop-name', c.title || '', (v) => { const t = v.trim(); if (!t) return false; c.title = t; }, 'Clip name'),
        field('cvsw-kpop-time', fmt(c.t), (v) => { const t = parse(v); if (t == null) return false; c.t = t; }, 'Start', true),
        field('cvsw-kpop-time', fmt(c.in), (v) => { if (!v.trim()) { c.in = null; return; } const t = parse(v); if (t == null) return false; c.in = t; }, 'In', true),
        field('cvsw-kpop-time', fmt(c.out), (v) => { if (!v.trim()) { c.out = null; return; } const t = parse(v); if (t == null) return false; c.out = t; }, 'Out', true),
        field('cvsw-kpop-tags', (c.tags || []).map((t) => `#${t}`).join(' '), (v) => { c.tags = tagWords(v); }, 'Tags'),
        del);
    });
    const open = $('button', { cls: 'cvsw-kpop-open', attr: { type: 'button' } }, icon('circle-play'), $('span', { text: 'Open in the editor' })); open.onclick = () => { closePop(p); go('v', f.path); };
    p.el.replaceChildren(...[
      $('div', { cls: 'cvsw-fpop-title' }, $('span', { text: `Clips` }), $('span', { cls: 'cvsw-tpop-sub', text: `${f.basename} · ${plural(clips.length, 'clip')}` })),
      legacy ? $('div', { cls: 'cvsw-kpop-hint', text: 'This note is in the old format. Open the video once and it is brought up to date; then its clips can be edited here.' }) : null,
      rows.length ? $('div', { cls: 'cvsw-kpop-head' }, $('span', { text: 'Clip' }), $('span', { text: 'Start' }), $('span', { text: 'In' }), $('span', { text: 'Out' }), $('span', { text: 'Tags' }), $('span')) : null,
      rows.length ? $('div', { cls: 'cvsw-kpop-list' }, ...rows) : $('div', { cls: 'cvsw-fpop-none', text: busy ? 'Reading...' : 'No clips yet. Open the video and press T to add one.' }),
      $('div', { cls: 'cvsw-kpop-foot' }, open)].filter(Boolean));
    p.place();
  };
  busy = true; draw();
  (async () => {
    if (nf) { try { text = await vault.read(nf); clips = I.parseTagsNote ? I.parseTagsNote(text) : []; legacy = !!clips.migrated && clips.length > 0; } catch (_) { text = ''; clips = []; } }
    busy = false; if (popOpen === p) draw();
  })();
}

// The quick look (a thumbnail click): a small popup player anchored to the thumbnail, 480px wide, 16:9,
// with a play / pause button, a timeline with the time, and trackpad scrubbing (a two-finger swipe over
// it, the same feel as the main player's scrub). Space plays or pauses, Escape or a click outside closes.
function quickLook(f, anchor) {
  const key = `ql:${f.path}`; if (popJustShut(key)) return;
  const p = openPop(anchor, { key, cls: 'cvsw-ql', anchorSel: `.cvsw-td-pic[data-vp="${CSS.escape(f.path)}"]`, label: `Quick look: ${f.basename}` });
  const v = $('video', { cls: 'cvsw-ql-video', attr: { playsinline: '', preload: 'auto' } });
  const play = $('button', { cls: 'cvsw-ql-play', attr: { type: 'button', 'aria-label': 'Play (space)' } }, icon('play'));
  const line = $('div', { cls: 'cvsw-ql-line', attr: { role: 'slider', 'aria-label': 'Time', tabindex: '-1' } }, $('div', { cls: 'cvsw-ql-fill' }), $('div', { cls: 'cvsw-ql-knob' }));
  const time = $('span', { cls: 'cvsw-ql-time', text: '0:00 / 0:00' });
  const stage = $('div', { cls: 'cvsw-ql-stage' }, v, $('div', { cls: 'cvsw-ql-name' }, statusDotButton(f, { cls: 'cvsw-ql-dot' }), $('span', { text: f.basename })));
  p.el.append(stage, $('div', { cls: 'cvsw-ql-bar' }, play, line, time));
  const s = plugin?.settings || {};
  v.volume = Math.max(0, Math.min(1, Number(s.volume ?? 1)));
  const paint = () => { const d = v.duration || 0; const t = v.currentTime || 0; line.querySelector('.cvsw-ql-fill').style.width = `${d ? (t / d) * 100 : 0}%`; line.querySelector('.cvsw-ql-knob').style.left = `${d ? (t / d) * 100 : 0}%`; time.setText(`${fmtLen(t)} / ${fmtLen(d)}`); line.setAttribute('aria-valuenow', String(Math.round(t))); line.setAttribute('aria-valuemax', String(Math.round(d))); };
  const syncPlay = () => { play.replaceChildren(icon(v.paused ? 'play' : 'pause')); play.setAttribute('aria-label', v.paused ? 'Play (space)' : 'Pause (space)'); };
  let raf = 0; const loop = () => { paint(); raf = requestAnimationFrame(loop); };
  v.addEventListener('loadedmetadata', () => { paint(); if (web.quickLookPlay !== false) v.play().catch(() => {}); });
  v.addEventListener('play', syncPlay); v.addEventListener('pause', syncPlay); v.addEventListener('ended', syncPlay);
  play.onclick = () => { if (v.paused) v.play().catch(() => {}); else v.pause(); };
  stage.onclick = () => play.onclick();
  // the timeline: click or drag to seek
  const seekAt = (x) => { const r = line.getBoundingClientRect(); if (v.duration) v.currentTime = Math.max(0, Math.min(1, (x - r.left) / r.width)) * v.duration; paint(); };
  line.addEventListener('pointerdown', (e) => { e.preventDefault(); try { line.setPointerCapture(e.pointerId); } catch (_) { /* a made-up pointer */ } seekAt(e.clientX); const mv = (ev) => seekAt(ev.clientX); line.addEventListener('pointermove', mv); line.addEventListener('pointerup', () => line.removeEventListener('pointermove', mv), { once: true }); });
  // trackpad scrub: a two-finger horizontal swipe over the popup, the main player's own curve (deltaSeconds)
  let aim = null; let last = 0; let idle = 0; let seekRaf = 0;
  p.el.addEventListener('wheel', (e) => {
    if (e.ctrlKey || e.metaKey) return;
    const ax = Math.abs(e.deltaX); const ay = Math.abs(e.deltaY);
    const delta = s.axis === 'both' ? (ax >= ay ? e.deltaX : e.deltaY) : (ax > ay ? e.deltaX : 0);
    if (!delta || !v.duration) return;
    e.preventDefault(); e.stopPropagation();
    const now = performance.now(); const dt = last ? now - last : 8; last = now;
    const fine = s.fineModifier === 'shift' ? e.shiftKey : s.fineModifier === 'alt' ? e.altKey : false;
    const step = HOST.internals?.deltaSeconds ? HOST.internals.deltaSeconds(delta, dt, s.sensitivity || 1, fine) : delta / 100;
    if (!v.paused) v.pause();
    aim = Math.max(0, Math.min(v.duration, (aim == null ? v.currentTime : aim) + (s.reverse ? 1 : -1) * step));
    if (!seekRaf) seekRaf = requestAnimationFrame(() => { seekRaf = 0; if (aim != null && !v.seeking) { v.currentTime = aim; paint(); } else if (aim != null) seekRaf = requestAnimationFrame(() => { seekRaf = 0; v.currentTime = aim; paint(); }); });
    clearTimeout(idle); idle = setTimeout(() => { aim = null; last = 0; }, 260);
  }, { passive: false });
  const keys = (e) => { if (popOpen !== p || e.target.closest?.('input, textarea')) return; if (e.key === ' ') { e.preventDefault(); e.stopPropagation(); play.onclick(); } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); if (v.duration) v.currentTime = Math.max(0, Math.min(v.duration, v.currentTime + (e.key === 'ArrowRight' ? 1 : -1))); paint(); } };
  addEventListener('keydown', keys, true);
  p.onClose = () => { removeEventListener('keydown', keys, true); cancelAnimationFrame(raf); clearTimeout(idle); try { v.pause(); } catch (_) { /* fine */ } v.removeAttribute('src'); try { v.load(); } catch (_) { /* fine */ } };
  raf = requestAnimationFrame(loop); syncPlay(); paint();
  p.place();
  cloud.tempLink(toCloud(f.path)).then((url) => { if (popOpen === p) v.src = url; }).catch((e) => { if (popOpen === p) stage.append($('div', { cls: 'cvsw-ql-err', text: `Could not load it: ${e.message || e}` })); });
}

// ---- status dots (Tony, 2026-10-02; one spec with CTH Docs) ---------------------------------------------
// A small dot beside each video: its status, from a set the folder defines (the workflow order: To-Do, WIP,
// Review, Live, Done to start). A video's status is `status: <name>` in its clips note's frontmatter (with
// its tags; it follows the video through renames and moves and syncs). A folder's set lives with its table
// views in the shared settings (settings.tableViews.statuses[<folder>] = { statuses: [{ id, name, color }],
// statusNew }, '' for the top level), re-keyed with the views when the folder moves. A video uses the set
// of the nearest folder above it that has one (its own first; Settings > Status can make the top-most
// folder win instead), else the top level's, else the built-in five.
const STATUS_COLORS = ['lightgray', 'gray', 'yellow', 'blue', 'green', 'red', 'black'];
const STATUS_COLOR_NAMES = { lightgray: 'Light grey', gray: 'Grey', yellow: 'Yellow', blue: 'Blue', green: 'Green', red: 'Red', black: 'Black' };
const BUILTIN_STATUSES = () => [{ id: 'todo', name: 'To-Do', color: 'lightgray', description: 'Not started' }, { id: 'wip', name: 'WIP', color: 'yellow', description: 'Being worked on' }, { id: 'review', name: 'Review', color: 'blue', description: 'Done, waiting on a check' }, { id: 'active', name: 'Active', color: 'green', description: 'Current and in use, not published or client-facing' }, { id: 'live', name: 'Live', color: 'green', description: 'Current, published and client-facing' }, { id: 'done', name: 'Done', color: 'black', description: 'Finished, nothing left to do' }];
/** Active (Tony, 2026-10-02) joins every stored set without it: before Live, else before the last status (the last stays "done"). On read; saved with the set's next save. */
const withActive = (list) => {
  if (!list.length || list.some((s) => s.name.toLowerCase() === 'active')) return list;
  const a = BUILTIN_STATUSES()[3]; const i = list.findIndex((s) => s.name.toLowerCase() === 'live'); const at = i < 0 ? Math.max(0, list.length - 1) : i;
  return list.length < 2 && i < 0 ? [...list, a] : [...list.slice(0, at), a, ...list.slice(at)];
};
const statusMap = () => { const st = viewStore(); if (!st.statuses || typeof st.statuses !== 'object' || Array.isArray(st.statuses)) st.statuses = {}; return st.statuses; };
const normStatusSet = (x) => {
  const list = withActive((Array.isArray(x?.statuses) ? x.statuses : []).map((s) => ({ id: String(s?.id || newId()), name: String(s?.name || '').replace(/\s+/g, ' ').trim(), color: STATUS_COLORS.includes(s?.color) ? s.color : 'gray', ...(s?.description ? { description: String(s.description).replace(/\s+/g, ' ').trim().slice(0, 120) } : {}) })).filter((s) => s.name));
  return { statuses: list, statusNew: typeof x?.statusNew === 'string' && list.some((s) => s.name === x.statusNew) ? x.statusNew : null };
};
const hasOwnStatuses = (folderVp) => { const x = statusMap()[viewKey(folderVp)]; return !!(x && Array.isArray(x.statuses) && x.statuses.length); };
/** The set a folder's videos use: { statuses, statusNew, from (the folder with the set, '' the top level, null built-in) }. */
function statusSetFor(folderVp) {
  const chain = []; let p = normVault(folderVp || VP); // own folder, then up to the top level
  while (p && p !== VP) { chain.push(p); p = parentOf(p); } chain.push(VP);
  const owned = chain.filter(hasOwnStatuses);
  const from = owned.length ? (web.statusInherit === 'top' ? owned[owned.length - 1] : owned[0]) : null;
  if (from == null) return { ...normStatusSet({ statuses: BUILTIN_STATUSES() }), from: null };
  return { ...normStatusSet(statusMap()[viewKey(from)]), from };
}
const setLabel = (from) => (from == null ? 'the built-in' : from === VP ? `${ROOT_NAME} (top level)` : baseName(from));
/** A set's default status: what videos without one (or with a name the set does not have) read as. */
const defaultStatus = (set) => set.statuses.find((s) => s.name === set.statusNew) || set.statuses[0] || BUILTIN_STATUSES()[0];
/**
 * A video's status (Tony, 2026-10-02: every video has one): { name (the status it reads as, in the set's spelling),
 * st (the set's entry), raw (what its note says, '' for no line), unknown (a name the set does not have), isDefault
 * (no line or unknown: the set's default) }. A missing or unknown value IS the default for every purpose.
 */
function videoStatus(f, set = statusSetFor(parentOf(f.path))) {
  const raw = clipInfo.get(f.path)?.status || '';
  const hit = raw ? set.statuses.find((s) => s.name.toLowerCase() === raw.toLowerCase()) || null : null;
  const st = hit || defaultStatus(set);
  return { name: st.name, st, raw, unknown: !!raw && !hit, isDefault: !hit };
}
const statusOrder = (f, set) => { const v = videoStatus(f, set); const i = set.statuses.indexOf(v.st); return i < 0 ? set.statuses.length : i; };
/** The status line of a note's frontmatter. */
function noteStatus(text) { const m = /^---\r?\n([\s\S]*?)\r?\n---/.exec(text || ''); if (!m) return ''; const l = m[1].split(/\r?\n/).find((x) => /^status\s*:/.test(x)); return l ? l.replace(/^status\s*:\s*/, '').trim().replace(/^(["'])(.*)\1$/, '$2').trim() : ''; }
function withFileStatus(text, name, videoVp) {
  const line = name ? [`status: ${/[:#"'\[\]{}]|^\s|\s$/.test(name) ? JSON.stringify(name) : name}`] : [];
  const m = /^---\r?\n([\s\S]*?)\r?\n---(\r?\n|$)/.exec(text || '');
  if (!m) return `---\nvideo: "${videoVp}"\ntype: video-clips\n${text ? '' : 'format: 2\n'}${line.map((l) => `${l}\n`).join('')}---\n${text ? (text.startsWith('\n') ? '' : '\n') + text : '\n'}`;
  const lines = m[1].split(/\r?\n/).filter((l) => !/^status\s*:/.test(l));
  return `---\n${[...lines, ...line].join('\n')}\n---${m[2]}${text.slice(m[0].length)}`;
}
async function writeFileStatus(f, name) {
  const np = sidecarOf(f.path); if (!np) throw new Error('The clips note has no place yet.');
  await noteQueue(np, async () => {
    const nf = vault.getFileByPath(np);
    if (!nf) { if (name) await vault.create(np, withFileStatus('', name, f.path)); }
    else { const text = await vault.read(nf); const next = withFileStatus(text, name, f.path); if (next !== text) await vault.modify(nf, next); }
  });
  const ci = clipInfo.get(f.path) || { ...NO_INFO };
  clipInfo.set(f.path, { ...ci, status: name || '', rev: vault.getFileByPath(np)?.rev || '' });
}
/**
 * Set a status on videos; name '' (clearing it) gives each its set's default. A video that already reads as that
 * status is left alone (a note without a status line is not rewritten to its default). Undo: Cmd+Z; a toast only
 * for 2+ videos (quiet confirmations).
 */
async function setStatuses(files, name, { toast = true } = {}) {
  const want = new Map(); for (const f of files) { const set = statusSetFor(parentOf(f.path)); const v = videoStatus(f, set); const to = name ? set.statuses.find((s) => s.name.toLowerCase() === name.toLowerCase())?.name : defaultStatus(set).name; if (!to || (!v.unknown && v.name === to)) continue; want.set(f, to); }
  const before = [...want.keys()].map((f) => [f, clipInfo.get(f.path)?.status || '']);
  const errors = []; let n = 0;
  for (const [f, to] of want) { try { await writeFileStatus(f, to); n++; } catch (e) { errors.push(`${f.basename}: ${e.message || e}`); } }
  paintStatuses();
  if (!toast || (!n && !errors.length)) return n;
  const undo = async () => { for (const [f, was] of before) { try { await writeFileStatus(f, was); } catch (_) { /* told below */ } } paintStatuses(); };
  const redo = async () => { for (const [f, to] of want) { try { await writeFileStatus(f, to); } catch (_) { /* fine */ } } paintStatuses(); };
  const label = [...new Set(want.values())].length === 1 ? [...want.values()][0] : 'Status';
  pushUndo(`${label}: ${plural(n, 'video')}`, undo, redo);
  bulkToast(errors, before.length, 'changed', n > 1 ? createFragment((fr) => { fr.appendText(`${label}: ${plural(n, 'video')}. `); const b = fr.createEl('button', { text: 'Undo', cls: 'cvs-notice-btn' }); b.onclick = () => { if (b.disabled) return; b.disabled = true; dropUndo(undo); void undo(); }; }) : '');
  return n;
}
/** Dots everywhere redraw without rebuilding: tree rows, table cells, cards, the top bar. */
function paintStatuses() {
  for (const r of tree.querySelectorAll('.cvsw-tree-list .cvsw-row[data-vp]')) { const f = vault.getFileByPath(r.dataset.vp); if (!f || !isVideo(f)) continue; const old = r.querySelector('.cvsw-sdot-btn'); const fresh = treeDot(f); if (old && fresh) old.replaceWith(fresh); else if (old) old.remove(); else if (fresh) { if (web.statusPos === 'after') r.querySelector('.cvsw-row-name')?.after(fresh); else r.querySelector('.cvsw-chev')?.after(fresh); } }
  paintProgress();
  for (const td of main.querySelectorAll('.cvsw-dt-table td[data-col="status"]')) { const f = vault.getFileByPath(td.parentElement.dataset.vp); if (f) td.replaceChildren(libCell(f, 'status')); }
  for (const c of main.querySelectorAll('.cvsw-card[data-vp]')) { const f = vault.getFileByPath(c.dataset.vp); const old = c.querySelector('.cvsw-sdot-btn'); const fresh = f && cardDot(f); if (old && fresh) old.replaceWith(fresh); else if (old) old.remove(); else if (fresh) c.querySelector('.cvsw-card-name')?.prepend(fresh); }
  if (openPath) { const f = vault.getFileByPath(openPath); const cur = bar.querySelector('.cvsw-crumb.is-current'); const old = bar.querySelector('.cvsw-crumb-dot'); const fresh = f && statusDotButton(f, { cls: 'cvsw-crumb-dot' }); if (old && fresh) old.replaceWith(fresh); else if (old) old.remove(); else if (fresh && cur) cur.before(fresh); }
}
/** The dot itself: 8px, solid, in its status color (every video has a status). */
function statusDot(v, { size = 8 } = {}) {
  const d = $('span', { cls: `cvsw-sdot is-${v.st ? v.st.color : 'gray'}`, attr: { 'aria-hidden': 'true' } });
  if (size !== 8) { d.style.width = `${size}px`; d.style.height = `${size}px`; }
  return d;
}
/** The 20px hit area around a dot, with the tooltip and the menu. */
function statusDotButton(f, { cls = '', size = 8 } = {}) {
  if (web.statusDots === false) return null;
  const set = statusSetFor(parentOf(f.path)); const v = videoStatus(f, set);
  const dot = statusDot(v, { size }); if (!dot) return null;
  const from = set.from == null ? '' : set.from === parentOf(f.path) ? '' : ` (from ${set.from === VP ? ROOT_NAME : baseName(set.from)})`;
  const label = v.unknown ? `${v.name}. Saved as “${v.raw}”, which is not one of this folder's statuses` : `${v.name}${from}${v.st.description ? `. ${v.st.description}` : ''}`;
  const b = $('button', { cls: `cvsw-sdot-btn ${cls}`.trim(), attr: { type: 'button', 'aria-label': label, title: label, 'aria-haspopup': 'menu', 'data-status': v.name, 'data-kind': v.unknown ? 'unknown' : v.isDefault ? 'default' : 'set', 'data-raw': v.raw } }, dot);
  b.onclick = (e) => { e.stopPropagation(); e.preventDefault(); statusMenu(e, [f], b); };
  b.ondblclick = (e) => e.stopPropagation(); b.onpointerdown = (e) => e.stopPropagation(); b.oncontextmenu = (e) => { e.preventDefault(); e.stopPropagation(); statusMenu(e, [f], b); };
  return b;
}
const treeDot = (f) => statusDotButton(f, { cls: 'cvsw-tree-dot' });
const cardDot = (f) => statusDotButton(f, { cls: 'cvsw-card-dot' });
/** The dot's menu: the set's statuses in order with their descriptions (a check on the current one), Edit statuses... Typing a first letter picks. */
function statusMenu(e, files, anchor) {
  const f0 = files[0]; const set = statusSetFor(parentOf(f0.path));
  const sets = [...new Map(files.map((f) => { const s = statusSetFor(parentOf(f.path)); return [s.from ?? 'builtin', s]; })).values()];
  const m = new Menu();
  const current = files.length === 1 && !videoStatus(f0, set).unknown ? videoStatus(f0, set).st : null;
  const items = sets.length === 1 ? set.statuses.map((s) => [s, set]) : sets.flatMap((s) => s.statuses.map((x) => [x, s])); // different sets: the union, each applied only where its set holds it
  for (const [s, from] of items) m.addItem((i) => i.setTitle(sets.length > 1 ? `${s.name} (${setLabel(from.from)})` : s.name).setChecked(current === s).onClick(() => void setStatuses(files.filter((f) => statusSetFor(parentOf(f.path)).statuses.some((y) => y.name === s.name)), s.name)));
  m.addSeparator();
  m.addItem((i) => i.setTitle('Edit statuses...').setIcon('settings-2').onClick(() => openStatusEditor(set.from == null ? VP : set.from)));
  // the dot items carry their colour
  for (const it of m.items) if (it && it.title) { const s = items.find(([x]) => x.name === it.title || it.title.startsWith(`${x.name} (`)); if (s) { it.dot = s[0].color; it.desc = s[0].description || ''; } }
  if (e && e.type === 'contextmenu') m.showAtMouseEvent(e); else m.showBelow(anchor);
  paintMenuDots();
}
/** Menu items that stand for a status show its dot in the icon column. */
function paintMenuDots() {
  const m = document.querySelector('.cvsw-fallback-menu'); if (!m) return;
  const menu = m.__menu; if (!menu) return;
  const rows = [...m.querySelectorAll('.menu-item')]; let k = 0;
  for (const it of menu.items) { if (!it || it.label != null) continue; const r = rows[k++]; if (it.dot && r) { const ic = r.querySelector('.menu-item-icon'); if (!ic) continue; ic.empty(); ic.append(statusDot({ st: { color: it.dot } })); if (it.desc) { r.setAttribute('title', it.desc); r.querySelector('.menu-item-title')?.setAttribute('data-desc', it.desc); /* a muted second line (CSS ::after: the title's text stays the name) */ } if (it.checked && !r.querySelector('.menu-item-check')) r.append($('span', { cls: 'menu-item-shortcut menu-item-check' }, icon('check'))); } }
}
/** Folder rows: a muted "3/10" of videos at the set's last status (Settings > Status > Folder progress). */
function paintProgress() {
  for (const r of tree.querySelectorAll('.cvsw-tree-list .cvsw-row.is-dir[data-vp]')) {
    r.querySelector('.cvsw-row-progress')?.remove();
    if (web.statusProgress !== 'count') continue;
    const d = vault.getFolderByPath(r.dataset.vp); if (!d) continue;
    const set = statusSetFor(d.path); const last = set.statuses[set.statuses.length - 1]; if (!last) continue;
    let n = 0; let done = 0; const walk = (x) => { for (const c of x.children) { if (c instanceof VFolder) walk(c); else if (isVideo(c)) { n++; if (videoStatus(c).name === last.name) done++; } } }; walk(d);
    if (n) r.querySelector('.cvsw-row-actions')?.before($('span', { cls: 'cvsw-row-progress', text: `${done}/${n}`, attr: { title: `${done} of ${n} ${last.name}` } }));
  }
}
/** The videos whose status comes from this folder's set (this folder and below, except what a sub-folder's own set covers). */
function videosUsingSet(folderVp) { return vault.getFiles().filter((f) => isVideo(f) && f.path.startsWith(`${folderVp === VP ? VP : folderVp}/`) && statusSetFor(parentOf(f.path)).from === folderVp); }

// The status editor: "Statuses for <folder>": customize or use the parent's; drag to reorder (the workflow
// order), the colour, the name, Default for new videos, delete; Add status. Renames and deletes rewrite the
// notes in scope with one toast and Undo.
function openStatusEditor(folderVp) { new StatusEditor(folderVp).open(); }
class StatusEditor extends Modal {
  constructor(folderVp) { super(app); this.vp = folderVp === VP ? VP : normVault(folderVp); }
  key() { return viewKey(this.vp); }
  own() { return hasOwnStatuses(this.vp); }
  set() { return this.own() ? normStatusSet(statusMap()[this.key()]) : statusSetFor(this.vp); }
  save(set) { statusMap()[this.key()] = { statuses: set.statuses, statusNew: set.statusNew }; saveViews(); paintStatuses(); if (!openPath && main.querySelector('.cvsw-dt')) renderLibrary(); }
  onOpen() {
    this.modalEl.addClass('cvsw-stmod');
    this.titleEl.setText(`Statuses for ${this.vp === VP ? `${ROOT_NAME} (top level)` : baseName(this.vp)}`);
    this.note = this.contentEl.createDiv({ cls: 'cvsw-stmod-note' });
    this.list = this.contentEl.createDiv({ cls: 'cvsw-stmod-list', attr: { 'data-sort-list': 'statuses', 'data-sort-axis': 'y' } });
    this.foot = this.contentEl.createDiv({ cls: 'cvsw-stmod-foot' });
    HOST.internals?.makeSortable?.(this.list, { onDrop: ({ fromIndex, toIndex }) => { const set = this.set(); const [m] = set.statuses.splice(fromIndex, 1); set.statuses.splice(toIndex, 0, m); this.save(set); this.draw(); } });
    this.draw();
  }
  draw() {
    const set = this.set(); const own = this.own();
    this.note.empty();
    if (!own) {
      const b = $('button', { text: 'Customize for this folder', attr: { type: 'button' } });
      b.onclick = () => { this.save(normStatusSet({ statuses: set.statuses.map((s) => ({ ...s })), statusNew: set.statusNew })); this.draw(); };
      this.note.append($('span', { text: `Using ${setLabel(set.from)} statuses.` }), b);
    } else {
      const parent = this.vp === VP ? null : statusSetFor(parentOf(this.vp));
      const b = $('button', { text: this.vp === VP ? 'Use the built-in statuses instead' : `Use ${setLabel(parent.from)} statuses instead`, attr: { type: 'button' } });
      b.onclick = () => {
        const next = this.vp === VP ? { statuses: BUILTIN_STATUSES() } : parent;
        const change = videosUsingSet(this.vp).filter((f) => { const v = videoStatus(f, set); const n2 = normStatusSet(next); const hit = v.raw ? n2.statuses.find((s) => s.name.toLowerCase() === v.raw.toLowerCase()) : null; return (hit || defaultStatus(n2)).name !== v.name; }).length;
        const go = () => { delete statusMap()[this.key()]; saveViews(); paintStatuses(); if (!openPath && main.querySelector('.cvsw-dt')) renderLibrary(); this.draw(); };
        if (!change) { go(); return; }
        const md = new Modal(app); md.titleEl.setText('Drop this folder\'s statuses?'); md.contentEl.createEl('p', { text: `${plural(change, 'video')} here will show a different dot: ${change === 1 ? 'its status is' : 'their statuses are'} not in the other set (the names stay on the videos).` });
        const row = md.contentEl.createDiv({ cls: 'modal-button-container' }); row.createEl('button', { text: 'Cancel' }).onclick = () => md.close(); row.createEl('button', { text: 'Drop them', cls: 'mod-warning' }).onclick = () => { md.close(); go(); }; md.open();
      };
      this.note.append($('span', { text: 'This folder has its own statuses.' }), b);
    }
    this.list.replaceChildren(...set.statuses.map((s) => this.row(s, set)));
    this.list.toggleClass('is-off', !own);
    const add = $('button', { attr: { type: 'button' } }, icon('plus'), $('span', { text: 'Add status' })); add.disabled = !own;
    add.onclick = () => { const names = new Set(set.statuses.map((x) => x.name.toLowerCase())); let name = 'New status'; for (let n = 2; names.has(name.toLowerCase()); n++) name = `New status ${n}`; set.statuses.push({ id: newId(), name, color: 'gray' }); this.save(set); this.draw(); requestAnimationFrame(() => { const i = this.list.lastElementChild?.querySelector('.cvsw-stmod-name'); i?.focus(); i?.select(); }); };
    this.foot.replaceChildren(add);
  }
  row(s, set) {
    const own = this.own();
    const grip = $('span', { cls: 'cvsw-stmod-grip', attr: { 'data-sort-handle': '', 'aria-hidden': 'true' } }, icon('grip-vertical'));
    const color = $('button', { cls: 'cvsw-stmod-color', attr: { type: 'button', 'aria-label': `Colour: ${STATUS_COLOR_NAMES[s.color]}`, 'aria-haspopup': 'menu' } }, statusDot({ st: s }, { size: 12 }));
    color.onclick = () => { const m = new Menu(); for (const c of STATUS_COLORS) m.addItem((i) => { i.setTitle(STATUS_COLOR_NAMES[c]).setChecked(s.color === c).onClick(() => { s.color = c; this.save(set); this.draw(); }); i.dot = c; }); m.showBelow(color); paintMenuDots(); };
    const name = $('input', { cls: 'cvsw-stmod-name', type: 'text', attr: { spellcheck: 'false', autocomplete: 'off', 'aria-label': 'Status name' } }); name.value = s.name;
    const commit = () => {
      const want = name.value.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
      if (!want || want === s.name) { name.value = s.name; return; }
      if (set.statuses.some((x) => x !== s && x.name.toLowerCase() === want.toLowerCase())) { new Notice(`There is already a status called "${want}" here.`); name.value = s.name; return; }
      const old = s.name; s.name = want; if (set.statusNew === old) set.statusNew = want; this.save(set);
      // every video using this set with the old name gets the new one
      const hit = videosUsingSet(this.vp).filter((f) => (clipInfo.get(f.path)?.status || '').toLowerCase() === old.toLowerCase());
      if (hit.length) void (async () => { for (const f of hit) { try { await writeFileStatus(f, want); } catch (_) { /* fine */ } } paintStatuses(); new Notice(createFragment((fr) => { fr.appendText(`Renamed to ${want} on ${plural(hit.length, 'video')}. `); const b = fr.createEl('button', { text: 'Undo', cls: 'cvs-notice-btn' }); b.onclick = async () => { if (b.disabled) return; b.disabled = true; s.name = old; if (set.statusNew === want) set.statusNew = old; this.save(set); for (const f of hit) { try { await writeFileStatus(f, old); } catch (_) { /* fine */ } } paintStatuses(); if (this.containerEl.isConnected) this.draw(); }; }), 8000, { routine: hit.length < 2 }); })();
    };
    name.onchange = commit; name.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); name.blur(); } else if (e.key === 'Escape') { e.stopPropagation(); name.value = s.name; name.blur(); } };
    const desc = $('input', { cls: 'cvsw-stmod-desc', type: 'text', attr: { spellcheck: 'false', autocomplete: 'off', 'aria-label': 'Status description', placeholder: 'Description', maxlength: '120' } }); desc.value = s.description || '';
    desc.onchange = () => { const d = desc.value.replace(/\s+/g, ' ').trim(); if (d === (s.description || '')) return; if (d) s.description = d; else delete s.description; this.save(set); };
    desc.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); desc.blur(); } else if (e.key === 'Escape') { e.stopPropagation(); desc.value = s.description || ''; desc.blur(); } };
    const radio = $('input', { type: 'radio', cls: 'cvsw-stmod-radio', attr: { name: 'cvsw-status-new', 'aria-label': `Default for new videos: ${s.name}`, title: 'Default for new videos' } }); radio.checked = set.statusNew === s.name;
    radio.onclick = () => { set.statusNew = set.statusNew === s.name ? null : s.name; this.save(set); this.draw(); };
    const del = $('button', { cls: 'cvsw-stmod-del', attr: { type: 'button', 'aria-label': `Delete ${s.name}` } }, icon('trash-2'));
    del.onclick = () => {
      // the videos that read as it: named in their note, or (it is the default) without a status line
      const isDef = defaultStatus(set) === s;
      const hit = videosUsingSet(this.vp).filter((f) => { const raw = clipInfo.get(f.path)?.status || ''; return raw ? raw.toLowerCase() === s.name.toLowerCase() : isDef; });
      const drop = async (become) => {
        set.statuses = set.statuses.filter((x) => x !== s); if (set.statusNew === s.name) set.statusNew = null; this.save(set); this.draw();
        const nextDef = defaultStatus(set).name; const write = hit.filter((f) => clipInfo.get(f.path)?.status || become !== nextDef); // a video without a line that reads as `become` anyway stays untouched
        if (write.length) { for (const f of write) { try { await writeFileStatus(f, become); } catch (_) { /* fine */ } } paintStatuses(); new Notice(`${plural(write.length, 'video')} moved to ${become}.`); }
      };
      if (!hit.length) { void drop(set.statuses.find((x) => x !== s).name); return; }
      const m = new Menu(); m.addLabel(`${plural(hit.length, 'video')} with ${s.name} become`);
      for (const o of set.statuses.filter((x) => x !== s)) m.addItem((i) => { i.setTitle(o.name).onClick(() => void drop(o.name)); i.dot = o.color; });
      m.showBelow(del, 'end'); paintMenuDots();
    };
    if (!own) { name.readOnly = true; desc.readOnly = true; radio.disabled = true; del.disabled = true; color.disabled = true; }
    return $('div', { cls: 'cvsw-stmod-row', attr: { 'data-sort-item': s.id } }, grip, color, name, desc, $('label', { cls: 'cvsw-stmod-new', attr: { title: 'Default for new videos' } }, radio, $('span', { text: 'New' })), del);
  }
}

// The rubber band (the same as the tree's): press on empty space in the library (the card's padding, under
// the last row, a row's blank space: never text, a chip, a button or a field) and drag; after 4 px the rows
// the box touches are ticked live; Cmd or Shift adds; the table scrolls near its edges. A plain click on
// empty space clears the ticks.
let dtBand = null; let bandEndedAt = 0;
const bandJustEnded = () => Date.now() - bandEndedAt < 80;
const NOT_BLANK = 'button, input, textarea, select, a, th, tfoot, [contenteditable="true"], .cvsw-tag, .cvsw-td-name, .cvsw-td-pic, .cvsw-td-text, .cvsw-dt-foot, .cvsw-dt-group, .cvsw-card, .cvsw-folder, .cvsw-vtabs, .cvsw-dt-title, .cvsw-dt-search, .cvsw-empty, .cvsw-section, .cvsw-dt-bulkbar';
function wireTableBand(body) {
  body.addEventListener('pointerdown', (e) => {
    tableHot = true;
    const row = e.target.closest('.cvsw-dt-table tbody tr');
    const blank = !e.target.closest(NOT_BLANK);
    if (row && (row.dataset.vp || row.classList.contains('cvsw-dt-folder'))) row.draggable = !blank; // blank space starts a band, text and pictures a move
    if (e.button !== 0 || e.pointerType === 'touch' || !blank || !main.querySelector('.cvsw-dt-table')) return;
    const sc = main.querySelector('.cvsw-dt-scroll');
    for (const box of [body, sc]) { const r = box.getBoundingClientRect(); if (box.contains(e.target) && (e.clientX > r.left + box.clientWidth || e.clientY > r.top + box.clientHeight)) return; } // a scrollbar
    const add = e.metaKey || e.ctrlKey || e.shiftKey;
    const tableBox = () => main.querySelector('.cvsw-dt-table')?.getBoundingClientRect();
    const t0 = tableBox(); if (!t0) return;
    const b = dtBand = { x0: e.clientX, y0: e.clientY, sx: e.clientX - t0.left, sy: e.clientY - t0.top, add, base: new Set(add ? libChecked : []), on: false, px: e.clientX, py: e.clientY, el: null, raf: 0, inRow: !!row };
    const paint = () => {
      const t = tableBox(); if (!t) return;
      const ax = t.left + b.sx; const ay = t.top + b.sy; // where the press was, wherever the table has scrolled to since
      const x = Math.min(ax, b.px); const y = Math.min(ay, b.py); const w = Math.abs(b.px - ax); const h = Math.abs(b.py - ay);
      if (!b.el) { b.el = $('div', { cls: 'cvsw-band is-fixed', attr: { 'aria-hidden': 'true' } }); document.body.append(b.el); }
      Object.assign(b.el.style, { left: `${x}px`, top: `${y}px`, width: `${w}px`, height: `${h}px` });
      libChecked.clear(); for (const p of b.base) libChecked.add(p);
      for (const r of main.querySelectorAll('.cvsw-dt-table tbody tr[data-vp]')) { const q = r.getBoundingClientRect(); if (q.top < y + h && q.bottom > y && q.left < x + w && q.right > x) libChecked.add(r.dataset.vp); }
      applyChecked();
    };
    const tick = () => {
      b.raf = 0; if (dtBand !== b || !b.on) return;
      const s = main.querySelector('.cvsw-dt-scroll'); if (!s) return;
      const r = s.getBoundingClientRect(); const edge = 28;
      const dy = b.py < r.top + edge ? -Math.ceil((r.top + edge - b.py) / 3) : b.py > r.bottom - edge ? Math.ceil((b.py - (r.bottom - edge)) / 3) : 0;
      if (dy) { const before = s.scrollTop; s.scrollTop += dy; if (s.scrollTop !== before) paint(); b.raf = requestAnimationFrame(tick); }
    };
    const move = (ev) => {
      b.px = ev.clientX; b.py = ev.clientY;
      if (!b.on) { if (Math.hypot(ev.clientX - b.x0, ev.clientY - b.y0) < 4) return; b.on = true; document.documentElement.classList.add('cvsw-banding'); closePop(); cellFocus = null; paintCellFocus(); }
      paint();
      if (!b.raf) b.raf = requestAnimationFrame(tick);
    };
    const up = () => {
      removeEventListener('pointermove', move, true); removeEventListener('pointerup', up, true); removeEventListener('pointercancel', up, true);
      cancelAnimationFrame(b.raf); b.el?.remove(); document.documentElement.classList.remove('cvsw-banding');
      if (b.on) { bandEndedAt = Date.now(); checkAnchor = null; } // the click that follows the drag opens nothing
      else if (!b.add && !b.inRow && libChecked.size) { libChecked.clear(); applyChecked(); } // a plain click on empty space
      if (dtBand === b) dtBand = null;
    };
    addEventListener('pointermove', move, true); addEventListener('pointerup', up, true); addEventListener('pointercancel', up, true);
  });
}
document.addEventListener('pointerdown', (e) => { if (!e.target.closest?.('.cvsw-dt, .cvsw-pop, .menu')) tableHot = false; }, true);

// Keys while the table has the last click: arrows move a cell focus ring, Enter edits the cell or opens
// the video, Space ticks the row, Escape leaves the cell and then clears the ticks, Cmd+A ticks the page.
function paintCellFocus() {
  main.querySelectorAll('.cvsw-dt-table td.is-focus').forEach((x) => x.classList.remove('is-focus'));
  if (!cellFocus) return null;
  const row = main.querySelector(`.cvsw-dt-table tbody tr[data-vp="${CSS.escape(cellFocus.vp)}"]`);
  const td = row?.querySelector(`td[data-col="${cellFocus.col}"]`);
  if (!td) { cellFocus = null; return null; }
  td.classList.add('is-focus');
  return td;
}
function cellEnter(f, col, td) {
  const sel = `.cvsw-dt-table tr[data-vp="${CSS.escape(f.path)}"] td[data-col="${col}"]`;
  if (col === 'tags' && td) openTagEditor(td, f, { anchorSel: sel });
  else if (col === 'clips' && td) openClipsEditor(td, f, { anchorSel: sel });
  else if (col === 'status' && td) statusMenu(null, [f], td.querySelector('.cvsw-sdot-btn') || td);
  else go('v', f.path);
}
function tableKey(e) {
  if (openPath || isModalOpen() || e.isComposing || e.altKey || popOpen || !main.querySelector('.cvsw-dt-table')) return false;
  if (document.querySelector('.cvsw-fallback-menu, .cvsw-qp')) return false;
  if (e.target?.closest?.('input:not([type="checkbox"]), textarea, select, [contenteditable="true"]')) return false;
  const mod = e.metaKey || e.ctrlKey; const k = e.key;
  if (k === ' ' && e.target?.matches?.('input[type="checkbox"]')) return false; // the box ticks itself
  const stop = () => { e.preventDefault(); e.stopPropagation(); return true; };
  if (mod && !e.shiftKey && k.toLowerCase() === 'a') { if (!tablePage.length) return false; for (const f of tablePage) libChecked.add(f.path); applyChecked(); return stop(); }
  if (k === 'Escape') { if (cellFocus) { cellFocus = null; paintCellFocus(); return stop(); } if (libChecked.size) { libChecked.clear(); applyChecked(); return stop(); } return false; }
  if (mod) return false;
  const rows = [...main.querySelectorAll('.cvsw-dt-table tbody tr[data-vp]')].map((r) => r.dataset.vp); const cols = tableCols.map((c) => c.key);
  if (!rows.length) return false;
  if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'ArrowLeft' || k === 'ArrowRight') {
    if (!cellFocus || !rows.includes(cellFocus.vp) || !cols.includes(cellFocus.col)) cellFocus = { vp: rows[0], col: cols[0] };
    else {
      const r = rows.indexOf(cellFocus.vp); const c = cols.indexOf(cellFocus.col);
      if (k === 'ArrowDown') cellFocus = { vp: rows[Math.min(rows.length - 1, r + 1)], col: cellFocus.col };
      else if (k === 'ArrowUp') cellFocus = { vp: rows[Math.max(0, r - 1)], col: cellFocus.col };
      else cellFocus = { vp: cellFocus.vp, col: cols[Math.max(0, Math.min(cols.length - 1, c + (k === 'ArrowRight' ? 1 : -1)))] };
    }
    const td = paintCellFocus();
    if (td) { const sc = td.closest('.cvsw-dt-scroll'); const s = sc.getBoundingClientRect(); const t = td.getBoundingClientRect(); const head = sc.querySelector('thead')?.getBoundingClientRect().height || 0; const foot = sc.querySelector('tfoot')?.getBoundingClientRect().height || 0; if (t.top < s.top + head) sc.scrollTop -= s.top + head - t.top; else if (t.bottom > s.bottom - foot) sc.scrollTop += t.bottom - (s.bottom - foot); if (!td.classList.contains('is-frozen')) { const fl = [...td.parentElement.querySelectorAll('.is-frozen')].reduce((n, x) => Math.max(n, x.getBoundingClientRect().right), s.left); if (t.left < fl) sc.scrollLeft -= fl - t.left; else if (t.right > s.left + sc.clientWidth) sc.scrollLeft += t.right - (s.left + sc.clientWidth); } }
    return stop();
  }
  if (!cellFocus) return false;
  const f = vault.getFileByPath(cellFocus.vp); if (!f) return false;
  if (k === 'Enter') { cellEnter(f, cellFocus.col, paintCellFocus()); return stop(); }
  if (k === ' ') { checkRow(f.path, !libChecked.has(f.path), e.shiftKey); return stop(); }
  return false;
}
document.addEventListener('keydown', (e) => {
  if (e.defaultPrevented || !tableHot) return;
  const a = document.activeElement;
  if (a && a !== document.body && !main.contains(a)) return;
  tableKey(e);
});

function exportCsvText(rows, cols) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const val = (f, k) => { const raw = libRaw(f, k); return k === 'tags' ? raw.map((t) => `#${t}`).join(' ') : k === 'length' ? (raw ? fmtLen(raw) : '') : k === 'modified' || k === 'created' ? (raw ? new Date(raw).toISOString() : '') : k === 'resolution' ? String(raw).replace(/ x /, 'x') : k === 'type' ? f.extension : k === 'folder' ? relOf(parentOf(f.path)) : raw ?? ''; };
  return [cols.map((c) => esc(c.label)).join(','), ...rows.map((f) => cols.map((c) => esc(val(f, c.key))).join(','))].join('\n');
}
/** Export CSV: the current view (its columns, every row after search, filters and sorts). */
function exportCsv(rows, cols) {
  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([exportCsvText(rows, cols)], { type: 'text/csv' })); a.download = `${(vault.getFolderByPath(currentFolder)?.name || ROOT_NAME).replace(/[\\/:*?"<>|]+/g, '-')}.csv`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
function moveManyPicker(items, { noun = 'video', pick = null } = {}) {
  const md = new Modal(app);
  md.titleEl.setText(`Move ${plural(items.length, noun)} to`);
  const list = md.contentEl.createDiv({ cls: 'cvsw-move-list' });
  const inside = (d) => items.some((f) => f instanceof VFolder && (d.path === f.path || d.path.startsWith(`${f.path}/`))); // never into a folder that moves
  const folders = [...vault.index.values()].filter((x) => x instanceof VFolder && !inside(x)).sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  for (const d of folders) {
    const depth = d.path === VP ? 0 : relOf(d.path).split('/').length;
    const b = list.createEl('button', { cls: 'cvsw-move-item', attr: { type: 'button' } });
    b.style.paddingLeft = `${10 + depth * 14}px`;
    b.append(icon(d.path === VP ? (cloud.local ? 'hard-drive' : 'cloud') : 'folder'), $('span', { text: d.path === VP ? ROOT_NAME : d.name }));
    if (items.every((f) => parentOf(f.path) === d.path)) { b.disabled = true; b.title = items.length === 1 ? 'It is already here' : 'They are already here'; }
    b.onclick = async () => { md.close(); if (pick) { pick(d.path); return; } await bulkMove(items, d.path); libChecked.clear(); if (!openPath) renderLibrary(); };
  }
  md.open();
}
function confirmDeleteMany(items) {
  const md = new Modal(app);
  md.titleEl.setText(`Delete ${items.length} video${items.length === 1 ? '' : 's'}?`);
  md.contentEl.createEl('p', { text: `They and their clips go to the ${cloud.local ? 'Trash on this Mac' : 'trash (kept 30 days)'}, so you can still get them back.` });
  const row = md.contentEl.createDiv({ cls: 'modal-button-container' });
  row.createEl('button', { text: 'Cancel' }).onclick = () => md.close();
  const del = row.createEl('button', { text: 'Delete', cls: 'mod-warning' });
  del.onclick = async () => { md.close(); for (const f of items) { try { const note = plugin?.sidecarFor(f.path); await vault.trash(f); const nf = note && vault.getFileByPath(note); if (nf) await vault.trash(nf).catch(() => {}); } catch (err) { new Notice(`Could not delete ${f.basename}: ${err.message || err}`); } } libChecked.clear(); await refresh({ quiet: true }); renderLibrary(); new Notice('Moved to the Trash'); };
  md.open(); setTimeout(() => del.focus(), 0);
}

// The server makes no thumbnails, so a card's picture is a frame of the video itself,
// 10% in, grabbed through the signed link (moov + one keyframe, not the file)
// and kept in this browser by path and revision, so each video is grabbed once.
const THUMB_KEY = 'cthvw.thumbs.v1';
// a video's length and picture size arrive with its thumbnail: fill those table cells in place
function refreshMetaCells(key) {
  const row = [...main.querySelectorAll('.cvsw-dt-table tr[data-vp]')].find((r) => `${r.dataset.vp.toLowerCase()}@${vault.getFileByPath(r.dataset.vp)?.rev}` === key);
  const f = row && vault.getFileByPath(row.dataset.vp); if (!f) return;
  for (const k of ['length', 'resolution']) { const td = row.querySelector(`td[data-col="${k}"]`); if (td) td.replaceChildren(libCell(f, k)); }
  const v = curView(); const uses = (k) => v.sorts.some((s) => s.key === k) || filterCount(v.filters[k]) || v.groupBy === k || v.calcs[k];
  if (uses('length') || uses('resolution')) { clearTimeout(refreshMetaCells.t); refreshMetaCells.t = setTimeout(() => { if (!openPath && !popOpen && main.querySelector('.cvsw-dt')) renderLibrary(); }, 400); }
}
const thumbStore = (() => { try { return JSON.parse(localStorage.getItem(THUMB_KEY) || '{}') || {}; } catch (_) { return {}; } })();
function keepThumb(key, url, meta) {
  thumbStore[key] = { url, at: Date.now(), ...(meta ? { meta } : {}) };
  const keys = Object.keys(thumbStore).sort((a, b) => thumbStore[b].at - thumbStore[a].at);
  for (const k of keys.slice(150)) delete thumbStore[k];
  for (let n = keys.length; n > 0; n = Math.floor(n / 2)) {
    try { localStorage.setItem(THUMB_KEY, JSON.stringify(thumbStore)); return; } catch (_) { for (const k of Object.keys(thumbStore).sort((a, b) => thumbStore[a].at - thumbStore[b].at).slice(0, Math.ceil(n / 2))) delete thumbStore[k]; }
  }
}
async function grabFrame(dp) {
  const url = await cloud.tempLink(dp);
  return new Promise((res, rej) => {
    const v = document.createElement('video');
    v.muted = true; v.preload = 'metadata'; v.crossOrigin = 'anonymous'; v.playsInline = true;
    const done = (x, e) => { clearTimeout(to); v.removeAttribute('src'); try { v.load(); } catch (_) { /* fine */ } if (e) rej(e); else res(x); };
    const to = setTimeout(() => done(null, new Error('timed out')), 30000);
    v.onloadedmetadata = () => { v.currentTime = Math.min(Math.max(0.5, v.duration * 0.1), Math.max(0, v.duration - 0.1)); };
    v.onseeked = () => {
      try {
        const w = 320; const h = Math.round((w * v.videoHeight) / (v.videoWidth || 16)) || 180;
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        c.getContext('2d').drawImage(v, 0, 0, w, h);
        done({ url: c.toDataURL('image/jpeg', 0.72), meta: { dur: v.duration || 0, w: v.videoWidth || 0, h: v.videoHeight || 0 } });
      } catch (e) { done(null, e); }
    };
    v.onerror = () => done(null, new Error('the browser could not read this video'));
    v.src = url;
  });
}
async function fillThumbs() {
  const run = ++thumbRun;
  const todo = [];
  for (const img of main.querySelectorAll('img.cvsw-thumb')) {
    const hit = thumbs.get(img.__key) || thumbStore[img.__key]?.url;
    if (hit) img.src = hit;
    if (!hit || (img.closest('.cvsw-dt') && !metaOf({ path: img.__vp || '', rev: '' }) && !thumbStore[img.__key]?.meta && !thumbs.get(`meta:${img.__key}`))) todo.push(img);
  }
  const worker = async () => {
    while (todo.length && run === thumbRun) {
      const img = todo.shift();
      try { const got = await grabFrame(img.__path); const url = got.url; thumbs.set(img.__key, url); thumbs.set(`meta:${img.__key}`, got.meta); keepThumb(img.__key, url, got.meta); if (img.isConnected) img.src = url; if (main.querySelector('.cvsw-dt')) refreshMetaCells(img.__key); }
      catch (_) { img.closest('.cvsw-card-pic')?.addClass('is-broken'); }
    }
  };
  await Promise.all([worker(), worker()]);
}

// ---- uploads and folders ---------------------------------------------------------------------

function pickUpload(folderVp) {
  const i = $('input', { type: 'file', attr: { accept: 'video/mp4,video/quicktime,video/x-m4v,video/webm,.mp4,.mov,.m4v', multiple: true } });
  i.style.cssText = 'position:fixed;left:-9999px';
  i.onchange = () => { void uploadFiles(folderVp, [...(i.files || [])]); i.remove(); };
  document.body.append(i); i.click();
}
let uploading = 0;
async function uploadFiles(folderVp, files) {
  const vids = files.filter((f) => VIDEO_RE.test(f.name));
  if (files.length && !vids.length) { new Notice('Only mp4, mov, m4v and webm videos can be uploaded here.'); return; }
  for (let k = 0; k < vids.length; k++) {
    const f = vids[k];
    const name = uniqueName(folderVp, f.name);
    const of = vids.length > 1 ? ` (${k + 1} of ${vids.length})` : '';
    let msg;
    const n = new Notice(createFragment((fr) => { msg = fr.createDiv({ text: `Uploading ${name}${of}...` }); fr.createDiv({ cls: 'cvsw-notice-sub', text: 'Keep this tab open until it finishes.' }); }), 0);
    uploading++;
    try {
      await cloud.uploadAny(toCloud(`${folderVp}/${name}`), f, { overwrite: false, onProgress: (p) => msg.setText(`Uploading ${name}${of}: ${Math.round(p * 100)}%`) });
      n.hide(); new Notice(`Uploaded ${name}.`);
      await refresh({ quiet: true });
      const sn = defaultStatus(statusSetFor(folderVp)).name; const nf = vault.getFileByPath(`${folderVp}/${name}`); if (sn && nf) { try { await writeFileStatus(nf, sn); paintStatuses(); } catch (_) { /* no status then */ } } // the folder's status for new videos
    } catch (e) { n.hide(); noteTrouble(e); new Notice(`Could not upload ${f.name}: ${e.message || e}`, 9000); }
    finally { uploading--; }
  }
}
function uniqueName(folderVp, name) {
  const i = name.lastIndexOf('.'); const stem = i > 0 ? name.slice(0, i) : name; const ext = i > 0 ? name.slice(i) : '';
  for (let n = 1; ; n++) { const cand = n === 1 ? name : `${stem}-${n}${ext}`; if (!vault.index.has(`${folderVp}/${cand}`)) return cand; }
}
// ---- drag and drop -----------------------------------------------------------------------
// Videos from the desktop dropped on a folder upload there. A video or folder
// dragged (tree row, card, folder tile) onto a folder (tree row, folder tile,
// breadcrumb, the library around the cards, or empty space in the tree = the
// top folder) moves there, its clips note with it. A video row stands for its
// folder, and its folder's row lights up. Hovering a closed folder opens it.
// The notice after a move has Undo.
const MOVE_TYPE = 'application/x-cth-move';
let dragging = null;
let expandTimer = 0;
let expandVp = null;
let draggingMany = null; // the picked items, when a picked row (2+ picked) is dragged
function wireDrag(el, f) {
  el.draggable = true;
  el.addEventListener('dragstart', (e) => {
    dragging = f;
    draggingMany = sel.size >= 2 && sel.has(f.path) ? selItems() : libChecked.size >= 2 && libChecked.has(f.path) && el.closest('.cvsw-dt') ? checkedFiles() : null; // a picked tree row or a ticked table row takes the others along
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(MOVE_TYPE, f.path);
    // a clean preview (icon and name in a small card) instead of the browser's faded copy
    try {
      const chip = draggingMany ? $('div', { cls: 'cvsw-drag-chip' }, icon('files'), $('span', { text: plural(draggingMany.length) }))
        : $('div', { cls: 'cvsw-drag-chip' }, icon(f instanceof VFolder ? 'folder' : 'video'), $('span', { text: f instanceof VFolder ? f.name : f.basename }));
      document.body.append(chip);
      e.dataTransfer.setDragImage(chip, 16, 16);
      setTimeout(() => chip.remove(), 0);
    } catch (_) { /* the browser's own preview */ }
    document.documentElement.addClass('cvsw-is-dragging');
    requestAnimationFrame(() => { el.addClass('is-drag-source'); if (draggingMany) for (const r of treeRows()) if (sel.has(r.dataset.vp)) r.classList.add('is-drag-source'); });
  });
  el.addEventListener('dragend', endDrag);
}
function endDrag() {
  dragging = null; draggingMany = null; stopDragScroll();
  clearTimeout(expandTimer); expandVp = null;
  document.documentElement.removeClass('cvsw-is-dragging');
  document.querySelectorAll('.cvsw-dropping, .is-drag-source').forEach((x) => { x.classList.remove('cvsw-dropping', 'is-drag-source'); delete x.dataset.dropHint; });
  markGuides(null, 'is-drop');
}
/** Can `f` move into the folder `folderVp`? (not into itself, not where it already is) */
function canDrop(f, folderVp) {
  if (!f || parentOf(f.path) === folderVp) return false;
  if (f instanceof VFolder) return folderVp !== f.path && !folderVp.startsWith(`${f.path}/`);
  return true;
}
/** Light up the drop target: the row or tile, a "Move here" / "Upload here" pill on tree rows,
 *  and the target folder's guide line in the tree (so it is clear where the item will land). */
function markDrop(el, k = 'move') {
  document.querySelectorAll('.cvsw-dropping').forEach((x) => { if (x !== el) { x.classList.remove('cvsw-dropping'); delete x.dataset.dropHint; } });
  if (el) { el.classList.add('cvsw-dropping'); el.dataset.dropHint = k === 'files' ? 'Upload here' : 'Move here'; }
  markGuides(el?.dataset?.vp || null, 'is-drop');
}
function wireDrop(el, folderVp, { mark = null, expand = false } = {}) {
  const kind = (e) => { const t = [...(e.dataTransfer?.types || [])]; return t.includes(MOVE_TYPE) ? (dragging ? 'move' : null) : t.includes('Files') ? 'files' : null; };
  const target = () => (typeof mark === 'function' ? mark() : mark) || el;
  el.addEventListener('dragover', (e) => {
    const k = kind(e);
    if (!k) return;
    e.stopPropagation();
    if (k === 'move' && !(draggingMany ? draggingMany.some((x) => canDrop(x, folderVp)) : canDrop(dragging, folderVp))) { e.dataTransfer.dropEffect = 'none'; markDrop(null); return; }
    e.preventDefault();
    e.dataTransfer.dropEffect = k === 'move' ? 'move' : 'copy';
    markDrop(target(), k);
    if (k === 'move' && expand && expandVp !== folderVp) {
      clearTimeout(expandTimer); expandVp = folderVp;
      if (!web.expanded.includes(folderVp)) expandTimer = setTimeout(() => toggleExpanded(folderVp, true), 600); // spring-loaded, like Finder
    } else if (!expand) { clearTimeout(expandTimer); expandVp = null; }
  });
  el.addEventListener('dragleave', (e) => { if (!el.contains(e.relatedTarget)) { const t = target(); t?.classList.remove('cvsw-dropping'); if (t) delete t.dataset.dropHint; markGuides(null, 'is-drop'); } });
  el.addEventListener('drop', (e) => {
    const k = kind(e);
    if (!k) return;
    e.preventDefault(); e.stopPropagation();
    const f = dragging; const many = draggingMany;
    endDrag();
    if (k === 'files') void uploadFiles(folderVp, [...e.dataTransfer.files]);
    else if (many) void bulkMove(many, folderVp);
    else if (canDrop(f, folderVp)) void moveByDrop(f, folderVp);
  });
}
// The tree scrolls while something is dragged over it (Tony, 2026-10-02; the same in CTH Docs): within
// 40px of the list's top or bottom edge (the header and footer rows count as past the edge) it scrolls
// that way, 4 to 18 px a frame, faster the closer to the edge. It stops when the drag leaves the tree,
// drops or ends. The browser keeps sending dragover while the pointer rests, so the drop pill and the
// guide line keep up with the rows moving underneath. A wheel during the drag is left alone.
const dragScroll = { raf: 0, v: 0 };
function stopDragScroll() { cancelAnimationFrame(dragScroll.raf); dragScroll.raf = 0; dragScroll.v = 0; }
function dragScrollTick() {
  dragScroll.raf = 0;
  const l = tree.querySelector('.cvsw-tree-list');
  if (!l || !dragScroll.v) return;
  const before = l.scrollTop; l.scrollTop += dragScroll.v;
  if (l.scrollTop !== before || dragScroll.v) dragScroll.raf = requestAnimationFrame(dragScrollTick);
}
tree.addEventListener('dragover', (e) => {
  const t = [...(e.dataTransfer?.types || [])];
  if (!(dragging || t.includes('Files'))) return;
  const l = tree.querySelector('.cvsw-tree-list'); if (!l) return;
  const r = l.getBoundingClientRect(); const zone = 40;
  const up = r.top + zone - e.clientY; const down = e.clientY - (r.bottom - zone);
  const speed = (d) => Math.round(4 + 14 * Math.min(1, Math.max(0, d) / zone));
  dragScroll.v = up > 0 ? -speed(up) : down > 0 ? speed(down) : 0;
  if (dragScroll.v && !dragScroll.raf) dragScroll.raf = requestAnimationFrame(dragScrollTick);
}, true);
tree.addEventListener('dragleave', (e) => { if (!tree.contains(e.relatedTarget)) stopDragScroll(); }, true);
tree.addEventListener('drop', () => stopDragScroll(), true);

async function moveByDrop(f, folderVp) {
  const from = f.path;
  const where = folderVp === VP ? ROOT_NAME : baseName(folderVp);
  const to = await relocate(f, `${folderVp}/${f.name}`);
  if (!to) return;
  new Notice(createFragment((fr) => {
    fr.appendText(`Moved ${f instanceof VFolder ? f.name : f.basename} to ${where}. `);
    const b = fr.createEl('button', { text: 'Undo', cls: 'cvs-notice-btn' });
    b.onclick = () => { if (b.disabled) return; b.disabled = true; const back = vault.getAbstractFileByPath(to); if (back) void relocate(back, from).then((ok) => { if (ok) new Notice('Moved back.'); }); };
  }), 8000);
}

// ---- moving and renaming ---------------------------------------------------------------------

const cleanName = (s) => String(s || '').replace(/[\\/:*?"<>|#^[\]]+/g, ' ').replace(/\s+/g, ' ').trim();
/** Rename a video (keeping its extension) or a folder. */
async function renameItem(f, want) {
  let name = cleanName(want);
  if (!name) return null;
  if (f instanceof VFile) { if (name.toLowerCase().endsWith(`.${f.extension.toLowerCase()}`)) name = name.slice(0, -(f.extension.length + 1)); name = `${name}.${f.extension}`; }
  if (name === f.name) return null;
  const to = await relocate(f, `${parentOf(f.path)}/${name}`);
  if (to) new Notice(`Renamed to ${f instanceof VFile ? baseName(to).replace(/\.[^.]+$/, '') : baseName(to)}.`, undefined, { routine: true });
  return to;
}
/**
 * Move or rename a video or a folder. A video's clips note goes
 * first (and comes back if the video cannot move), so a video never opens
 * without its clips; resume positions and open folders follow. The open video
 * is saved, closed and reopened at its new place. Returns the new path, or null.
 */
async function relocate(f, newVp, opts = {}) {
  // opts.quiet (bulk actions): no notices (reasons go to opts.errors), no refresh per item;
  // the caller settles once at the end (settleMoves) and reopens opts.reopen
  const fail = (msg) => { if (opts.quiet) opts.errors?.push(msg); else new Notice(msg, 8000); return null; };
  if (!f || !newVp || newVp === f.path) return null;
  const lo = newVp.toLowerCase();
  const clash = [...vault.index.values()].find((x) => x !== f && x.path.toLowerCase() === lo);
  if (clash) return fail(`There is already ${clash instanceof VFolder ? 'a folder' : 'a video'} called "${clash.name}" in ${relOf(parentOf(newVp)) || ROOT_NAME}.`);
  const oldVp = f.path;
  const within = (p) => p === oldVp || p.startsWith(`${oldVp}/`);
  const swap = (p) => newVp + p.slice(oldVp.length);
  const wasOpen = openPath && within(openPath) ? openPath : null;
  if (wasOpen) {
    const p = curPlayer();
    try { p?.commitEditIfAny?.(); if (p?.dirty) await p.save(); } catch (_) { /* its note is kept on this device */ }
    await closeVideo();
  }
  relocating++;
  try {
    if (f instanceof VFolder) await vault.rename(f, newVp); // the server moves everything inside, clips notes too
    else {
      const oldNote = plugin?.sidecarFor(oldVp); const newNote = plugin?.sidecarFor(newVp);
      const note = oldNote && oldNote !== newNote ? vault.getFileByPath(oldNote) : null;
      const carry = !!note && !vault.getAbstractFileByPath(newNote);
      if (carry) await vault.rename(note, newNote);
      try { await vault.rename(f, newVp); }
      catch (e) { if (carry) { try { await vault.rename(vault.getFileByPath(newNote), oldNote); } catch (_) { /* left beside the new name */ } } throw e; }
      if (carry) { try { await app.fileManager.processFrontMatter(vault.getFileByPath(newNote), (y) => { y.video = newVp; y.type = 'video-clips'; }); } catch (_) { /* the player repairs it when it opens */ } }
    }
  } catch (e) {
    noteTrouble(e);
    if (wasOpen) { if (opts.quiet) opts.reopen = wasOpen; else go('v', wasOpen); }
    return fail(`Could not move "${f.name}": ${e.message || e}`);
  } finally { relocating--; }
  const pos = plugin?.positions;
  if (pos) { for (const k of Object.keys(pos)) if (within(k)) { pos[swap(k)] = pos[k]; delete pos[k]; } plugin.saveSoon?.(); }
  web.expanded = web.expanded.map((x) => (within(x) ? swap(x) : x));
  if (!web.expanded.includes(parentOf(newVp))) web.expanded.push(parentOf(newVp));
  saveWeb();
  if (within(currentFolder)) currentFolder = swap(currentFolder);
  if (wasOpen) opts.reopen = swap(wasOpen);
  else if (opts.reopen && within(opts.reopen)) opts.reopen = swap(opts.reopen); // the open video's folder moved after it
  for (const p of [...sel]) if (within(p)) { sel.delete(p); sel.add(swap(p)); } // the selection follows
  for (const p of [...libChecked]) if (within(p)) { libChecked.delete(p); libChecked.add(swap(p)); }
  if (f instanceof VFolder) rekeyViews(oldVp, newVp); // a folder's table views go with it
  if (!opts.quiet) await settleMoves(opts.reopen);
  return newVp;
}
/** After moves and renames: read the list once, reopen the video that was open, show the folder. */
async function settleMoves(reopen) {
  await refresh({ quiet: true });
  if (reopen) go('v', reopen);
  else if (vault.getFolderByPath(currentFolder)) { const hash = currentFolder === VP ? '#/' : `#/f/${encodeRel(relOf(currentFolder))}`; if (!openPath && location.hash !== hash) history.replaceState(null, '', hash); if (!openPath) renderLibrary(); }
  renderTree();
}

/** Right-click a video or folder (tree row, card, folder tile). */
function itemMenu(e, f) {
  const m = new Menu();
  const dir = f instanceof VFolder;
  m.addItem((i) => i.setTitle('Open').setIcon(dir ? 'folder-open' : 'play').onClick(() => go(dir ? 'f' : 'v', f.path)));
  if (dir) m.addItem((i) => i.setTitle(web.expanded.includes(f.path) ? 'Collapse' : 'Expand').setIcon('chevrons-up-down').onClick(() => toggleExpanded(f.path)));
  if (dir) m.addItem((i) => i.setTitle('Statuses...').setIcon('circle-dot').onClick(() => openStatusEditor(f.path)));
  else if (web.statusDots !== false) m.addItem((i) => i.setTitle('Status...').setIcon('circle-dot').onClick(() => statusMenu(null, [f], tree.querySelector(`.cvsw-tree-list [data-vp="${CSS.escape(f.path)}"] .cvsw-sdot-btn`) || e.target)));
  // (Copy embed link retired 2026-10-02: Notion is no longer used)
  m.addSeparator();
  m.addItem((i) => i.setTitle('Add videos').setIcon('plus').onClick(() => pickUpload(dir ? f.path : parentOf(f.path))));
  m.addItem((i) => i.setTitle('New folder').setIcon('folder-plus').onClick(() => void newFolder(dir ? f.path : parentOf(f.path))));
  m.addSeparator();
  m.addItem((i) => i.setTitle('Rename').setIcon('pencil').onClick(() => {
    if (renameInTree(f.path)) return;
    const top = openPath === f.path && bar.querySelector('.cvsw-crumb.is-current');
    if (top) { top.click(); return; }
    new NamePrompt(dir ? 'Rename folder' : 'Rename video', (v) => void renameItem(f, v), { value: dir ? f.name : f.basename, action: 'Rename' }).open();
  }));
  m.addItem((i) => i.setTitle('Duplicate').setIcon('copy').onClick(() => void duplicateItem(f)));
  m.addItem((i) => i.setTitle('Move').setIcon('folder-input').onClick(() => moveToPicker(f)));
  if (!dir) m.addItem((i) => i.setTitle('Tags').setIcon('tag').onClick(() => { const row = tree.querySelector(`.cvsw-tree-list [data-vp="${CSS.escape(f.path)}"]`) || e.target; openTagEditor(row, f, { anchorSel: `.cvsw-tree-list [data-vp="${CSS.escape(f.path)}"]` }); }));
  m.addSeparator();
  m.addItem((i) => i.setTitle('Copy name').setIcon('clipboard-copy').onClick(() => void navigator.clipboard.writeText(dir ? f.name : f.basename).then(() => new Notice('Name copied', undefined, { routine: true }), () => {})));
  if (cloud.local) m.addItem((i) => i.setTitle('Show in Finder').setIcon('external-link').onClick(() => void revealOnMac(f.path)));
  m.addSeparator();
  m.addItem((i) => i.setTitle('Delete').setIcon('trash-2').onClick(() => confirmDelete(f)));
  m.showAtMouseEvent(e);
}
/** Right-click the tree's empty space. */
function treeMenu(e) {
  const m = new Menu();
  const here = openPath ? parentOf(openPath) : currentFolder;
  m.addItem((i) => i.setTitle('Add videos').setIcon('plus').onClick(() => pickUpload(here)));
  m.addItem((i) => i.setTitle('New folder').setIcon('folder-plus').onClick(() => void newFolder(here)));
  m.addSeparator();
  m.addItem((i) => i.setTitle('Expand all').setIcon('chevrons-up-down').onClick(() => { web.expanded = [...vault.index.values()].filter((x) => x instanceof VFolder && x.path !== VP).map((x) => x.path); saveWeb(); renderTree(); }));
  m.addItem((i) => i.setTitle('Collapse all').setIcon('chevrons-down-up').onClick(() => { web.expanded = []; saveWeb(); renderTree(); }));
  m.addItem((i) => i.setTitle(web.treeGuides !== false ? 'Hide guides' : 'Show guides').setIcon('list-tree').onClick(() => { web.treeGuides = web.treeGuides === false; saveWeb(); renderTree(); }));
  m.addItem((i) => i.setTitle('Filter').setIcon('search').onClick(() => { filterOpen = true; focusFilter = true; renderTree(); }));
  m.addItem((i) => i.setTitle('Statuses...').setIcon('circle-dot').onClick(() => openStatusEditor(VP)));
  m.addSeparator();
  if (cloud.local) m.addItem((i) => i.setTitle('Show in Finder').setIcon('external-link').onClick(() => void revealOnMac(VP)));
  m.addItem((i) => i.setTitle('Settings').setIcon('settings').onClick(() => openSettings()));
  m.showAtMouseEvent(e);
}
async function revealOnMac(vp) {
  try { const r = await fetch('/api/fs/reveal', { method: 'POST', headers: { 'X-CTH-Local': '1', 'Content-Type': 'application/json' }, body: JSON.stringify({ path: toCloud(vp) || '' }) }); if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.status); }
  catch (err) { new Notice(`Could not show it in Finder: ${err.message || err}`); }
}
/** Delete goes to the Trash (macOS Trash on the Mac, the cloud trash for 30 days), never for good. */
function confirmDelete(f) {
  const dir = f instanceof VFolder;
  const n = dir ? [...vault.index.values()].filter((x) => x.path.startsWith(`${f.path}/`) && isVideo(x)).length : 0;
  const md = new Modal(app);
  md.titleEl.setText(dir ? `Delete the folder "${f.name}"?` : `Delete "${f.basename}"?`);
  md.contentEl.createEl('p', { text: `${dir ? `It and everything in it (${n} video${n === 1 ? '' : 's'})` : 'It and its clips'} go${dir ? '' : ''} to the ${cloud.local ? 'Trash on this Mac' : 'trash (kept 30 days)'}, so you can still get ${dir ? 'them' : 'it'} back.` });
  const row = md.contentEl.createDiv({ cls: 'modal-button-container' });
  row.createEl('button', { text: 'Cancel' }).onclick = () => md.close();
  const del = row.createEl('button', { text: 'Delete', cls: 'mod-warning' });
  del.onclick = async () => {
    md.close();
    try {
      if (openPath && (openPath === f.path || openPath.startsWith(`${f.path}/`))) { await closeVideo(); go('f', dir ? parentOf(f.path) : parentOf(f.path)); }
      const note = !dir ? plugin?.sidecarFor(f.path) : null;
      await vault.trash(f);
      const nf = note && vault.getFileByPath(note); if (nf) await vault.trash(nf).catch(() => {});
      await refresh({ quiet: true }); renderTree();
      new Notice('Moved to the Trash');
    } catch (err) { new Notice(`Could not delete it: ${err.message || err}`); }
  };
  md.open();
  setTimeout(() => del.focus(), 0);
}
/** Pick a folder of the library (the top level first): a dialog with the folder list. */
function folderPicker(title, currentVp, onPick) {
  const md = new Modal(app);
  md.titleEl.setText(title);
  const list = md.contentEl.createDiv({ cls: 'cvsw-move-list' });
  const folders = [...vault.index.values()].filter((x) => x instanceof VFolder && !x.name.startsWith('.')).sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  for (const d of folders) {
    const depth = d.path === VP ? 0 : relOf(d.path).split('/').length;
    const b = list.createEl('button', { cls: `cvsw-move-item${d.path === normVault(currentVp || VP) ? ' is-on' : ''}`, attr: { type: 'button' } });
    b.style.paddingLeft = `${10 + depth * 14}px`;
    b.append(icon(d.path === VP ? (cloud.local ? 'hard-drive' : 'cloud') : 'folder'), $('span', { text: d.path === VP ? `${ROOT_NAME} (top level)` : d.name }));
    b.onclick = () => { md.close(); onPick(d.path); };
  }
  md.open();
}
/** Move to...: pick a folder from the list. */
function moveToPicker(f) {
  const md = new Modal(app);
  md.titleEl.setText(`Move "${f instanceof VFolder ? f.name : f.basename}" to`);
  const list = md.contentEl.createDiv({ cls: 'cvsw-move-list' });
  const folders = [...vault.index.values()].filter((x) => x instanceof VFolder && !(f instanceof VFolder && (x.path === f.path || x.path.startsWith(`${f.path}/`)))).sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  for (const d of folders) {
    const depth = d.path === VP ? 0 : relOf(d.path).split('/').length;
    const b = list.createEl('button', { cls: 'cvsw-move-item', attr: { type: 'button' } });
    b.style.paddingLeft = `${10 + depth * 14}px`;
    b.append(icon(d.path === VP ? (cloud.local ? 'hard-drive' : 'cloud') : 'folder'), $('span', { text: d.path === VP ? ROOT_NAME : d.name }));
    if (d.path === parentOf(f.path)) { b.disabled = true; b.title = 'It is already here'; }
    b.onclick = async () => { md.close(); const to = `${d.path}/${f.name}`; const done = await relocate(f, to); if (done) new Notice(`Moved to ${d.path === VP ? ROOT_NAME : d.name}`, undefined, { routine: true }); };
  }
  md.open();
}

// ---- renaming in place (the top bar) ---------------------------------------------------------

const nameEditing = (box) => !!box && document.activeElement?.classList.contains('cvsw-name-input') && box.contains(document.activeElement);
const measure = document.createElement('canvas').getContext('2d');
/** Swap `el` for a text field holding `current`: Enter or clicking away saves (save(value)), Escape cancels. */
function editName(el, current, save, label) {
  if (!el?.isConnected) return;
  const input = $('input', { cls: 'cvsw-input cvsw-name-input', type: 'text', value: current, attr: { spellcheck: 'false', autocomplete: 'off', 'aria-label': label } });
  el.replaceWith(input);
  const size = () => {
    const cs = getComputedStyle(input);
    measure.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    input.style.width = `${Math.round(Math.min(Math.max(measure.measureText(input.value || ' ').width + 34, 140), Math.max(200, innerWidth * 0.5)))}px`;
  };
  size();
  input.focus();
  input.select();
  let done = false;
  const finish = async (ok) => {
    if (done) return;
    done = true;
    const want = input.value.trim();
    if (input.isConnected) input.replaceWith(el);
    if (ok && want && want !== current) await save(want);
    if (barSkipped) { barSkipped = false; renderBar(); }
  };
  input.addEventListener('input', size);
  input.addEventListener('keydown', (e) => {
    e.stopPropagation(); // the player's keys stay out of the name
    if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); void finish(true); }
    else if (e.key === 'Escape') { e.preventDefault(); void finish(false); }
  });
  input.addEventListener('blur', () => void finish(true));
}

// ---- the file tree's width ------------------------------------------------------------------

function setTreeW(w, fromHub = false) {
  const max = Math.max(240, Math.min(640, innerWidth - 360));
  web.treeW = Math.round(Math.min(max, Math.max(200, w)));
  document.documentElement.style.setProperty('--tree-w', `${web.treeW}px`);
  saveWeb();
  if (!fromHub) panelToHub();
}
// Inside CTH Admin every app's left panel shares one width and open state, so switching apps never
// jumps (Tony, 2026-10-02): changes go to the hub, which passes them to the other apps.
function panelToHub() {
  if (!cloud.inHub || isNarrow()) return;
  try { window.parent.postMessage({ cthHub: 'panel', side: 'left', open: !!web.treeOpen, width: web.treeW }, location.ancestorOrigins?.[0] || '*'); } catch (_) { /* fine */ }
}
function treeResizer() {
  const grip = $('div', { cls: 'cvsw-tree-resize', attr: { role: 'separator', 'aria-orientation': 'vertical', 'aria-label': 'Resize the file tree', title: 'Drag to resize. Double-click to reset.' } });
  grip.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    grip.setPointerCapture(e.pointerId);
    const x0 = e.clientX; const w0 = tree.getBoundingClientRect().width;
    document.documentElement.addClass('cvsw-resizing');
    const move = (ev) => setTreeW(w0 + ev.clientX - x0);
    const up = () => { grip.removeEventListener('pointermove', move); document.documentElement.removeClass('cvsw-resizing'); };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up, { once: true });
    grip.addEventListener('pointercancel', up, { once: true });
  });
  grip.addEventListener('dblclick', () => setTreeW(272));
  return grip;
}

class NamePrompt extends Modal {
  constructor(title, onOk, { value = '', action = 'Create' } = {}) { super(app); this.t = title; this.onOk = onOk; this.value = value; this.action = action; }
  onOpen() {
    this.titleEl.setText(this.t);
    const input = this.contentEl.createEl('input', { type: 'text', cls: 'cvs-modal-input', attr: { spellcheck: 'false' } });
    input.value = this.value;
    const row = this.contentEl.createDiv({ cls: 'modal-button-container' });
    const ok = () => { const v = input.value.trim(); if (v) { this.close(); this.onOk(v); } };
    row.createEl('button', { text: 'Cancel' }).onclick = () => this.close();
    row.createEl('button', { text: this.action, cls: 'mod-cta' }).onclick = ok;
    input.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); ok(); } };
    setTimeout(() => { input.focus(); input.select(); }, 0);
  }
}
/** The first free "<stem><n><ext>" next to the others (Name, Name 2, ...; or Name copy, Name copy 2, ...). */
function freeName(parentVp, stem, ext = '', taken = null) {
  for (let n = 1; ; n++) { const vp = `${parentVp}/${stem}${n > 1 ? ` ${n}` : ''}${ext}`; const lo = vp.toLowerCase(); if (!taken?.has(lo) && ![...vault.index.keys()].some((k) => k.toLowerCase() === lo)) return vp; }
}
/** After making something: show it in the tree with its name selected, ready to type (Tony, 2026-10-02). */
async function nameRightAway(vp, title) {
  const parent = parentOf(vp);
  if (parent !== VP && !web.expanded.includes(parent)) { web.expanded.push(parent); saveWeb(); }
  if (isNarrow() || !web.treeOpen) setTree(true);
  if (treeFilter) { treeFilter = ''; filterOpen = false; }
  renderTree();
  await new Promise((r) => requestAnimationFrame(r));
  tree.querySelector(`.cvsw-tree-list [data-vp="${CSS.escape(vp)}"]`)?.scrollIntoView({ block: 'nearest' });
  if (renameInTree(vp)) return;
  const f = vault.getAbstractFileByPath(vp); if (!f) return;
  new NamePrompt(title, (v) => void renameItem(f, v), { value: f instanceof VFolder ? f.name : f.basename, action: 'Rename' }).open();
}
/** New folder: made as "New folder" and named in place in the tree. */
async function newFolder(parentVp) {
  const vp = freeName(parentVp, 'New folder');
  try { await vault.createFolder(vp); } catch (e) { new Notice(`Could not make the folder: ${e.message || e}`); return; }
  await nameRightAway(vp, 'Name the folder');
}
/** Copy a video (with its clips note) or a folder (with everything in it) next to itself; returns the copy's path.
 *  `taken` (bulk duplicates) holds the names already given out before the list is read again. */
async function copyItem(f, taken = null) {
  const dir = f instanceof VFolder; const parent = parentOf(f.path);
  const vp = dir ? freeName(parent, `${f.name} copy`, '', taken) : freeName(parent, `${f.basename} copy`, f.extension ? `.${f.extension}` : '', taken);
  taken?.add(vp.toLowerCase());
  if (!dir) { const p = [...(plugin?.players.values() || [])].find((x) => x.vaultPath === f.path); try { p?.commitEditIfAny?.(); if (p?.dirty) await p.save(); } catch (_) { /* kept on this device */ } }
  await cloud.copy(toCloud(f.path), toCloud(vp));
  if (!dir) { const note = plugin?.sidecarFor(f.path); const to = plugin?.sidecarFor(vp); if (note && to && vault.getFileByPath(note) && !vault.getAbstractFileByPath(to)) await cloud.copy(toCloud(note), toCloud(to)); }
  return vp;
}
/** Duplicate a video (with its clips) or a folder (with everything in it), then name the copy in place. */
async function duplicateItem(f) {
  let vp;
  try { vp = await copyItem(f); await refresh({ quiet: true }); } catch (e) { new Notice(`Could not duplicate it: ${e.message || e}`); return; }
  await nameRightAway(vp, f instanceof VFolder ? 'Name the folder' : 'Name the video');
}

// ---- the player -----------------------------------------------------------------------------

let openSeq = 0;
let leafEl = null;
async function openVideo(vp) {
  const seq = ++openSeq;
  const f = vault.getFileByPath(vp);
  if (!f) {
    await closeVideo();
    waitingFor = vp;
    showMain($('div', { cls: 'cvsw-lib' }, $('div', { cls: 'cvsw-lib-body' }, empty('search-x', 'That video is not here yet', `Nothing called "${relOf(vp)}" is in the videos folder. If it is still uploading, it opens here by itself when it arrives. Otherwise it was renamed, moved or deleted: pick it from the list.`))));
    renderTree(); renderBar();
    return;
  }
  // Everything that shows changes in one frame (Tony, 2026-10-02: the switch used to jump): the player is
  // built at once (not 120 ms later, after a black box), its stage takes the picture's shape from the
  // thumbnail, the top bar and the tree update in the same frame, and the screen fades in over the table.
  // The video's link arrives a moment later and only fills the picture.
  await closeVideo({ keep: true });
  waitingFor = null;
  openPath = vp;
  openId = f.id;
  goneWarned = false;
  // Obsidian-style leaf markup (the UI kit's shape): the player's CSS is scoped to it.
  const leaf = leafEl = $('div', { cls: 'cvsw-leaf workspace-leaf-content', attr: { 'data-type': 'video' } });
  const content = leaf.createDiv({ cls: 'view-content' });
  const holder = content.createDiv({ cls: 'video-container' });
  const v = holder.createEl('video', { attr: { playsinline: '', preload: 'auto', crossorigin: 'anonymous' } });
  v.dataset.cvsPath = absOf(vp);
  const mt = metaOf(f); if (mt && mt.w && mt.h) { v.dataset.cvsW = String(mt.w); v.dataset.cvsH = String(mt.h); }
  showMain(leaf, { fade: true });
  document.title = `${f.basename} · ${APP_TITLE}`;
  workspace.leaf = new Leaf(null);
  workspace.leaf.view = new VideoView(workspace.leaf, leaf, content, f);
  workspace.trigger('layout-change');
  workspace.trigger('active-leaf-change', workspace.leaf);
  plugin?.scanNow?.();
  renderBar();
  renderTree();
  let url;
  try { url = await cloud.tempLink(toCloud(vp)); } catch (e) { if (seq === openSeq) failOpen(vp, e); return; }
  if (seq !== openSeq) return;
  // A signed link lasts 4 hours: when it dies mid-session, get a new one and
  // carry on from the same time.
  v.addEventListener('error', async () => {
    if (seq !== openSeq || !v.error) return;
    const at = v.currentTime; const wasPlaying = !v.paused;
    try { v.src = await cloud.tempLink(toCloud(vp), { fresh: true }); v.currentTime = at; if (wasPlaying) void v.play(); } catch (_) { /* the player shows the error */ }
  });
  v.src = url;
  // Keys work at once: after a click in the tree or the library, focus sits
  // there, and the player only takes keys aimed at it.
  const t0 = Date.now();
  const focusPlayer = () => {
    if (seq !== openSeq || leafEl !== leaf) return;
    const st = leaf.querySelector('.cvs-stage');
    if (!st) { if (Date.now() - t0 < 5000) setTimeout(focusPlayer, 100); return; }
    const a = document.activeElement;
    if (!a || a === document.body || !leaf.contains(a)) st.focus({ preventScroll: true });
  };
  focusPlayer();
}
let openId = '';
let waitingId = null;
let waitingFor = null;
let goneWarned = false;

// The open video was renamed or moved (on the Mac, or from another device):
// follow it by its file id, carrying the clips note along when nothing moved
// it, so clips never split across two notes.
async function followMove(newVp) {
  const oldVp = openPath;
  const p = curPlayer();
  const oldNote = plugin?.sidecarFor(oldVp); const newNote = plugin?.sidecarFor(newVp);
  try {
    const nf = oldNote && vault.getFileByPath(oldNote);
    if (nf && !vault.getFileByPath(newNote)) await vault.rename(nf, newNote);
  } catch (_) { /* the plugin repairs the note's video: line when it loads */ }
  if (p) { p.vaultPath = newVp; p.absPath = absOf(newVp); p.commitEditIfAny?.(); if (p.dirty) await p.save(); }
  new Notice(`"${baseName(oldVp)}" moved to ${relOf(newVp) || ROOT_NAME}. Following it.`);
  location.replace(`#/v/${encodeRel(relOf(newVp))}`);
}
function failOpen(vp, e) {
  if (e?.message === 'NOT_CONNECTED') return; // signed out: the page is reloading onto the password card
  showMain($('div', { cls: 'cvsw-lib' }, $('div', { cls: 'cvsw-lib-body' }, empty('triangle-alert', 'Could not open this video', `${relOf(vp)}: ${e?.message || e}`))));
}
/** Close the open video. keep: its screen stays in the page for the next showMain to fade out (its player is
 *  saved and taken down when the screen leaves the page); otherwise it goes at once. */
async function closeVideo({ keep = false } = {}) {
  if (!openPath) return;
  openPath = null;
  const leaf = leafEl; leafEl = null;
  if (leaf) {
    leaf.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (_) { /* fine */ } });
    leaf.__onGone = () => workspace.trigger('layout-change'); // the plugin reaps (and saves) the player whose video left the page
    if (!keep) { if (mainFade) mainFade.finish(); leaf.remove(); }
  }
  workspace.leaf = null;
  workspace.trigger('layout-change');
  document.title = APP_TITLE;
}

// ---- settings, shortcuts --------------------------------------------------------------------

// The Settings dialog (Tony, 2026-10-02; shadcn/studio Account Settings 09, the same in CTH Docs): about
// 880 x 620, a title bar, a 220px nav on the left (search on top, then one row per section), the section
// on the right scrolling by itself (a title, one line about it, then rows with hairlines between). The
// search filters rows across every section. The player's own settings (its settings tab builds them
// under h3 groups) are dealt into the matching sections. The last section is remembered (web.setSec).
const SET_SECTIONS = [
  ['general', 'General', 'settings', 'How the app behaves around the videos.'],
  ['appearance', 'Appearance', 'palette', 'Light or dark, and how the picture sits on the page.'],
  ['tables', 'Tables', 'table-2', 'The folder table: rows, columns and what a click does.'],
  ['panels', 'Quick panels', 'notebook-pen', 'The keys for the Scratchpad and CLAUDE.md at the top right.'],
  ['status', 'Status', 'circle-dot', 'The status dot beside each video and the statuses a folder uses.'],
  ['shortcuts', 'Shortcuts', 'keyboard', 'Every key, in one list. Use the search on the left to find one.'],
  ['player', 'Player', 'circle-play', 'Playback, the clock, skipping and full screen.'],
  ['clips', 'Clips', 'scissors', 'The clip log beside the video and how clips are named.'],
  ['panel', 'Button panel', 'layout-grid', 'The rows of quick buttons above the timeline.'],
  ['annotate', 'Annotate', 'pen-tool', 'Freeze frames and the marks drawn on them.'],
  ['export', 'Export', 'download', 'Where exported clips go and how they are made.'],
  ['scrub', 'Trackpad scrub', 'mouse-pointer-2', 'Two-finger scrubbing on the video.'],
  ['sync', 'Sync', 'refresh-cw', 'How this app, the website and Notion stay in step.'],
  ['about', 'About', 'info', 'Where the files live, the build and the backup.'],
];
// the player's settings tab groups (its h3 titles) -> a section here; two rows of "Player" belong to Appearance
const SET_GROUPS = { Player: 'player', Clips: 'clips', 'Clip log': 'clips', 'Button panel': 'panel', Export: 'export', Annotate: 'annotate', 'Trackpad scrub': 'scrub' };
const SET_ROW_HOME = { Appearance: 'appearance', 'Picture area': 'appearance' };
/** The app's own keys (the player's come from its shortcutGroups). */
function appShortcutGroups() {
  const g = [];
  const app = [[keyLabel('mod+['), 'Back'], [keyLabel('mod+]'), 'Forward']];
  if (scratchPanel) app.push([keyLabel(shared.scratchKey), 'Scratchpad: new note (opens it)']);
  if (claudePanel) app.push([keyLabel(shared.claudeKey), 'CLAUDE.md: open or close']);
  app.push(['Esc', 'Close a menu, a panel or a dialog']);
  g.push(['App', app]);
  g.push(['File tree', [['Click', 'Open a folder or a video'], ['Double-click or F2', 'Rename in place'], [`${keyLabel('mod+x').slice(0, -1)} + click`, 'Add or remove a row from the selection'], ['Shift + click', 'Select a range'], ['Drag on empty space', 'Select with a box'], [keyLabel('mod+a'), 'Select every row shown'], ['Up / Down', 'Move between rows (Shift extends the selection)'], ['Left / Right', 'Close or open a folder'], [keyLabel('mod+d'), 'Duplicate the selection'], ['Delete', 'Move the selection to the Trash'], ['S', 'Change the video\'s status'], ['Esc', 'Clear the selection']]]);
  g.push(['Folder table', [['Click a row', 'Open the video'], ['Click a thumbnail', 'Quick look'], ['Click a checkbox', 'Select the row (Shift selects a range)'], ['Drag on empty space', 'Select with a box'], [keyLabel('mod+a'), 'Select every row on the page'], ['Arrow keys', 'Move between cells'], ['Enter', 'Edit the cell, or open the video'], ['Space', 'Select or unselect the row'], ['Esc', 'Leave the cell, then clear the selection'], ['Double-click a column edge', 'Fit the column to its content']]]);
  return g;
}
class SettingsModal extends Modal {
  constructor(a, section) { super(a); this.section = section || web.setSec || 'general'; }
  onOpen() {
    this.modalEl.addClass('cvsw-settings');
    this.titleEl.setText('Settings');
    this.secs = new Map();
    const search = $('input', { cls: 'cvsw-set-search-input', type: 'search', attr: { placeholder: 'Search settings', 'aria-label': 'Search settings', spellcheck: 'false', 'data-esc-own': '1' } });
    this.search = search;
    this.navEl = $('nav', { cls: 'cvsw-set-nav', attr: { 'aria-label': 'Settings sections' } }, $('label', { cls: 'cvsw-set-search' }, icon('search'), search));
    this.pick = $('select', { cls: 'cvsw-set-pick', attr: { 'aria-label': 'Section' } });
    this.bodyEl = $('div', { cls: 'cvsw-set-body' });
    this.noneEl = $('div', { cls: 'cvsw-set-none', text: 'Nothing matches.' });
    for (const [id, label, ic, desc] of SET_SECTIONS) {
      const own = $('div', { cls: 'cvsw-set-rows' }); const fromPlayer = $('div', { cls: 'cvsw-set-rows' });
      const el = $('section', { cls: 'cvsw-set-sec', attr: { 'data-sec': id } }, $('div', { cls: 'cvsw-set-sechead' }, $('h2', { text: label }), $('p', { text: desc })), own, fromPlayer);
      const nb = $('button', { cls: 'cvsw-set-navbtn', attr: { type: 'button', 'data-sec': id } }, icon(ic), $('span', { text: label }));
      nb.onclick = () => { if (this.search.value) { this.search.value = ''; } this.go(id); };
      this.secs.set(id, { id, label, el, own, fromPlayer, nb });
      this.pick.append($('option', { text: label, attr: { value: id } }));
    }
    this.pick.onchange = () => { this.search.value = ''; this.go(this.pick.value); };
    search.oninput = () => this.filter();
    search.onkeydown = (e) => { if (e.key === 'Escape' && search.value) { e.stopPropagation(); search.value = ''; this.filter(); } };
    this.contentEl.append($('div', { cls: 'cvsw-set' }, this.navEl, $('div', { cls: 'cvsw-set-main' }, this.pick, this.bodyEl)));
    this.buildOwn();
    // the player's settings, dealt into the sections (again whenever its tab redraws itself)
    const tab = this.tab = plugin.settingTabs[0];
    if (tab) {
      const draw = Object.getPrototypeOf(tab).display;
      tab.display = () => { tab.containerEl = createDiv(); draw.call(tab); this.deal(tab.containerEl); this.finish(); };
      tab.display();
    } else this.finish();
  }
  row(sec, name, desc) { const st = new Setting(this.secs.get(sec).own).setName(name); if (desc) st.setDesc(desc); return st; }
  buildOwn() {
    const SH = 'Shared across CTH Admin.';
    // General
    this.row('general', 'Confirmations', 'Quiet: messages only for errors, deletions and changes to several videos (Cmd+Z undoes the rest). Show all: a message after every change.').addDropdown((d) => d.addOptions({ quiet: 'Quiet', all: 'Show all' }).setValue(web.confirmations === 'all' ? 'all' : 'quiet').onChange((v) => { web.confirmations = v; Notice.mode = v; saveWeb(); }));
    this.row('general', 'Guide lines in the file tree', 'A thin line for each folder level; the folder you are in shows darker.').addToggle((t) => t.setValue(web.treeGuides !== false).onChange((v) => { web.treeGuides = v; saveWeb(); renderTree(); }));
    this.row('general', 'Fade between the table and the player', 'A short cross-fade when a video opens or closes. Off switches at once.').addToggle((t) => t.setValue(web.fade !== false).onChange((v) => { web.fade = v; saveWeb(); }));
    this.row('general', 'Gallery order', 'How the cards in a gallery are ordered.').addDropdown((d) => d.addOptions({ modified: 'Last modified', name: 'Name A to Z' }).setValue(web.sort === 'name' ? 'name' : 'modified').onChange((v) => { web.sort = v; saveWeb(); if (!openPath) renderLibrary(); }));
    // Tables
    {
      const st = this.row('tables', 'Row height', `Short 36, Medium 48, Tall 64, or a number. ${SH}`);
      const num = $('input', { type: 'number', cls: 'cvsw-set-num', attr: { min: '28', max: '96', step: '1', 'aria-label': 'Row height in pixels' } });
      const seg = $('div', { cls: 'cvsw-seg', attr: { role: 'radiogroup', 'aria-label': 'Row height' } });
      const paint = () => { num.value = String(shared.rowHeight); seg.querySelectorAll('.cvsw-seg-btn').forEach((b) => { const on = Number(b.dataset.v) === shared.rowHeight; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', String(on)); }); };
      for (const [v, t] of [[36, 'Short'], [48, 'Medium'], [64, 'Tall']]) { const b = $('button', { cls: 'cvsw-seg-btn', attr: { type: 'button', role: 'radio', 'data-v': String(v) } }, $('span', { text: t })); b.onclick = () => { void setShared({ rowHeight: v }); paint(); }; seg.append(b); }
      num.onchange = () => { void setShared({ rowHeight: Math.max(28, Math.min(96, Math.round(Number(num.value) || 36))) }); paint(); };
      st.controlEl.append(seg, num); paint(); this.paintRowH = paint;
    }
    this.row('tables', 'Rows per page', `Rows a table shows before the next page. ${SH}`).addDropdown((d) => { d.addOptions({ 25: '25', 50: '50', 100: '100', 100000: 'All' }); if (![25, 50, 100, 100000].includes(shared.rowsPerPage)) d.addOption(String(shared.rowsPerPage), String(shared.rowsPerPage)); d.setValue(String(shared.rowsPerPage)).onChange((v) => void setShared({ rowsPerPage: Number(v) })); });
    this.row('tables', 'Column icons', `A small icon before each column title. ${SH}`).addToggle((t) => t.setValue(!!shared.columnIcons).onChange((v) => void setShared({ columnIcons: v })));
    this.row('tables', 'Thumbnail quick look', 'Clicking a thumbnail opens a small popup player. Off: it opens the video.').addToggle((t) => t.setValue(web.quickLook !== false).onChange((v) => { web.quickLook = v; saveWeb(); }));
    this.row('tables', 'Quick look starts playing', 'The popup plays as soon as it opens. Off: it waits for Space.').addToggle((t) => t.setValue(web.quickLookPlay !== false).onChange((v) => { web.quickLookPlay = v; saveWeb(); }));
    // Quick panels
    const keyRow = (panel, what) => {
      if (!panel) return;
      const st = this.row('panels', `${panel.cfg.title} key`, `${what} Click the key, then press the new one. ${SH}`);
      const kb = $('button', { cls: 'cvsw-set-key', attr: { type: 'button', 'aria-label': `${panel.cfg.title} key` } });
      const reset = $('button', { text: 'Reset', attr: { type: 'button' } });
      const paint = () => { kb.setText(keyLabel(panel.key)); kb.removeClass('is-recording'); reset.disabled = panel.key === SHARED_DEFAULTS[panel.cfg.keyName]; };
      kb.onclick = () => { if (keyRecording) return; kb.setText('Press keys...'); kb.addClass('is-recording'); recordKey(async (combo) => { await setKey(panel.cfg.keyName, combo, panel.cfg.title); paint(); }, paint); };
      reset.onclick = async () => { await setKey(panel.cfg.keyName, SHARED_DEFAULTS[panel.cfg.keyName], panel.cfg.title); paint(); };
      st.controlEl.append(kb, reset); paint();
    };
    keyRow(scratchPanel, 'Opens the Scratchpad on a new note.');
    keyRow(claudePanel, 'Opens and closes the CLAUDE.md editor.');
    // Status
    const repaint = () => { renderTree(); if (!openPath) renderLibrary(); else paintStatuses(); };
    this.row('status', 'Status dots', 'A small dot beside each video in the tree, the table, the gallery and the player.').addToggle((t) => t.setValue(web.statusDots !== false).onChange((v) => { web.statusDots = v; saveWeb(); repaint(); }));
    this.row('status', 'Dot position', 'Before the name (in the icon slot), or after it.').addDropdown((d) => d.addOptions({ before: 'Before the name', after: 'After the name' }).setValue(web.statusPos === 'after' ? 'after' : 'before').onChange((v) => { web.statusPos = v; saveWeb(); repaint(); }));
    this.row('status', 'A folder without its own statuses uses', 'Nearest: the closest folder above it that has a set. Top-most: the highest folder that has one.').addDropdown((d) => d.addOptions({ nearest: 'Nearest folder above', top: 'Top-most folder' }).setValue(web.statusInherit === 'top' ? 'top' : 'nearest').onChange((v) => { web.statusInherit = v; saveWeb(); repaint(); }));
    this.row('status', 'Folder progress', 'A small count on each folder row of the videos at the set\'s last status, like 3/10.').addDropdown((d) => d.addOptions({ off: 'Off', count: 'Show the count' }).setValue(web.statusProgress === 'count' ? 'count' : 'off').onChange((v) => { web.statusProgress = v; saveWeb(); paintProgress(); }));
    this.row('status', 'Top-level statuses', 'The set every folder starts from (To-Do, WIP, Review, Live, Done unless changed).').addButton((b) => b.setButtonText('Edit top-level statuses...').onClick(() => openStatusEditor(VP)));
    // Shortcuts: the app's keys, then the player's
    const keys = this.secs.get('shortcuts').own;
    for (const [name, rows] of [...appShortcutGroups(), ...(HOST.internals?.shortcutGroups?.(plugin.settings) || [])]) {
      keys.append($('h3', { cls: 'cvsw-set-keyhead', text: name.replace(/\s*\(.*\)$/, '') }));
      for (const [k, d] of rows) keys.append($('div', { cls: 'cvsw-set-keyrow setting-row' }, $('span', { cls: 'cvsw-set-keydesc', text: d }), $('kbd', { cls: 'cvsw-set-kbd', text: k })));
    }
    // Sync
    if (cloud.local) this.row('sync', 'The website', 'The same videos at videos.coachtonyhockey.com, for the phone, Notion and other computers.').addButton((b) => b.setButtonText('Open the website').onClick(() => window.open(`https://videos.coachtonyhockey.com/${location.hash || '#/'}`, '_blank', 'noopener')));
    else this.row('sync', 'This device', 'Signed in with the CTH password. Signing out saves open work first.').addButton((b) => b.setButtonText('Sign out').onClick(async () => { b.setDisabled(true); try { await flushAll(); } catch (_) { /* kept on this device */ } location.href = '/__cth/logout'; }));
    this.row('sync', 'Shared settings', 'Player settings and table views are shared by every browser and Notion embed through videos/.cth-video.json. Exports are made in the browser and saved to the export folder.');
    this.row('sync', 'Table and quick panel settings', inAdmin ? 'Row height, rows per page, column icons, the quick panels\' keys and the Scratchpad are shared with the other CTH Admin apps.' : 'Row height, rows per page, column icons and the Scratchpad are kept in this browser. Inside CTH Admin they are shared with its other apps.');
    // About
    {
      const st = this.row('about', 'Where the videos live', cloud.local ? 'This is CTH Videos on this Mac: it plays straight from ~/CTH/Videos, so a video dropped in there opens in a second or two. The cloud copy (your phone, Notion embeds, the backup) catches up by itself.' : 'On Tony\'s Mac in ~/CTH/Videos. They sync to the cloud by themselves.');
      if (cloud.local) st.addButton((b) => b.setButtonText('Show in Finder').onClick(() => void revealOnMac(VP)));
    }
    {
      const st = this.row('about', 'Build', `${APP_TITLE} ${version}`);
      const v = (document.querySelector('script[src*="app.js"]')?.getAttribute('src') || '').match(/v=([0-9a-f]+)/);
      if (v) st.setDesc(`${APP_TITLE} ${version}, build ${v[1]}`);
    }
    {
      const st = this.row('about', 'Backup', cloud.local ? 'Cloud copy: checking...' : 'The cloud copy is the backup. Deleted files stay in its trash for 30 days.');
      st.descEl.addClass('cvsw-set-backup');
      if (cloud.local) void cloud.syncStatus().then((x) => {
        const b = x && x.backup;
        if (!b) { st.setDesc('Cloud copy: the Mac sync is not set up on this Mac.'); return; }
        const q = b.queue; const cur = q && q.current;
        const left = q && q.n ? `${q.n} file${q.n === 1 ? '' : 's'} (${fmtSize(q.bytes)}) still to upload${cur ? `, now ${baseName(cur.path)} ${cur.pct}%` : ''}.` : 'up to date.';
        st.setDesc(!b.alive ? 'Cloud copy: the Mac sync is not running. Videos still play here; the phone and Notion wait until it runs again.' : b.lastError ? `Cloud copy: ${left} Last problem: ${b.lastError.replace(/^\S+ /, '')}` : `Cloud copy: ${left}`);
      }).catch(() => st.setDesc('Cloud copy: could not check.'));
    }
  }
  /** Deal the player's rows (h3 groups in its settings tab) into the sections. */
  deal(from) {
    for (const s of this.secs.values()) s.fromPlayer.empty();
    let sec = 'player';
    for (const node of [...from.children]) {
      if (node.tagName === 'H3') { sec = SET_GROUPS[node.textContent.trim()] || 'player'; continue; }
      const name = node.querySelector('.setting-item-name')?.textContent.trim() || '';
      this.secs.get(SET_ROW_HOME[name] || sec).fromPlayer.append(node);
    }
  }
  finish() {
    const shown = [...this.secs.values()].filter((s) => s.own.childElementCount || s.fromPlayer.childElementCount);
    this.navEl.querySelectorAll('.cvsw-set-navbtn').forEach((b) => b.remove());
    this.navEl.append(...shown.map((s) => s.nb));
    for (const o of this.pick.options) o.hidden = !shown.some((s) => s.id === o.value);
    this.bodyEl.replaceChildren(...shown.map((s) => s.el), this.noneEl);
    if (!shown.some((s) => s.id === this.section)) this.section = shown[0].id;
    this.filter();
  }
  go(id) { this.section = id; web.setSec = id; saveWeb(); this.filter(); this.bodyEl.scrollTop = 0; }
  /** With text in the search: every section that has a matching row, only those rows. Without: the chosen section. */
  filter() {
    const q = this.search.value.trim().toLowerCase(); let any = false;
    for (const s of this.secs.values()) {
      let hits = 0;
      for (const r of s.el.querySelectorAll('.setting-item, .cvsw-set-keyrow')) { const on = !q || r.textContent.toLowerCase().includes(q); r.toggleClass('is-filtered', !on); if (on) hits++; }
      for (const h of s.el.querySelectorAll('.cvsw-set-keyhead')) { let n = h.nextElementSibling; let live = false; while (n && !n.matches('.cvsw-set-keyhead')) { if (!n.hasClass('is-filtered')) live = true; n = n.nextElementSibling; } h.toggleClass('is-filtered', !live); }
      const show = q ? hits > 0 : s.id === this.section;
      s.el.toggleClass('is-on', show); if (show) any = true;
      s.nb.toggleClass('is-active', !q && s.id === this.section); s.nb.toggleClass('is-dim', !!q && !hits);
      s.nb.setAttribute('aria-current', String(!q && s.id === this.section));
    }
    this.pick.value = this.section;
    this.bodyEl.toggleClass('is-searching', !!q);
    this.noneEl.toggleClass('is-on', !any);
  }
  onClose() { if (this.tab) delete this.tab.display; applyTheme(); plugin.saveSettings?.(); }
}
let settingsOpen = null;
function openSettings(section) {
  if (!plugin) return;
  if (settingsOpen) { if (section) settingsOpen.go(section); return; }
  const m = settingsOpen = new SettingsModal(app, section);
  const close = m.close.bind(m); m.close = () => { if (settingsOpen === m) settingsOpen = null; close(); };
  m.open();
}
function openShortcuts() { openSettings('shortcuts'); }
onShared(() => { settingsOpen?.paintRowH?.(); });

// ---- sync ---------------------------------------------------------------------------------

// One refresh at a time; a request that arrives meanwhile runs once more after
// it, so a change is never missed.
let refreshing = null; let refreshAgain = false;
async function refresh(opts = {}) {
  if (refreshing) { refreshAgain = true; return refreshing; }
  refreshing = (async () => { do { refreshAgain = false; await refreshOnce(opts); } while (refreshAgain); })();
  try { return await refreshing; } finally { refreshing = null; }
}
async function refreshOnce({ quiet = false } = {}) {
  try { await vault.refresh(); noteOk(); void loadSync(); }
  catch (e) {
    noteTrouble(e);
    if (e.message === 'NOT_CONNECTED') return false; // signed out: the page is reloading onto the password card
    if (!quiet && !cloud.isNetworkError(e)) new Notice(`Could not read the videos folder: ${e.message || e}`, 6000);
    if (!openPath) renderLibrary();
    return false;
  }
  // clip notes changed elsewhere (another browser or device): idle players
  // reload; newer shared settings are taken in
  const changed = vault.changed.splice(0);
  for (const f of changed) { if (f.path === `${VP}/.cth-video.json`) { if (await plugin?.pullShared?.()) applyTheme(); } else vault.trigger('modify', f); }
  if (openPath && !vault.getFileByPath(openPath)) {
    const moved = openId && vault.getFiles().find((x) => x.id === openId);
    if (moved) { await followMove(moved.path); return true; }
    if (!goneWarned) { goneWarned = true; new Notice(`"${baseName(openPath)}" was deleted from the videos folder. Its clips are still in its note; close the video when you are done.`, 10000); }
  }
  if (waitingId && resolveRoute().kind !== 'missing') { waitingId = null; void onRoute(); return true; }
  if (waitingFor && vault.getFileByPath(waitingFor)) { const r = route(); if (r.kind === 'v' && r.vp === waitingFor) void onRoute(); }
  renderTree();
  if (!openPath) renderLibrary();
  void flushPending();
  void refreshClipInfo();
  return true;
}
let watchId = 0;
async function startWatch() {
  const id = ++watchId;
  let cursor = null;
  while (id === watchId) {
    try {
      if (!cursor) cursor = await cloud.cursorFor();
      const { changes, backoff } = await cloud.longpoll(cursor);
      if (id !== watchId) return;
      if (changes) { cursor = null; await refresh({ quiet: true }); }
      if (backoff) await new Promise((r) => setTimeout(r, backoff * 1000));
    } catch (_) { cursor = null; await new Promise((r) => setTimeout(r, 15000)); }
  }
}
window.addEventListener('focus', () => { if (started) void refresh({ quiet: true }); });

// ---- offline, signed out, waiting changes: one quiet status pill --------------------

function noteOk() { if (!netOk || signedOut) { netOk = true; signedOut = false; updateBanner(); } }
function noteTrouble(e) {
  if (e?.message === 'NOT_CONNECTED') { signedOut = true; updateBanner(); }
  else if (cloud.isNetworkError(e)) { netOk = false; updateBanner(); }
}
const banner = createDiv({ cls: 'cvsw-banner is-hidden', attr: { role: 'status', 'aria-live': 'polite' } });
function updateBanner() {
  const n = Object.keys(pendingAll()).length;
  const waiting = n ? ` ${n} change${n === 1 ? ' is' : 's are'} kept on this device.` : '';
  let text = ''; let action = null;
  if (!navigator.onLine || !netOk) text = `Offline. Changes save by themselves when you are back online.${waiting}`;
  else if (signedOut) { text = `Signed out.${waiting || ' Nothing is lost.'}`; action = ['Sign in', () => location.reload()]; }
  else if (n) { text = `${n} change${n === 1 ? '' : 's'} waiting to save.`; action = ['Save now', () => void flushPending(true)]; }
  banner.empty();
  banner.toggleClass('is-hidden', !text);
  if (!text) return;
  banner.append(icon(!navigator.onLine || !netOk ? 'wifi-off' : action && action[0] === 'Sign in' ? 'log-in' : 'cloud-upload'), $('span', { text }));
  if (action) { const b = $('button', { cls: 'cvsw-banner-btn', text: action[0], attr: { type: 'button' } }); b.onclick = action[1]; banner.append(b); }
}
let flushing = false;
async function flushPending(manual = false) {
  if (flushing || !started) return;
  const all = pendingAll();
  const keys = Object.keys(all);
  if (!keys.length) { if (manual) new Notice('Everything is saved.'); return; }
  flushing = true;
  try {
    for (const vp of keys) {
      try {
        await vault.writeText(vp, all[vp].text);
        for (const p of plugin?.players.values() || []) if (p.notePath() === vp) p.savedElsewhere?.(all[vp].text);
      } catch (e) { if (manual) new Notice(`Still not saved: ${e.message || e}`); break; }
    }
  } finally { flushing = false; }
}
setInterval(() => { if (navigator.onLine) void flushPending(); }, 20000);
window.addEventListener('online', () => { netOk = true; updateBanner(); void refresh({ quiet: true }); void flushPending(); });
window.addEventListener('offline', () => { netOk = false; updateBanner(); });

async function flushAll() {
  for (const p of plugin?.players.values() || []) { p.commitEditIfAny?.(); if (p.dirty) await p.save(); }
  await plugin?.shareNow?.();
}
// Only ask before leaving when something would really be lost: an edit not yet
// written anywhere, an export or an upload in progress. Edits kept on this
// device (offline, signed out) survive a reload, so they do not ask.
window.addEventListener('beforeunload', (e) => {
  const kept = pendingAll();
  const dirty = [...(plugin?.players.values() || [])].some((p) => (p.dirty && !kept[p.notePath()]) || p.exporting);
  if (dirty || vault.pending || uploading) { void flushAll(); e.preventDefault(); e.returnValue = ''; }
});
// ---- inside CTH Admin (the Mac app hub) ----------------------------------------------------
// Updates are manual (Tony, 2026-10-02: the auto-update was retired): the hub's Refresh button
// reloads. This reports whether a newer build is waiting (/__version, every 5 s) so the hub can
// show a dot on Refresh, and answers Refresh's { cthHub: 'prepare' } by saving first.
function hubLink() {
  const toHub = (msg) => { try { window.parent.postMessage(msg, location.ancestorOrigins?.[0] || '*'); } catch (_) { /* fine */ } };
  let have = null;
  const tick = async () => { try { const { v } = await (await fetch('/__version', { cache: 'no-store' })).json(); if (!have) have = v; toHub({ cthHub: 'state', update: v !== have }); } catch (_) { /* server restarting */ } };
  setInterval(() => void tick(), 5000); void tick();
  toHub({ cthHub: 'panel-hello' }); // the hub answers with the shared panel state
  addEventListener('message', (e) => {
    const d = e.source === window.parent && e.data;
    if (!d || d.cthHub !== 'panel' || d.side !== 'left' || isNarrow()) return;
    if (Number(d.width) && Math.round(d.width) !== web.treeW) setTreeW(Number(d.width), true);
    if (typeof d.open === 'boolean' && d.open !== !!web.treeOpen) setTree(d.open, true);
  });
  // CTH Admin's back and forward (Tony, 2026-10-02): tell it where we are, go where it asks (no new entry)
  const tell = () => toHub({ cthHub: 'nav', url: location.hash || '#/' });
  tell(); window.addEventListener('hashchange', tell);
  addEventListener('message', (e) => {
    if (e.source !== window.parent || !e.data || e.data.cthHub !== 'go' || typeof e.data.url !== 'string') return;
    if (location.hash !== e.data.url) { history.replaceState(null, '', e.data.url); void onRoute(); }
    tell();
  });
  addEventListener('message', async (e) => {
    if (e.source !== window.parent || !e.data || e.data.cthHub !== 'prepare') return;
    try { await flushAll(); } catch (_) { /* kept on this device */ }
    toHub({ cthHub: 'prepared' });
  });
}

// Cmd+[ back, Cmd+] forward (Tony, 2026-10-02): in CTH Admin the hub keeps one history across its apps;
// on its own (the website, a full window) the browser's history.
window.addEventListener('keydown', (e) => {
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || (e.key !== '[' && e.key !== ']') || cloud.embedded) return;
  e.preventDefault(); e.stopPropagation();
  if (cloud.inHub) { try { window.parent.postMessage({ cthHub: 'key', key: e.key === ']' ? 'forward' : 'back' }, location.ancestorOrigins?.[0] || '*'); } catch (_) { /* fine */ } }
  else if (e.key === ']') history.forward(); else history.back();
}, true);

// Reopen where you left off: a bare address goes back to the last folder or video.
const LAST_KEY = 'cthvw.last.v1';
// (not in an embed: there the embed's own link says what to show)
window.addEventListener('hashchange', () => { if (cloud.embedded) return; try { localStorage.setItem(LAST_KEY, linkParams(location.hash).base); } catch (_) { /* fine */ } }); // a link's start is not a place
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flushAll(); });

// ---- boot ---------------------------------------------------------------------------------
// The password card (worker/src/gate.js) stands in front of the site, so the
// app always starts signed in. A 401 later reloads the page onto that card.

let plugin = null;
function render() {
  shell.toggleClass('is-tree-closed', !web.treeOpen);
  if (web.treeW && web.treeW !== 272) document.documentElement.style.setProperty('--tree-w', `${web.treeW}px`);
  renderTree();
  if (!openPath) renderLibrary();
  renderBar();
}

let started = false;
async function boot() {
  if (cloud.embedded) {
    document.documentElement.classList.add('cvsw-embed');
    // an embed starts compact: the video or library fills it (the tree opens with one click)
    if (firstRun) { web.treeOpen = false; saveWeb(); }
  }
  if (!location.hash && !cloud.embedded) { try { const last = localStorage.getItem(LAST_KEY); if (last && last !== '#/') history.replaceState(null, '', `${location.pathname}${last}`); } catch (_) { /* fine */ } }
  document.body.append(shell, banner);
  applyTheme();
  started = true;
  render();
  await refresh();
  plugin = new CthVideoPlugin(app, manifest);
  app.plugins.plugins[manifest.id] = plugin;
  await plugin.onload();
  applyTheme();
  workspace.layoutReady = true;
  if (cloud.local && cloud.inHub) hubLink();
  workspace.trigger('__ready');
  const r = route();
  currentFolder = r.kind === 'f' ? r.vp : r.kind === 'v' ? (parentOf(r.vp) || VP) : VP;
  await onRoute();
  void startWatch();
  updateBanner();
  void flushPending();
}

// Exposed for the headless smoke test only.

// ---- tooltips: one for the whole app (shadcn's Tooltip look, player/styles.css .cvs-tooltip) ----
// Any icon-only button (its aria-label) or element with a title shows it after 100 ms (at once while
// moving between buttons); a trailing "(S)" or "(Cmd+K)" in the label becomes the key chip. The
// native title is parked in data-tip so the slow browser tooltip never shows too. Not on touch.
(() => {
  let tip = null; let timer = 0; let shownAt = 0; let over = null;
  const hide = () => { clearTimeout(timer); if (tip) { tip.remove(); tip = null; shownAt = Date.now(); } over = null; };
  const labelOf = (el) => {
    if (el.hasAttribute('title')) { el.dataset.tip = el.getAttribute('title'); el.removeAttribute('title'); }
    if (el.dataset.tip) return el.dataset.tip;
    if (el.matches('button[aria-label], [role="button"][aria-label]') && !el.textContent.trim()) return el.getAttribute('aria-label');
    return '';
  };
  const show = (el, text) => {
    if (!el.isConnected || document.querySelector('.menu, .cvs-menu, .cvsw-fallback-menu') || document.documentElement.classList.contains('cvs-sorting') || document.documentElement.classList.contains('cvsw-banding')) return;
    const m = text.match(/^(.*?)\s*\(([^()]{1,12})\)$/);
    tip = $('div', { cls: 'cvs-tooltip', attr: { role: 'tooltip' } }, $('span', { text: m ? m[1] : text }), m ? $('kbd', { cls: 'cvs-tooltip-kbd', text: m[2] }) : null);
    document.body.append(tip);
    const r = el.getBoundingClientRect(); const t = tip.getBoundingClientRect();
    const left = Math.max(4, Math.min(innerWidth - t.width - 4, r.left + r.width / 2 - t.width / 2));
    const below = r.top - t.height - 8 < 4 || !!el.closest('.cvsw-appbar, .cvsw-panel-head, .cvs-side-bar, .cvs-filter');
    tip.style.left = `${left}px`; tip.style.top = `${below ? r.bottom + 8 : r.top - t.height - 8}px`;
    tip.style.setProperty('--arrow-x', `${Math.max(10, Math.min(t.width - 10, r.left + r.width / 2 - left))}px`);
    tip.classList.toggle('is-below', below);
  };
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType === 'touch') return;
    const el = e.target.closest?.('[title], [data-tip], button[aria-label], [role="button"][aria-label]');
    if (!el || el === over || el.closest('.cvs-pbtn, .modal, .menu, .cvs-menu')) return;
    const text = labelOf(el); if (!text) return;
    hide(); over = el;
    timer = setTimeout(() => show(el, text), Date.now() - shownAt < 800 ? 0 : 100);
  });
  document.addEventListener('pointerout', (e) => { if (over && !over.contains(e.relatedTarget)) hide(); });
  for (const ev of ['pointerdown', 'keydown', 'wheel', 'blur']) addEventListener(ev, hide, true);
})();
window.__cvsw = { app, vault, get plugin() { return plugin; }, cloud, web, table: { view: () => curView(), views: () => viewStore(), csv: () => exportCsvText(tableRows, tableCols), rows: () => tableRows.map((f) => f.path) } };
void boot();
